import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Button,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Checkbox,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Layers as LayersIcon,
  Add as AddIcon,
  Timeline as CableIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Map as MapIcon,
  Satellite as SatelliteIcon,
  Straighten as ReguaIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CallSplit as SplitterIcon,
  Visibility as VisibilityIcon,
  OpenWith as OpenWithIcon,
  ContentCopy as ContentCopyIcon,
  Assessment as AssessmentIcon,
  AccountTree as AccountTreeIcon,
  Landscape as LandscapeIcon,
  Hub as CaixaCtoIcon,
  Storage as ArmarioIcon,
  Upgrade as PosteIcon,
  Build as BuildIcon,
  Animation as ReservaIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DraggableMapFab } from './components/DraggableMapFab';
import { Wrapper } from '@googlemaps/react-wrapper';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import { ftthArmarioService, FtthArmario } from '../../services/ftthArmarioService';
import { configurationService } from '../../services/configurationService';
import {
  addAguardandoInstalacaoBorderToSvg,
  addPortTextToPinSvg,
  MAP_PIN_PORT_TEXT_COLOR,
  CEO_MAP_DISPLAY_HEIGHT,
  CEO_MAP_DISPLAY_WIDTH,
  CTO_PIN_ANCHOR_X,
  CTO_PIN_ANCHOR_Y,
  CTO_PIN_DISPLAY_HEIGHT,
  CTO_PIN_DISPLAY_WIDTH,
  getCeoIconDataUrl,
  getCeoMapIconAnchor,
  getCtoIconDataUrl,
  MAP_PIN_ANCHOR_X,
  MAP_PIN_ANCHOR_Y,
  MAP_PIN_DISPLAY_HEIGHT,
  MAP_PIN_DISPLAY_WIDTH,
} from '../../utils/mapPinIcons';
import { getArmarioIconDataUrl } from '../../utils/armarioIcons';
import { getPosteIconDataUrl } from '../../utils/posteIcons';
import { getReservaIconDataUrl } from '../../utils/reservaIcons';
import {
  appendNameBelowIconSvg,
  getExtraDisplayHeightForNameLabel,
} from '../../utils/mapMarkerNameUtils';
import { wrapSvgWithBounceAnimation } from '../../utils/iconAnimationUtils';
import { bindMapOverlayContextMenu } from './utils/mapOverlayContextMenu';
import { caboService, Cabo, CaboPonto, CaboFuncaoEstrutural, CaboTipo, getCaboFuncaoEstruturalLabel, getCaboTipoLabel } from '../../services/caboService';
import { buildCablePolylineOptions, buildCableSelectionOutlineOptions } from './utils/cableMapStyle';
import { ftthPosteService, FtthPoste, FtthPosteForMap } from '../../services/ftthPosteService';
import { ftthAreaMapaService, FtthAreaMapaForMap } from '../../services/ftthAreaMapaService';
import { escritorioService, Escritorio } from '../../services/escritorioService';

import CaixaForm from '../../components/forms/FtthCaixaCtoForm';
import FtthCaixaReservaForm from '../../components/forms/FtthCaixaReservaForm';
import FtthCaixaCeoForm from '../../components/forms/FtthCaixaCeoForm';
import FtthArmarioForm from '../../components/forms/FtthArmarioForm';
import FtthPosteForm from '../../components/forms/FtthPosteForm';
import FtthAreaMapaForm from '../../components/forms/FtthAreaMapaForm';
import CabosForm from '../../components/forms/FtthCabosForm';
import CaixaDiagramModal from '../../components/modals/CaixaDiagramModal';
import ViabilidadeModal, { ViabilidadeResultado } from '../../components/modals/ViabilidadeModal';
import ArmarioDiagramModal from '../../components/modals/ArmarioDiagramModal';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Tipos para coordenadas e cabos
interface Coordinates {
  lat: number;
  lng: number;
  id?: number;
  proximo_ponto_id?: number | null;
  caixa_armario_id?: number | null;
  caixa_armario_tipo?: 'caixa' | 'armario' | null;
}

interface Cable {
  id: string;
  points: Coordinates[];
  type: 'fiber' | 'drawing';
  color: string;
  editable?: boolean;
  titulo?: string;
  qtde_fo?: number;
  tipo?: CaboTipo | null;
  funcao_estrutural?: CaboFuncaoEstrutural | null;
  fabricante?: string | null;
  lote?: string | null;
  observacoes?: string | null;
  escritorio_id?: number | null;
}

function mapCaboToCable(cabo: Cabo, editable = false): Cable {
  return {
    id: `cabo-${cabo.id}`,
    points: mapCaboPontosToCoordinates(cabo.pontos),
    type: 'fiber',
    color: cabo.color,
    editable,
    titulo: cabo.titulo,
    qtde_fo: cabo.qtde_fo,
    tipo: cabo.tipo ?? 'as',
    funcao_estrutural: cabo.funcao_estrutural ?? 'distribuicao',
    fabricante: cabo.fabricante ?? null,
    lote: cabo.lote ?? null,
    observacoes: cabo.observacoes ?? null,
    escritorio_id: cabo.escritorio_id ?? null,
  };
}

function filterCablesByEscritorio(cables: Cable[], escritorioId?: number): Cable[] {
  if (!escritorioId) return cables;
  return cables.filter((cable) => Number(cable.escritorio_id) === escritorioId);
}

function mapCaboPontosToCoordinates(pontos: CaboPonto[]): Coordinates[] {
  return pontos.map((p) => ({
    lat: p.lat,
    lng: p.lng,
    id: p.id,
    proximo_ponto_id: p.proximo_ponto_id,
    caixa_armario_id: p.caixa_armario_id ?? null,
    caixa_armario_tipo: p.caixa_armario_tipo ?? null,
  }));
}

interface CaixaContextMenu {
  visible: boolean;
  x: number;
  y: number;
  caixa: FtthCaixa | null;
}

interface CableContextMenu {
  visible: boolean;
  x: number;
  y: number;
  cable: Cable | null;
}

// Interface para área do mapa (polígono) - usada em AreaContextMenu
interface FtthAreaMapaForMapLocal {
  id: number;
  nome: string;
  coordenadas: Array<{ lat: number; lng: number }>;
  cor: string;
  escritorio_id: number;
  informacoes?: string | null;
}

interface PosteContextMenu {
  visible: boolean;
  x: number;
  y: number;
  poste: FtthPosteForMap | null;
}

interface ArmarioContextMenu {
  visible: boolean;
  x: number;
  y: number;
  armario: FtthArmario | null;
}

interface AreaContextMenu {
  visible: boolean;
  x: number;
  y: number;
  area: FtthAreaMapaForMapLocal | null;
}

// Cores dos marcadores de CAIXA baseadas no status
const CAIXA_COLORS = {
  ativo: '#4CAF50',          // Verde
  inativo: '#F44336',        // Vermelho
  aguardando_instalacao: '#FF9800', // Laranja
  manutencao: '#9C27B0'      // Roxo
};

// Função utilitária para validar e converter coordenadas
const validateCoordinates = (lat: any, lng: any): { lat: number; lng: number } | null => {
  const latitude = typeof lat === 'string' ? parseFloat(lat) : lat;
  const longitude = typeof lng === 'string' ? parseFloat(lng) : lng;

  if (latitude && longitude &&
    !isNaN(latitude) && !isNaN(longitude) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180) {
    return { lat: latitude, lng: longitude };
  }

  return null;
};

// Função para calcular distância de um ponto a um segmento de linha
const distanceToLineSegment = (point: Coordinates, lineStart: Coordinates, lineEnd: Coordinates): number => {
  const A = point.lat - lineStart.lat;
  const B = point.lng - lineStart.lng;
  const C = lineEnd.lat - lineStart.lat;
  const D = lineEnd.lng - lineStart.lng;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  if (lenSq !== 0) // em caso de linha com comprimento zero
    param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = lineStart.lat;
    yy = lineStart.lng;
  } else if (param > 1) {
    xx = lineEnd.lat;
    yy = lineEnd.lng;
  } else {
    xx = lineStart.lat + param * C;
    yy = lineStart.lng + param * D;
  }

  const dx = point.lat - xx;
  const dy = point.lng - yy;
  return Math.sqrt(dx * dx + dy * dy);
};

// Função para calcular distância entre dois pontos geográficos (Haversine)
const calculateDistance = (point1: Coordinates, point2: Coordinates): number => {
  const R = 6371; // Raio da Terra em km
  const dLat = (point2.lat - point1.lat) * Math.PI / 180;
  const dLng = (point2.lng - point1.lng) * Math.PI / 180;
  const lat1 = point1.lat * Math.PI / 180;
  const lat2 = point2.lat * Math.PI / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distância em km
};

// Função para calcular distância total de uma sequência de pontos
const calculateTotalDistance = (points: Coordinates[]): number => {
  if (points.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    totalDistance += calculateDistance(points[i], points[i + 1]);
  }

  return totalDistance;
};

// Função para calcular área de polígono em m² (coordenadas lat/lng)
const calculatePolygonAreaM2 = (coords: Array<{ lat: number; lng: number }>): number => {
  if (!coords || coords.length < 3) return 0;
  const pts = [...coords];
  if (pts[0].lat !== pts[pts.length - 1].lat || pts[0].lng !== pts[pts.length - 1].lng) {
    pts.push(pts[0]);
  }
  const lat0 = pts[0].lat * Math.PI / 180;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos(lat0);
  let area = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const x1 = pts[i].lng * mPerDegLng;
    const y1 = pts[i].lat * mPerDegLat;
    const x2 = pts[i + 1].lng * mPerDegLng;
    const y2 = pts[i + 1].lat * mPerDegLat;
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area / 2);
};

// Função para formatar distância para exibição
const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${(distance * 1000).toFixed(0)}m`;
  } else {
    return `${distance.toFixed(2)}km`;
  }
};

function getCableDisplayLabel(cable: Pick<Cable, 'id' | 'titulo'>): string {
  const caboId = cable.id.replace('cabo-', '');
  return cable.titulo?.trim() || `Cabo #${caboId}`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const MAP_IW_STYLES = `
  <style>
    .map-iw-root {
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      overflow: hidden;
      font-family: 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .map-iw-inner {
      box-sizing: border-box;
      width: 100%;
      overflow: hidden;
      padding: 6px 6px 4px 4px;
    }
    .map-iw-kpis {
      display: flex;
      gap: 5px;
      margin-bottom: 8px;
      flex-wrap: wrap;
    }
    .map-iw-kpi {
      flex: 1 1 calc(33.333% - 4px);
      min-width: 0;
      background: #f4f6f8;
      border-radius: 6px;
      padding: 5px 4px;
      text-align: center;
      box-sizing: border-box;
    }
    .map-iw-kpi-value {
      font-size: 14px;
      font-weight: 800;
      color: #1a1a2e;
      line-height: 1.1;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .map-iw-kpi-label {
      font-size: 9px;
      font-weight: 600;
      color: #6b7280;
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .map-iw-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .map-iw-table td:first-child {
      width: 36%;
      padding: 4px 8px 4px 0;
      color: #6b7280;
      font-size: 11px;
      font-weight: 500;
      vertical-align: top;
      white-space: normal;
      word-break: break-word;
    }
    .map-iw-table td:last-child {
      padding: 4px 0;
      color: #111827;
      font-size: 12px;
      font-weight: 600;
      text-align: right;
      vertical-align: top;
      word-break: break-word;
      overflow-wrap: anywhere;
    }
    .map-iw-table tr:not(:last-child) td {
      border-bottom: 1px solid #eef0f3;
    }
    .map-iw-btn,
    .cable-iw-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      padding: 5px 7px;
      margin-bottom: 2px;
      border-radius: 6px;
      cursor: pointer;
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      text-align: left;
    }
    .map-iw-btn span,
    .cable-iw-btn span {
      min-width: 0;
      flex: 1;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .map-iw-btn:hover,
    .cable-iw-btn:hover { background: #f3f4f6 !important; }
    .map-iw-btn--danger:hover,
    .cable-iw-btn--danger:hover { background: #fee2e2 !important; }
    @media (max-width: 480px) {
      .map-iw-kpi { flex: 1 1 calc(50% - 3px); }
      .map-iw-kpi-value { font-size: 13px; }
      .map-iw-table td:first-child { width: 40%; font-size: 10px; }
      .map-iw-table td:last-child { font-size: 11px; }
    }
  </style>`;

function getMapInfoWindowMaxWidth(variant: 'default' | 'wide' = 'default'): number {
  if (typeof window === 'undefined') return variant === 'wide' ? 340 : 260;
  const cap = variant === 'wide' ? 340 : 260;
  return Math.max(180, Math.min(cap, window.innerWidth - 40));
}

function applyMapInfoWindowResponsiveOptions(
  infoWindow: google.maps.InfoWindow,
  variant: 'default' | 'wide' = 'default',
): void {
  infoWindow.setOptions({ maxWidth: getMapInfoWindowMaxWidth(variant) });
}

function normalizeMapInfoWindowLayout(maxWidth?: number): void {
  const maxW = maxWidth ?? getMapInfoWindowMaxWidth();
  document.querySelectorAll('.gm-style-iw-c').forEach((el) => {
    const node = el as HTMLElement;
    node.style.maxWidth = `${maxW}px`;
    node.style.padding = '0';
  });
  document.querySelectorAll('.gm-style-iw-ch').forEach((el) => {
    const node = el as HTMLElement;
    node.style.padding = '0';
    node.style.overflow = 'hidden';
  });
  document.querySelectorAll('.gm-style-iw-d').forEach((el) => {
    const node = el as HTMLElement;
    node.style.overflow = 'hidden';
    node.style.overflowX = 'hidden';
    node.style.maxWidth = '100%';
    node.style.boxSizing = 'border-box';
    node.style.padding = '0';
  });
  document.querySelectorAll('.gm-style-iw').forEach((el) => {
    (el as HTMLElement).style.padding = '0';
  });
}

function mapIwKpi(valor: string, rotulo: string): string {
  return `
    <div class="map-iw-kpi">
      <div class="map-iw-kpi-value">${valor}</div>
      <div class="map-iw-kpi-label">${rotulo}</div>
    </div>`;
}

function mapIwLinha(rotulo: string, valor: string): string {
  return `
    <tr>
      <td>${rotulo}</td>
      <td>${valor}</td>
    </tr>`;
}

function mapIwActionBtn(
  dataAttr: 'data-cable-action' | 'data-caixa-action',
  action: string,
  label: string,
  icon: string,
  btnClass: string,
  danger = false,
): string {
  return `
    <button type="button" ${dataAttr}="${action}" class="${btnClass}${danger ? ` ${btnClass}--danger` : ''}" style="border:1px solid ${danger ? '#fecaca' : '#e5e7eb'};background:${danger ? '#fff5f5' : '#fff'};color:${danger ? '#d32f2f' : '#374151'};">
      ${icon}
      <span>${label}</span>
    </button>`;
}

function mapIwShell(id: string, accentColor: string, sectionTitle: string, nome: string, identificador: string, body: string): string {
  return `
    ${MAP_IW_STYLES}
    <div id="${id}" class="map-iw-root">
      <div class="map-iw-inner" style="border-top:3px solid ${accentColor};">
        <div style="font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:2px;">${sectionTitle}</div>
        <div style="font-size:15px;font-weight:800;color:#111827;line-height:1.2;margin-bottom:2px;word-break:break-word;overflow-wrap:anywhere;">${nome}</div>
        <div style="font-size:11px;color:#6b7280;margin-bottom:8px;">Identificador <strong style="color:#374151;">#${identificador}</strong></div>
        ${body}
      </div>
    </div>`;
}

function mapIwActionsSection(actionsHtml: string): string {
  return `
    <div style="margin-top:8px;padding-top:6px;border-top:1px solid #eef0f3;">
      <div style="font-size:9px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px;">Ações</div>
      ${actionsHtml}
    </div>`;
}

const MAP_INFO_SWAL_STYLES = `
  <style>
    .swal2-popup.map-info-swal-popup { padding: 0.65rem 0.85rem 0.75rem !important; border-radius: 12px !important; }
    .swal2-popup.map-info-swal-popup .swal2-title.map-info-swal-title {
      display: none !important;
      margin: 0 !important;
      padding: 0 !important;
      height: 0 !important;
    }
    .swal2-popup.map-info-swal-popup .swal2-html-container.map-info-swal-html {
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }
    .swal2-popup.map-info-swal-popup .swal2-actions.map-info-swal-actions {
      margin: 0.65rem 0 0 !important;
      padding: 0 !important;
    }
    .swal2-popup.map-info-swal-popup .swal2-confirm.map-info-swal-btn {
      margin: 0 !important;
      padding: 0.4rem 1rem !important;
      font-size: 0.8rem !important;
      font-weight: 600 !important;
      border-radius: 8px !important;
      box-shadow: none !important;
    }
  </style>`;

function getMapInfoSwalTarget(): HTMLElement {
  return (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement;
}

function getMapInfoSwalCustomClass(isFullscreen: boolean) {
  return {
    ...(isFullscreen ? {
      container: 'swal2-fullscreen-container',
      popup: 'swal2-fullscreen-popup map-info-swal-popup',
    } : {
      popup: 'map-info-swal-popup',
    }),
    title: 'map-info-swal-title',
    htmlContainer: 'map-info-swal-html',
    actions: 'map-info-swal-actions',
    confirmButton: 'map-info-swal-btn',
  };
}

async function showMapInfoSwal(params: {
  html: string;
  confirmColor?: string;
  isFullscreen: boolean;
}) {
  await Swal.fire({
    title: ' ',
    html: `${MAP_INFO_SWAL_STYLES}${params.html}`,
    confirmButtonText: 'Fechar',
    confirmButtonColor: params.confirmColor || '#1976d2',
    width: 'min(360px, 92vw)',
    padding: '0.65rem',
    target: getMapInfoSwalTarget(),
    customClass: getMapInfoSwalCustomClass(params.isFullscreen),
    allowOutsideClick: true,
    allowEscapeKey: true,
  });
}

function buildAreaInfoSwalHtml(area: FtthAreaMapaForMapLocal, areaM2: number, informacoes: string): string {
  const nome = escapeHtml(area.nome || 'Área');
  const color = area.cor?.trim() || '#2196F3';
  const m2Str = areaM2 >= 1 ? `${areaM2.toFixed(2)} m²` : `${areaM2.toFixed(4)} m²`;
  const hectaresStr = `${(areaM2 / 10_000).toFixed(4)} ha`;
  const km2Str = `${(areaM2 / 1_000_000).toFixed(4)} km²`;

  const obs = informacoes.trim()
    ? `<div style="margin-top:6px;padding:6px 8px;background:#fafafa;border-radius:6px;border:1px solid #eee;">
        <div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;margin-bottom:2px;">Informações</div>
        <div style="font-size:11px;color:#374151;line-height:1.4;white-space:pre-wrap;max-height:96px;overflow-y:auto;">${escapeHtml(informacoes.trim())}</div>
      </div>`
    : '';

  const body = `
    <div class="map-iw-kpis">
      ${mapIwKpi(m2Str, 'Metragem')}
      ${mapIwKpi(hectaresStr, 'Hectares')}
      ${mapIwKpi(km2Str, 'Km²')}
    </div>
    ${obs}`;

  return mapIwShell('swal-area-info', color, 'Detalhes da Área', nome, String(area.id), body);
}

function buildCableInfoWindowContent(cable: Cable): string {
  const caboId = cable.id.replace('cabo-', '');
  const nome = escapeHtml(getCableDisplayLabel(cable));
  const totalDistance = formatDistance(calculateTotalDistance(cable.points));
  const color = cable.color?.trim() || '#FF9800';
  const tipo = escapeHtml(getCaboTipoLabel(cable.tipo));
  const funcao = escapeHtml(getCaboFuncaoEstruturalLabel(cable.funcao_estrutural));
  const fabricante = cable.fabricante?.trim() || '—';
  const lote = cable.lote?.trim() || '—';
  const observacoes = cable.observacoes?.trim();
  const fibras = cable.qtde_fo != null ? String(cable.qtde_fo) : '—';
  const pontos = String(cable.points.length);
  const estado = cable.type === 'fiber' ? 'Fibra óptica' : 'Em desenho';

  const extras = [
    mapIwLinha('Fabricante', escapeHtml(fabricante)),
    mapIwLinha('Lote', escapeHtml(lote)),
    mapIwLinha('Estado', estado),
    mapIwLinha('Cor no mapa', `<span style="display:inline-flex;align-items:center;gap:4px;justify-content:flex-end;flex-wrap:wrap;"><span style="width:12px;height:12px;border-radius:3px;background:${color};border:1px solid rgba(0,0,0,0.1);display:inline-block;flex-shrink:0;"></span>${escapeHtml(color.toUpperCase())}</span>`),
  ].join('');

  const obs = observacoes
    ? `<div style="margin-top:6px;padding:6px 8px;background:#fafafa;border-radius:6px;border:1px solid #eee;">
        <div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;margin-bottom:2px;">Observações</div>
        <div style="font-size:11px;color:#374151;line-height:1.4;">${escapeHtml(observacoes)}</div>
      </div>`
    : '';

  const iconEdit = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#1976d2"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
  const iconMove = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#616161"><path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2-5-5v3h-3v4h3v3l5-5zm-6 1h-4v3H7l5 5 5-5h-3v-3z"/></svg>';
  const iconDelete = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#d32f2f"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>';

  const actions = [
    mapIwActionBtn('data-cable-action', 'edit', 'Editar Informações do Cabo', iconEdit, 'cable-iw-btn'),
    mapIwActionBtn('data-cable-action', 'edit-points', 'Editar Pontos do Cabo', iconMove, 'cable-iw-btn'),
    mapIwActionBtn('data-cable-action', 'delete', 'Deletar Cabo', iconDelete, 'cable-iw-btn', true),
  ].join('');

  const body = `
    <div class="map-iw-kpis">
      ${mapIwKpi(totalDistance, 'Distância')}
      ${mapIwKpi(pontos, 'Pontos')}
      ${mapIwKpi(fibras, 'Fibras')}
    </div>
    <table class="map-iw-table">
      ${mapIwLinha('Tipo', `<span style="color:${color};">${tipo}</span>`)}
      ${mapIwLinha('Função', funcao)}
      ${extras}
    </table>
    ${obs}
    ${mapIwActionsSection(actions)}`;

  return mapIwShell(`cable-infowindow-${caboId}`, color, 'Detalhes do Cabo', nome, caboId, body);
}

function bindCableInfoWindowActions(
  cable: Cable,
  infoWindow: google.maps.InfoWindow,
  actions: {
    onEdit: (cable: Cable) => void;
    onEditPoints: (cable: Cable) => void;
    onDelete: (cable: Cable) => void;
  },
) {
  const caboId = cable.id.replace('cabo-', '');
  const root = document.getElementById(`cable-infowindow-${caboId}`);
  if (!root) return;

  root.querySelectorAll('[data-cable-action]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const action = el.getAttribute('data-cable-action');
      infoWindow.close();
      switch (action) {
        case 'edit':
          actions.onEdit(cable);
          break;
        case 'edit-points':
          actions.onEditPoints(cable);
          break;
        case 'delete':
          actions.onDelete(cable);
          break;
      }
    });
  });
}

function getCaixaAccentColor(caixa: FtthCaixa): string {
  if (caixa.status === 'aguardando_instalacao') return CAIXA_COLORS.aguardando_instalacao;
  if (caixa.color && /^#[0-9A-Fa-f]{3,8}$/.test(caixa.color)) return caixa.color;
  if (caixa.tipo === 'reserva') return '#0091da';
  return CAIXA_COLORS[caixa.status as keyof typeof CAIXA_COLORS] || '#1976d2';
}

function getCaixaAvailablePorts(caixa: FtthCaixa): number {
  let disabledPortsList: Array<{ port: number }> = [];
  if (Array.isArray(caixa.disabled_ports)) {
    disabledPortsList = caixa.disabled_ports;
  } else if (typeof caixa.disabled_ports === 'string') {
    try {
      const parsed = JSON.parse(caixa.disabled_ports);
      disabledPortsList = Array.isArray(parsed) ? parsed : [];
    } catch {
      disabledPortsList = [];
    }
  }
  const totalPorts = caixa.capacidade ?? 0;
  const disabledCount = disabledPortsList.length;
  const occupiedPorts = caixa.portas_em_uso ?? 0;
  return Math.max(0, totalPorts - disabledCount - occupiedPorts);
}

function formatReservaMapLabel(caixa: Pick<FtthCaixa, 'id' | 'metragem'>): string {
  if (caixa.metragem != null && Number.isFinite(Number(caixa.metragem))) {
    return `${caixa.metragem}m`;
  }
  return `#${caixa.id}`;
}

function getCaixaMarkerLabelText(caixa: FtthCaixa, editingCable: Cable | null): string | null {
  if (caixa.tipo === 'cto' || caixa.tipo === 'ceo') {
    return caixa.name;
  }
  if (caixa.tipo === 'reserva' && editingCable) {
    const linkedToEditingCable = editingCable.points.some(
      (p) => p.caixa_armario_id === caixa.id && p.caixa_armario_tipo === 'caixa',
    );
    if (linkedToEditingCable) {
      return formatReservaMapLabel(caixa);
    }
  }
  return null;
}

function getLinkedCaixaForPoint(point: Coordinates, caixas: FtthCaixa[]): FtthCaixa | null {
  if (point.caixa_armario_tipo !== 'caixa' || point.caixa_armario_id == null) return null;
  return caixas.find((c) => c.id === point.caixa_armario_id) ?? null;
}

function buildCaixaInfoWindowContent(
  caixa: FtthCaixa,
  coordinates: { lat: number; lng: number },
  options: { isMoving: boolean },
): string {
  const isReserva = caixa.tipo === 'reserva';
  const nome = escapeHtml(caixa.name || `Caixa #${caixa.id}`);
  const color = getCaixaAccentColor(caixa);
  const statusColor = CAIXA_COLORS[caixa.status as keyof typeof CAIXA_COLORS] || color;
  const statusLabel = escapeHtml(caixa.status.replace(/_/g, ' ').toUpperCase());
  const tipoLabel = escapeHtml(caixa.tipo.toUpperCase());
  const endereco = escapeHtml(caixa.address?.trim() || 'Não informado');
  const sectionTitle = isReserva ? 'Detalhes da Reserva' : 'Detalhes da Caixa';

  const iconEdit = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#1976d2"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
  const iconMove = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#616161"><path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2-5-5v3h-3v4h3v3l5-5zm-6 1h-4v3H7l5 5 5-5h-3v-3z"/></svg>';
  const iconDelete = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#d32f2f"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>';
  const iconDiagram = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#2e7d32"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 8h2v-2H7v2zm0-4h2v-2H7v2zm0-4h2V7H7v2zm4 8h2v-2h-2v2zm0-4h2v-2h-2v2zm0-4h2V7h-2v2zm4 8h2v-2h-2v2zm0-4h2v-2h-2v2z"/></svg>';
  const iconView = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#0288d1"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>';
  const iconCancel = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#d32f2f"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';

  let kpis = '';
  let detailRows = '';

  if (isReserva) {
    const metragem = (caixa as FtthCaixa & { metragem?: number | null }).metragem;
    const caboId = (caixa as FtthCaixa & { cabo_id?: number | null }).cabo_id;
    kpis = `
      <div class="map-iw-kpis">
        ${mapIwKpi(metragem != null ? `${metragem} m` : '—', 'Metragem')}
        ${mapIwKpi(caboId ? `#${caboId}` : '—', 'Cabo')}
      </div>`;
    detailRows = `
      ${mapIwLinha('Tipo', tipoLabel)}
      ${mapIwLinha('Endereço', endereco)}`;
  } else {
    const livres = getCaixaAvailablePorts(caixa);
    const emUso = caixa.portas_em_uso ?? 0;
    const capacidade = caixa.capacidade ?? 0;
    kpis = `
      <div class="map-iw-kpis">
        ${mapIwKpi(String(capacidade), 'Capacidade')}
        ${mapIwKpi(String(emUso), 'Em uso')}
        ${mapIwKpi(String(livres), 'Livres')}
      </div>`;
    const oltRow = caixa.pon_port?.olt?.name
      ? mapIwLinha('OLT', escapeHtml(caixa.pon_port.olt.name))
      : '';
    const sfpValue = (caixa.sfp ?? caixa.pon_port?.sfp)?.trim();
    const sfpRow = caixa.tipo === 'cto'
      ? mapIwLinha('SFP', escapeHtml(sfpValue || '—'))
      : '';
    detailRows = `
      ${mapIwLinha('Tipo', tipoLabel)}
      ${mapIwLinha('Status', `<span style="color:${statusColor};">${statusLabel}</span>`)}
      ${mapIwLinha('Endereço', endereco)}
      ${oltRow}
      ${sfpRow}
      ${mapIwLinha('Coordenadas', `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`)}`;
  }

  let actions = '';
  if (options.isMoving) {
    actions = mapIwActionBtn('data-caixa-action', 'cancel', 'Cancelar alteração', iconCancel, 'map-iw-btn', true);
  } else if (isReserva) {
    actions = [
      mapIwActionBtn('data-caixa-action', 'edit', 'Editar reserva', iconEdit, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'move', 'Mover', iconMove, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'delete', 'Deletar reserva', iconDelete, 'map-iw-btn', true),
    ].join('');
  } else {
    actions = [
      mapIwActionBtn('data-caixa-action', 'edit', 'Editar Caixa', iconEdit, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'diagram', 'Diagrama de Rede', iconDiagram, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'view', 'Visualizar Caixa', iconView, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'move', 'Mover', iconMove, 'map-iw-btn'),
      mapIwActionBtn('data-caixa-action', 'delete', 'Deletar Caixa', iconDelete, 'map-iw-btn', true),
    ].join('');
  }

  const body = `
    ${kpis}
    <table class="map-iw-table">
      ${detailRows}
    </table>
    ${mapIwActionsSection(actions)}`;

  return mapIwShell(`caixa-infowindow-${caixa.id}`, color, sectionTitle, nome, String(caixa.id), body);
}

function bindCaixaInfoWindowActions(
  caixa: FtthCaixa,
  infoWindow: google.maps.InfoWindow,
  actions: {
    onEdit: (caixa: FtthCaixa) => void;
    onMove: (caixa: FtthCaixa) => void;
    onDelete: (caixa: FtthCaixa) => void;
    onDiagram: (caixa: FtthCaixa) => void;
    onView: (caixaId: number) => void;
    onCancelMove: () => void;
  },
) {
  const root = document.getElementById(`caixa-infowindow-${caixa.id}`);
  if (!root) return;

  root.querySelectorAll('[data-caixa-action]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const action = el.getAttribute('data-caixa-action');
      infoWindow.close();
      switch (action) {
        case 'edit':
          actions.onEdit(caixa);
          break;
        case 'move':
          actions.onMove(caixa);
          break;
        case 'delete':
          actions.onDelete(caixa);
          break;
        case 'diagram':
          actions.onDiagram(caixa);
          break;
        case 'view':
          actions.onView(caixa.id);
          break;
        case 'cancel':
          actions.onCancelMove();
          break;
      }
    });
  });
}

// Função para extrair pontos_id de caixa ou armário (pode vir como array ou JSON string)
const parsePontosId = (val: unknown): number[] => {
  if (Array.isArray(val)) return val.filter((x): x is number => typeof x === 'number');
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed.filter((x: unknown): x is number => typeof x === 'number') : [];
    } catch { return []; }
  }
  return [];
};

// Componente do Menu de Contexto para Caixas
interface CaixaContextMenuProps {
  menu: CaixaContextMenu;
  onClose: () => void;
  onEdit: (caixa: FtthCaixa) => void;
  onMove: (caixa: FtthCaixa) => void;
  onDelete: (caixa: FtthCaixa) => void;
  onDiagram: (caixa: FtthCaixa) => void;
  caixaIdBeingMoved?: number | null;
  onCancelMove?: () => void;
}

const CaixaContextMenuComponent: React.FC<CaixaContextMenuProps> = ({
  menu,
  onClose,
  onEdit,
  onMove,
  onDelete,
  onDiagram,
  caixaIdBeingMoved = null,
  onCancelMove,
}) => {
  const navigate = useNavigate();

  if (!menu.visible || !menu.caixa) return null;

  const isReserva = menu.caixa.tipo === 'reserva';

  // Handler para bloquear menu de contexto dentro do componente
  const handleMenuContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();    
  };
  const visualizarCaixa = (caixaId: number) => {
    navigate(`/ftth/caixas/${caixaId}`);
  };

  return (
    <>
      {/* Overlay para fechar o menu */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: 'transparent',
        }}
        onClick={onClose}
        onContextMenu={handleMenuContextMenu}
      />

      {/* Menu de contexto */}
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: menu.y,
          left: menu.x,
          zIndex: 999999,
          minWidth: 180,
          py: 1,
          border: '2px solid #4CAF50',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}
        onContextMenu={handleMenuContextMenu}
      >
        {caixaIdBeingMoved !== menu.caixa?.id && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                if (menu.caixa) {
                  onEdit(menu.caixa);
                  onClose();
                }
              }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
              <Typography variant="body2">{isReserva ? 'Editar reserva' : 'Editar Caixa'}</Typography>
            </Box>

            {!isReserva && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                if (menu.caixa) {
                  onDiagram(menu.caixa);
                  onClose();
                }
              }}
            >
              <SplitterIcon sx={{ mr: 1, fontSize: 18 }} color="success" />
              <Typography variant="body2">Diagrama de Rede</Typography>
            </Box>
            )}

            {!isReserva && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                if (menu.caixa) {
                  visualizarCaixa(menu.caixa.id);
                  onClose();
                }
              }}
            >
              <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} color="info" />
              <Typography variant="body2">Visualizar Caixa</Typography>
            </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                if (menu.caixa) {
                  onMove(menu.caixa);
                  onClose();
                }
              }}
            >
              <OpenWithIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
              <Typography variant="body2">Mover</Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => {
                if (menu.caixa) {
                  onDelete(menu.caixa);
                  onClose();
                }
              }}
            >
              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
              <Typography variant="body2" color="error">{isReserva ? 'Deletar reserva' : 'Deletar Caixa'}</Typography>
            </Box>
          </>
        )}

        {caixaIdBeingMoved === menu.caixa?.id && onCancelMove && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => { onCancelMove(); onClose(); }}
          >
            <CancelIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
            <Typography variant="body2" color="error">Cancelar alteração</Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

// Componente do Menu de Contexto para Cabos
interface CableContextMenuProps {
  menu: CableContextMenu;
  onClose: () => void;
  onEdit: (cable: Cable) => void;
  onEditPoints: (cable: Cable) => void;
  onDelete: (cable: Cable) => void;
}

// Componente do Menu de Contexto para Armários (Editar, Diagrama, Mover, Deletar)
interface ArmarioContextMenuProps {
  menu: ArmarioContextMenu;
  onClose: () => void;
  onEdit: (armario: FtthArmario) => void;
  onMove: (armario: FtthArmario) => void;
  onDiagram: (armario: FtthArmario) => void;
  onDelete: (armario: FtthArmario) => void;
  armarioIdBeingMoved?: number | null;
  onCancelMove?: () => void;
}

const ArmarioContextMenuComponent: React.FC<ArmarioContextMenuProps> = ({
  menu,
  onClose,
  onEdit,
  onMove,
  onDiagram,
  onDelete,
  armarioIdBeingMoved = null,
  onCancelMove,
}) => {
  if (!menu.visible || !menu.armario) return null;

  const handleMenuContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: 'transparent',
        }}
        onClick={onClose}
        onContextMenu={handleMenuContextMenu}
      />
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: menu.y,
          left: menu.x,
          zIndex: 999999,
          minWidth: 180,
          py: 1,
          border: '2px solid #9C27B0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}
        onContextMenu={handleMenuContextMenu}
      >
        {armarioIdBeingMoved !== menu.armario?.id && (
          <>
            <Box
              sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => { if (menu.armario) { onEdit(menu.armario); onClose(); } }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
              <Typography variant="body2">Editar</Typography>
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => { if (menu.armario) { onDiagram(menu.armario); onClose(); } }}
            >
              <SplitterIcon sx={{ mr: 1, fontSize: 18 }} color="success" />
              <Typography variant="body2">Diagrama</Typography>
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => { if (menu.armario) { onMove(menu.armario); onClose(); } }}
            >
              <OpenWithIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
              <Typography variant="body2">Mover</Typography>
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => { if (menu.armario) { onDelete(menu.armario); onClose(); } }}
            >
              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
              <Typography variant="body2" color="error">Deletar</Typography>
            </Box>
          </>
        )}
        {armarioIdBeingMoved === menu.armario?.id && onCancelMove && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={() => { onCancelMove(); onClose(); }}
          >
            <CancelIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
            <Typography variant="body2" color="error">Cancelar alteração</Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

const CableContextMenuComponent: React.FC<CableContextMenuProps> = ({
  menu,
  onClose,
  onEdit,
  onEditPoints,
  onDelete
}) => {
  if (!menu.visible || !menu.cable) return null;

  // Handler para bloquear menu de contexto dentro do componente
  const handleMenuContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      {/* Overlay para fechar o menu */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: 'transparent',
        }}
        onClick={onClose}
        onContextMenu={handleMenuContextMenu}
      />

      {/* Menu de contexto */}
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: menu.y,
          left: menu.x,
          zIndex: 999999,
          minWidth: 180,
          py: 1,
          border: '2px solid #FF9800',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}
        onContextMenu={handleMenuContextMenu}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            if (menu.cable) {
              onEdit(menu.cable);
              onClose();
            }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
          <Typography variant="body2">Editar Informações do Cabo</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            if (menu.cable) {
              onEditPoints(menu.cable);
              onClose();
            }
          }}
        >
          <OpenWithIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
          <Typography variant="body2">Editar Pontos do Cabo</Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            if (menu.cable) {
              onDelete(menu.cable);
              onClose();
            }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
          <Typography variant="body2" color="error">Deletar Cabo</Typography>
        </Box>
      </Paper>
    </>
  );
};

// Componente do Menu de Contexto para Postes
interface PosteContextMenuProps {
  menu: PosteContextMenu;
  onClose: () => void;
  onEdit: (poste: FtthPosteForMap) => void;
  onDelete: (poste: FtthPosteForMap) => void;
  onMove: (poste: FtthPosteForMap) => void;
  onCopy: (poste: FtthPosteForMap) => void;
  posteIdBeingMoved?: number | null;
  onCancelMove?: () => void;
}

const PosteContextMenuComponent: React.FC<PosteContextMenuProps> = ({
  menu,
  onClose,
  onEdit,
  onDelete,
  onMove,
  onCopy,
  posteIdBeingMoved = null,
  onCancelMove,
}) => {
  if (!menu.visible || !menu.poste) return null;

  const handleMenuContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: 'transparent',
        }}
        onClick={onClose}
        onContextMenu={handleMenuContextMenu}
      />
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: menu.y,
          left: menu.x,
          zIndex: 999999,
          minWidth: 180,
          py: 1,
          border: '2px solid #666666',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}
        onContextMenu={handleMenuContextMenu}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          onClick={() => {
            if (menu.poste) {
              onEdit(menu.poste);
              onClose();
            }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
          <Typography variant="body2">Editar</Typography>
        </Box>
        {posteIdBeingMoved !== menu.poste?.id && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
              if (menu.poste) {
                onMove(menu.poste);
                onClose();
              }
            }}
          >
            <OpenWithIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
            <Typography variant="body2">Mover</Typography>
          </Box>
        )}
        {posteIdBeingMoved === menu.poste?.id && onCancelMove && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => { onCancelMove(); onClose(); }}
          >
            <CancelIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
            <Typography variant="body2" color="error">Cancelar alteração</Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          onClick={() => {
            if (menu.poste) {
              onCopy(menu.poste);
              onClose();
            }
          }}
        >
          <ContentCopyIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
          <Typography variant="body2">Copiar</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          onClick={() => {
            if (menu.poste) {
              onDelete(menu.poste);
              onClose();
            }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
          <Typography variant="body2" color="error">Deletar</Typography>
        </Box>
      </Paper>
    </>
  );
};

// Componente do Menu de Contexto para Áreas (polígonos)
interface AreaContextMenuProps {
  menu: AreaContextMenu;
  onClose: () => void;
  onEdit: (area: FtthAreaMapaForMapLocal) => void;
  onEditPoints: (area: FtthAreaMapaForMapLocal) => void;
  onDelete: (area: FtthAreaMapaForMapLocal) => void;
  onInfo: (area: FtthAreaMapaForMapLocal) => void;
  /** Quando true e menu.area é a área em edição, mostra apenas "Cancelar edição" */
  editingAreaPoints?: boolean;
  editingAreaId?: number | null;
  onCancelEditPoints?: () => void;
}

const AreaContextMenuComponent: React.FC<AreaContextMenuProps> = ({
  menu,
  onClose,
  onEdit,
  onEditPoints,
  onDelete,
  onInfo,
  editingAreaPoints,
  editingAreaId,
  onCancelEditPoints,
}) => {
  if (!menu.visible || !menu.area) return null;

  const isEditingThisArea = editingAreaPoints && editingAreaId !== null && menu.area.id === editingAreaId;

  const handleMenuContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: 'transparent',
        }}
        onClick={onClose}
        onContextMenu={handleMenuContextMenu}
      />
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          top: menu.y,
          left: menu.x,
          zIndex: 999999,
          minWidth: 180,
          py: 1,
          border: '2px solid #666666',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}
        onContextMenu={handleMenuContextMenu}
      >
        {isEditingThisArea && onCancelEditPoints ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
              onCancelEditPoints();
              onClose();
            }}
          >
            <CancelIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
            <Typography variant="body2">Cancelar edição</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => {
                if (menu.area) {
                  onInfo(menu.area);
                  onClose();
                }
              }}
            >
              <InfoIcon sx={{ mr: 1, fontSize: 18 }} color="info" />
              <Typography variant="body2">Informações</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => {
                if (menu.area) {
                  onEdit(menu.area);
                  onClose();
                }
              }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 18 }} color="primary" />
              <Typography variant="body2">Editar</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => {
                if (menu.area) {
                  onEditPoints(menu.area);
                  onClose();
                }
              }}
            >
              <EditIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
              <Typography variant="body2">Editar pontos</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => {
                if (menu.area) {
                  onDelete(menu.area);
                  onClose();
                }
              }}
            >
              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} color="error" />
              <Typography variant="body2" color="error">Deletar</Typography>
            </Box>
          </>
        )}
      </Paper>
    </>
  );
};

// Wrapper para o Google Maps com tipos
interface GoogleMapProps {
  center: Coordinates;
  zoom: number;
  caixas: FtthCaixa[];
  armarios: FtthArmario[];
  postes: FtthPosteForMap[];
  cables: Cable[];
  currentCable: Cable | null;
  rulerPoints: Coordinates[];
  areas: FtthAreaMapaForMapLocal[];
  currentAreaPoints: Coordinates[];
  isAddingCaixa: boolean;
  isAddingArmario: boolean;
  isAddingPoste: boolean;
  isDrawingCable: boolean;
  isMeasuring: boolean;
  isPickingLocation: boolean;
  isAddingArea: boolean;
  autoFitApplied: boolean;
  onMapClick: (lat: number, lng: number) => void;
  onAddCablePointFromItem: (
    lat: number,
    lng: number,
    item: { tipo: 'caixa' | 'armario' | 'poste'; id: number }
  ) => void;
  onCaixaClick: (caixa: FtthCaixa) => void;
  onEditCaixa: (caixa: FtthCaixa) => void;
  onMoveCaixa: (caixa: FtthCaixa) => void;
  onDeleteCaixa: (caixa: FtthCaixa) => void;
  onDiagramCaixa: (caixa: FtthCaixa) => void;
  onViewCaixa: (caixaId: number) => void;
  onCancelCaixaMove: () => void;
  onCaixaMove: (caixaId: number, newPosition: Coordinates) => void;
  onCaixaDrag?: (caixaId: number, pontosLinked: number[], newPosition: Coordinates) => void;
  caixaIdBeingMoved: number | null;
  onArmarioMove: (armarioId: number, newPosition: Coordinates) => void;
  onArmarioDrag?: (armarioId: number, pontosLinked: number[], newPosition: Coordinates) => void;
  armarioIdBeingMoved: number | null;
  onMapLoad: (map: google.maps.Map) => void;
  onCablePointUpdate: (cableId: string, pointIndex: number, newPosition: Coordinates) => void;
  onCurrentCableUpdate: (updatedCable: Cable) => void;
  onCablePointInsert: (cableId: string, afterIndex: number, newPosition: Coordinates) => void;
  onCurrentCablePointInsert: (afterIndex: number, newPosition: Coordinates) => void;
  onCablePointDelete: (cableId: string, pointIndex: number) => void;
  onAutoFitApplied: () => void;
  onCaixaContextMenu: (caixa: FtthCaixa, x: number, y: number) => void;
  onArmarioContextMenu: (armario: FtthArmario, x: number, y: number) => void;
  onPosteContextMenu: (poste: FtthPosteForMap, x: number, y: number) => void;
  onPosteMove: (posteId: number, newPosition: Coordinates) => void;
  posteIdBeingMoved: number | null;
  onCableContextMenu: (cable: Cable, x: number, y: number) => void;
  onAreaContextMenu: (area: FtthAreaMapaForMapLocal, x: number, y: number) => void;
  editingAreaId: number | null;
  onAreaPathChange: (areaId: number, coords: Array<{ lat: number; lng: number }>) => void;
  onAreaVertexSelect?: (index: number) => void;
  selectedAreaVertexIndex?: number | null;
  onCurrentAreaPointUpdate?: (index: number, newPosition: Coordinates) => void;
  selectedCableId: string | null;
  onCableSelect: (cable: Cable) => void;
  onEditCable: (cable: Cable) => void;
  onEditCablePoints: (cable: Cable) => void;
  onDeleteCable: (cable: Cable) => void;
  onCancelCableEdit: () => void;
  isEditingCable: boolean;
  editingCable: Cable | null;
  editingCaboOriginal: any;
  blockMapDrag: boolean;
  editingAreaPoints: boolean;
  apiKey: string;
  onCablePointNearCaixaArmario?: (params: { cableId: string; pontoId: number; caboId: number; caixaId?: number; armarioId?: number }) => void;
  mapToast?: { success: (msg: string, opts?: object) => void; error: (msg: string, opts?: object) => void; info: (msg: string, opts?: object) => void; warning: (msg: string, opts?: object) => void };
  trackUserLocation: boolean;
  onUserLocationError?: (message: string) => void;
}

const USER_LOCATION_MAX_ACCURACY_RADIUS_M = 60;
const USER_LOCATION_HIDE_ACCURACY_CIRCLE_ABOVE_M = 200;

function getUserLocationAccuracyRadius(accuracyMeters: number): number | null {
  if (!Number.isFinite(accuracyMeters) || accuracyMeters > USER_LOCATION_HIDE_ACCURACY_CIRCLE_ABOVE_M) {
    return null;
  }
  return Math.min(Math.max(accuracyMeters, 8), USER_LOCATION_MAX_ACCURACY_RADIUS_M);
}

const GoogleMapComponent: React.FC<GoogleMapProps> = ({
  center,
  zoom,
  caixas,
  armarios,
  postes,
  cables,
  currentCable,
  rulerPoints,
  areas,
  currentAreaPoints,
  isAddingCaixa,
  isAddingArmario,
  isAddingPoste,
  isDrawingCable,
  isMeasuring,
  isPickingLocation,
  isAddingArea,
  autoFitApplied,
  onMapClick,
  onAddCablePointFromItem,
  onCaixaClick,
  onEditCaixa,
  onMoveCaixa,
  onDeleteCaixa,
  onDiagramCaixa,
  onViewCaixa,
  onCancelCaixaMove,
  onCaixaMove,
  onCaixaDrag,
  caixaIdBeingMoved,
  onArmarioMove,
  onArmarioDrag,
  armarioIdBeingMoved,
  onMapLoad,
  onCablePointUpdate,
  onCurrentCableUpdate,
  onCablePointInsert,
  onCurrentCablePointInsert,
  onCablePointDelete,
  onAutoFitApplied,
  onCaixaContextMenu,
  onArmarioContextMenu,
  onPosteContextMenu,
  onPosteMove,
  posteIdBeingMoved,
  onCableContextMenu,
  onAreaContextMenu,
  editingAreaId,
  onAreaPathChange,
  onAreaVertexSelect,
  selectedAreaVertexIndex,
  onCurrentAreaPointUpdate,
  selectedCableId,
  onCableSelect,
  onEditCable,
  onEditCablePoints,
  onDeleteCable,
  onCancelCableEdit,
  isEditingCable,
  editingCable,
  editingCaboOriginal,
  blockMapDrag,
  editingAreaPoints,
  apiKey,
  onCablePointNearCaixaArmario,
  mapToast,
  trackUserLocation,
  onUserLocationError
}) => {
  const isDrawingCableRef = useRef(isDrawingCable);
  const isEditingCableRef = useRef(isEditingCable);
  useEffect(() => { isDrawingCableRef.current = isDrawingCable; }, [isDrawingCable]);
  useEffect(() => { isEditingCableRef.current = isEditingCable; }, [isEditingCable]);
  const onAddCablePointFromItemRef = useRef(onAddCablePointFromItem);
  useEffect(() => { onAddCablePointFromItemRef.current = onAddCablePointFromItem; }, [onAddCablePointFromItem]);

  // Log para verificar se os props estão sendo atualizados
  useEffect(() => {
  }, [isAddingCaixa, isAddingArmario, isAddingPoste, isDrawingCable, isMeasuring, isPickingLocation]);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const armarioMarkersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const posteMarkersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const polylinesRef = useRef<Map<string, google.maps.Polyline>>(new Map());
  const polygonsRef = useRef<Map<string, google.maps.Polygon>>(new Map());
  const areaVertexMarkersRef = useRef<Map<number, google.maps.Marker[]>>(new Map());
  const currentAreaVertexMarkersRef = useRef<google.maps.Marker[]>([]);
  const cableMarkersRef = useRef<Map<string, google.maps.Marker[]>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const cableInfoActionsRef = useRef({
    onEdit: onEditCable,
    onEditPoints: onEditCablePoints,
    onDelete: onDeleteCable,
  });
  cableInfoActionsRef.current = {
    onEdit: onEditCable,
    onEditPoints: onEditCablePoints,
    onDelete: onDeleteCable,
  };
  const caixaInfoActionsRef = useRef({
    onEdit: onEditCaixa,
    onMove: onMoveCaixa,
    onDelete: onDeleteCaixa,
    onDiagram: onDiagramCaixa,
    onView: onViewCaixa,
    onCancelMove: onCancelCaixaMove,
  });
  caixaInfoActionsRef.current = {
    onEdit: onEditCaixa,
    onMove: onMoveCaixa,
    onDelete: onDeleteCaixa,
    onDiagram: onDiagramCaixa,
    onView: onViewCaixa,
    onCancelMove: onCancelCaixaMove,
  };
  const dragCircleRef = useRef<google.maps.Circle | null>(null);
  const userLocationMarkerRef = useRef<google.maps.Marker | null>(null);
  const userLocationAccuracyRef = useRef<google.maps.Circle | null>(null);
  const userLocationWatchIdRef = useRef<number | null>(null);

  const clearUserLocationOverlay = useCallback(() => {
    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setMap(null);
      userLocationMarkerRef.current = null;
    }
    if (userLocationAccuracyRef.current) {
      userLocationAccuracyRef.current.setMap(null);
      userLocationAccuracyRef.current = null;
    }
    if (userLocationWatchIdRef.current !== null) {
      navigator.geolocation.clearWatch(userLocationWatchIdRef.current);
      userLocationWatchIdRef.current = null;
    }
  }, []);

  const updateUserLocationOverlay = useCallback((position: { lat: number; lng: number }, accuracy: number) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;
    const accuracyRadius = getUserLocationAccuracyRadius(accuracy);

    if (accuracyRadius === null) {
      if (userLocationAccuracyRef.current) {
        userLocationAccuracyRef.current.setMap(null);
        userLocationAccuracyRef.current = null;
      }
    } else if (!userLocationAccuracyRef.current) {
      userLocationAccuracyRef.current = new window.google.maps.Circle({
        map,
        center: position,
        radius: accuracyRadius,
        strokeColor: '#4285F4',
        strokeOpacity: 0.4,
        strokeWeight: 1,
        fillColor: '#4285F4',
        fillOpacity: 0.15,
        clickable: false,
        zIndex: window.google.maps.Marker.MAX_ZINDEX + 1,
      });
    } else {
      userLocationAccuracyRef.current.setMap(map);
      userLocationAccuracyRef.current.setCenter(position);
      userLocationAccuracyRef.current.setRadius(accuracyRadius);
    }

    if (!userLocationMarkerRef.current) {
      userLocationMarkerRef.current = new window.google.maps.Marker({
        map,
        position,
        clickable: false,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        zIndex: window.google.maps.Marker.MAX_ZINDEX + 2,
      });
    } else {
      userLocationMarkerRef.current.setPosition(position);
    }
  }, []);

  // Raio do círculo durante drag (5 metros do centro até a borda)
  const DRAG_CIRCLE_RADIUS_M = 5;

  const showDragCircle = useCallback((center: { lat: number; lng: number }) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    if (dragCircleRef.current) dragCircleRef.current.setMap(null);
    dragCircleRef.current = new window.google.maps.Circle({
      map: mapInstanceRef.current,
      center: { lat: center.lat, lng: center.lng },
      radius: DRAG_CIRCLE_RADIUS_M,
      strokeColor: '#2196F3',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#2196F3',
      fillOpacity: 0.15,
      zIndex: 1000
    });
  }, []);

  const updateDragCircle = useCallback((center: { lat: number; lng: number }) => {
    if (dragCircleRef.current) {
      dragCircleRef.current.setCenter(center);
    }
  }, []);

  const hideDragCircle = useCallback(() => {
    if (dragCircleRef.current) {
      dragCircleRef.current.setMap(null);
      dragCircleRef.current = null;
    }
  }, []);

  // Ref para o listener de clique
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Inicializar o mapa
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: 'satellite' as any, // Modo satélite por padrão
      mapTypeControl: false,
      streetViewControl: true,
      fullscreenControl: false,
      zoomControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_LEFT,
      },
    });

    mapInstanceRef.current = map;
    onMapLoad(map);

    // InfoWindow para Caixas
    infoWindowRef.current = new window.google.maps.InfoWindow({
      maxWidth: getMapInfoWindowMaxWidth(),
    });

    return () => {
      // Cleanup
      hideDragCircle();
      clearUserLocationOverlay();
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current.clear();
      armarioMarkersRef.current.forEach(marker => marker.setMap(null));
      armarioMarkersRef.current.clear();
      posteMarkersRef.current.forEach(marker => marker.setMap(null));
      posteMarkersRef.current.clear();
      polylinesRef.current.forEach(polyline => polyline.setMap(null));
      polylinesRef.current.clear();
      polygonsRef.current.forEach(polygon => polygon.setMap(null));
      polygonsRef.current.clear();
      areaVertexMarkersRef.current.forEach(markers => markers.forEach(m => m.setMap(null)));
      areaVertexMarkersRef.current.clear();
      currentAreaVertexMarkersRef.current.forEach(m => m.setMap(null));
      currentAreaVertexMarkersRef.current = [];

      // Remover listener de clique
      if (clickListenerRef.current) {
        clickListenerRef.current.remove();
      }
    };
  }, []);

  // Atualizar listener de clique quando os modos mudarem
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remover listener anterior
    if (clickListenerRef.current) {
      clickListenerRef.current.remove();
    }

    // Criar novo listener com estados atualizados
    clickListenerRef.current = mapInstanceRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        // Durante edição de pontos do cabo, clique no mapa não faz nada
        if (isEditingCable) {
          return;
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        onMapClick(e.latLng.lat(), e.latLng.lng());
      }
    });
  }, [isAddingCaixa, isAddingArmario, isAddingPoste, isDrawingCable, isMeasuring, isPickingLocation, isAddingArea, isEditingCable, onMapClick]);

  // Atualizar cursor baseado no modo
  useEffect(() => {
    if (mapInstanceRef.current) {
      const map = mapInstanceRef.current;
      if (isAddingCaixa || isAddingArmario || isAddingPoste || isDrawingCable || isMeasuring || isPickingLocation || isAddingArea) {
        map.setOptions({ draggableCursor: 'crosshair' });
      } else {
        map.setOptions({ draggableCursor: 'grab' });
      }
    }
  }, [isAddingCaixa, isAddingArmario, isAddingPoste, isDrawingCable, isMeasuring, isPickingLocation]);

  // Controlar drag do mapa baseado no estado blockMapDrag
  // editingAreaPoints: usar apenas draggable:false para permitir arrastar vértices do polígono
  // (gestureHandling:'none' bloquearia a interação com overlays)
  useEffect(() => {
    if (mapInstanceRef.current) {
      const map = mapInstanceRef.current;

      if (blockMapDrag) {
        if (editingAreaPoints) {
          map.setOptions({ draggable: false, gestureHandling: 'greedy' });
          // greedy + draggable:false: mapa não arrasta, mas overlays (polígono) recebem eventos
        } else {
          map.setOptions({
            draggable: false,
            gestureHandling: 'none'
          });
        }
      } else {
        map.setOptions({
          draggable: true,
          gestureHandling: 'greedy'
        });
      }
    }
  }, [blockMapDrag, editingAreaPoints]);

  // Atualizar marcadores de Caixas
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Remover marcadores antigos
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current.clear();

    // Coletar coordenadas válidas para calcular bounds
    const validCoordinates: Coordinates[] = [];

    // Adicionar novos marcadores
    caixas.forEach(caixa => {
      const coordinates = validateCoordinates(caixa.latitude, caixa.longitude);

      if (coordinates) {
        validCoordinates.push(coordinates);

        // Definir ícone baseado no tipo da caixa. Ao mover: anchor na base + efeito pulando.
        const isMoving = caixaIdBeingMoved === caixa.id;
        const isReservaMarker = caixa.tipo === 'reserva';
        const isCeoMarker = caixa.tipo === 'ceo';
        const isCtoMarker = caixa.tipo === 'cto';
        const ceoMapAnchor = isCeoMarker
          ? getCeoMapIconAnchor(CEO_MAP_DISPLAY_WIDTH, CEO_MAP_DISPLAY_HEIGHT)
          : null;
        const pinWidth = isCeoMarker
          ? CEO_MAP_DISPLAY_WIDTH
          : isCtoMarker
          ? CTO_PIN_DISPLAY_WIDTH
          : isReservaMarker
          ? MAP_PIN_DISPLAY_WIDTH
          : 40;
        const pinHeight = isCeoMarker
          ? CEO_MAP_DISPLAY_HEIGHT
          : isCtoMarker
          ? CTO_PIN_DISPLAY_HEIGHT
          : isReservaMarker
          ? MAP_PIN_DISPLAY_HEIGHT
          : 40;
        const anchorX = isCeoMarker
          ? ceoMapAnchor!.x
          : isCtoMarker
          ? CTO_PIN_ANCHOR_X
          : isReservaMarker
          ? MAP_PIN_ANCHOR_X
          : 20;
        const anchorY = isCeoMarker
          ? ceoMapAnchor!.y
          : isCtoMarker
          ? CTO_PIN_ANCHOR_Y
          : isReservaMarker
          ? MAP_PIN_ANCHOR_Y
          : (isMoving ? 40 : 20);
        const isAguardandoInstalacao = caixa.status === 'aguardando_instalacao';
        const markerColor =
          isAguardandoInstalacao
            ? CAIXA_COLORS.aguardando_instalacao
            : caixa.color && /^#[0-9A-Fa-f]{3,8}$/.test(caixa.color)
            ? caixa.color
            : '#FF0000';

        let iconUrl = isCeoMarker
          ? getCeoIconDataUrl(markerColor)
          : isReservaMarker
          ? getReservaIconDataUrl(markerColor || '#0091da')
          : getCtoIconDataUrl(markerColor);

        // Portas disponíveis (mesma fórmula de FtthCaixaDetailsPage: capacidade - em uso - desabilitadas)
        // Normalizar disabled_ports como na página de detalhes (pode vir como string JSON na listagem)
        let disabledPortsList: Array<{ port: number }> = [];
        if (Array.isArray(caixa.disabled_ports)) {
          disabledPortsList = caixa.disabled_ports;
        } else if (typeof caixa.disabled_ports === 'string') {
          try {
            const parsed = JSON.parse(caixa.disabled_ports);
            disabledPortsList = Array.isArray(parsed) ? parsed : [];
          } catch {
            disabledPortsList = [];
          }
        }
        const totalPorts = caixa.capacidade ?? 0;
        const disabledCount = disabledPortsList.length;
        const occupiedPorts = caixa.portas_em_uso ?? 0;
        const availablePorts = Math.max(0, totalPorts - disabledCount - occupiedPorts);

        const markerLabelText = getCaixaMarkerLabelText(caixa, isEditingCable ? editingCable : null);
        const showCaixaNameLabel = markerLabelText != null;
        let markerIconHeight = pinHeight;
        let markerIconWidth = pinWidth;
        let markerAnchorX = anchorX;

        if (isCtoMarker) {
          iconUrl = addPortTextToPinSvg(iconUrl, String(availablePorts), MAP_PIN_PORT_TEXT_COLOR);
        }

        if (showCaixaNameLabel) {
          const named = appendNameBelowIconSvg(iconUrl, markerLabelText!, pinWidth);
          iconUrl = named.url;
          if (named.viewBoxHeightBefore > 0) {
            markerIconHeight += getExtraDisplayHeightForNameLabel(
              pinHeight,
              named.viewBoxHeightBefore,
              named.namePadding,
            );
            if (named.displayWidthScale > 1) {
              markerIconWidth = pinWidth * named.displayWidthScale;
              markerAnchorX = anchorX * named.displayWidthScale;
            }
          }
        }

        if (isAguardandoInstalacao) {
          iconUrl = addAguardandoInstalacaoBorderToSvg(iconUrl, CAIXA_COLORS.aguardando_instalacao);
        }

        if (isMoving) iconUrl = wrapSvgWithBounceAnimation(iconUrl);
        const markerIcon = {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(markerIconWidth, markerIconHeight),
          anchor: new window.google.maps.Point(markerAnchorX, anchorY),
        };

        const marker = new window.google.maps.Marker({
          position: coordinates,
          map,
          title: caixa.name,
          draggable: caixaIdBeingMoved === caixa.id,
          crossOnDrag: caixaIdBeingMoved === caixa.id,
          icon: markerIcon,
        });

        // Clique no marcador
        marker.addListener('click', () => {
          if (isDrawingCableRef.current || isEditingCableRef.current) {
            (onAddCablePointFromItemRef.current || onAddCablePointFromItem)(coordinates.lat, coordinates.lng, { tipo: 'caixa', id: caixa.id });
            return;
          }
          onCaixaClick(caixa);

          if (infoWindowRef.current) {
            applyMapInfoWindowResponsiveOptions(infoWindowRef.current, 'wide');
            infoWindowRef.current.setContent(buildCaixaInfoWindowContent(caixa, coordinates, {
              isMoving: caixaIdBeingMoved === caixa.id,
            }));
            infoWindowRef.current.open(map, marker);
            google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
              normalizeMapInfoWindowLayout(getMapInfoWindowMaxWidth('wide'));
              if (infoWindowRef.current) {
                bindCaixaInfoWindowActions(caixa, infoWindowRef.current, caixaInfoActionsRef.current);
              }
            });
          }
        });

        bindMapOverlayContextMenu(marker, (mouseX, mouseY) => {
          onCaixaContextMenu(caixa, mouseX, mouseY);
        }, {
          beforeOpen: () => {
            map.setOptions({
              draggable: false,
              gestureHandling: 'none',
            });
          },
        });

        // Listeners para arrastar Caixa
        marker.addListener('dragstart', () => {
          const pos = marker.getPosition();
          if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setOptions({ draggable: false });
          }
        });

        marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
            if (onCaixaDrag) {
              const newPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
              const pts = Array.isArray(caixa.pontos_id) ? caixa.pontos_id : [];
              onCaixaDrag(caixa.id, pts, newPos);
            }
          }
        });

        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          hideDragCircle();
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setOptions({ draggable: true });
          }

          if (event.latLng) {
            const newPosition = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            };
            mapToast?.success(`Caixa ${caixa.name} foi atualizado com sucesso!`);
            // Atualizar coordenadas no backend
            onCaixaMove(caixa.id, newPosition);
          }
        });

        markersRef.current.set(caixa.id, marker);
      }
    });

    // Auto-ajustar zoom para mostrar todas as Caixas e cabos (apenas na primeira carga)
    if (validCoordinates.length > 0 && !autoFitApplied && onAutoFitApplied) {
      const allMapPoints = [...validCoordinates];

      // Incluir pontos dos armários nos bounds
      armarios.forEach(armario => {
        const coord = validateCoordinates(armario.latitude, armario.longitude);
        if (coord) allMapPoints.push(coord);
      });

      // Incluir pontos dos postes nos bounds
      postes.forEach(poste => {
        const coord = validateCoordinates(poste.latitude, poste.longitude);
        if (coord) allMapPoints.push(coord);
      });

      // Incluir pontos dos cabos existentes nos bounds
      cables.forEach(cable => {
        cable.points.forEach(point => {
          allMapPoints.push(point);
        });
      });

      if (allMapPoints.length === 1) {
        // Se há apenas um ponto, centralizar nele com zoom fixo
        map.setCenter(allMapPoints[0]);
        map.setZoom(16);
      } else {
        // Se há múltiplos pontos, usar fitBounds para mostrar todos
        const bounds = new window.google.maps.LatLngBounds();

        allMapPoints.forEach(coord => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });

        // Aplicar bounds com padding para melhor visualização
        map.fitBounds(bounds, {
          top: 50,    // Padding superior
          right: 50,  // Padding direito
          bottom: 100, // Padding inferior (maior para toolbar)
          left: 50
        });

        // Aguardar o fitBounds ser aplicado, então verificar zoom
        setTimeout(() => {
          const currentZoom = map.getZoom();
          if (currentZoom && currentZoom > 18) {
            // Se zoom ficou muito próximo, limitar a 18
            map.setZoom(18);
          } else if (currentZoom && currentZoom < 5) {
            // Se zoom ficou muito distante, limitar a 5
            map.setZoom(5);
          }
        }, 500);
      }

      // Marcar que o auto-fit foi aplicado
      onAutoFitApplied();
    }
  }, [caixas, armarios, postes, cables, autoFitApplied, caixaIdBeingMoved, isEditingCable, editingCable, onCaixaMove, onCaixaDrag, showDragCircle, updateDragCircle, hideDragCircle, mapToast]);

  // Atualizar marcadores de Armários
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    armarioMarkersRef.current.forEach(marker => marker.setMap(null));
    armarioMarkersRef.current.clear();

    armarios.forEach(armario => {
      const coordinates = validateCoordinates(armario.latitude, armario.longitude);
      if (!coordinates) return;

      const isMovingArmario = armarioIdBeingMoved === armario.id;
      let armarioIconUrl = getArmarioIconDataUrl(armario.color, armario.icon);
      if (isMovingArmario) armarioIconUrl = wrapSvgWithBounceAnimation(armarioIconUrl);
      const markerIcon = {
        url: armarioIconUrl,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, isMovingArmario ? 40 : 20),
      };

      const marker = new window.google.maps.Marker({
        position: coordinates,
        map,
        title: armario.name,
        draggable: armarioIdBeingMoved === armario.id,
        icon: markerIcon,
      });

      marker.addListener('click', () => {
        if (isDrawingCableRef.current || isEditingCableRef.current) {
          (onAddCablePointFromItemRef.current || onAddCablePointFromItem)(coordinates.lat, coordinates.lng, { tipo: 'armario', id: armario.id });
          return;
        }
        const content = `
          <div style="padding: 4px 4px 4px 0; min-width: 0; margin: 0;">
            <h3 style="margin: 0 0 6px 0; color: #9C27B0;">${armario.name}</h3>
            ${armario.address ? `<p style="margin: 4px 0;"><strong>Endereço:</strong> ${armario.address}</p>` : ''}
            <p style="margin: 4px 0;"><strong>Status:</strong> 
              <span style="color: ${CAIXA_COLORS[armario.status]}; font-weight: bold;">
                ${armario.status.replace('_', ' ').toUpperCase()}
              </span>
            </p>
            <p style="margin: 4px 0; font-size: 12px; color: #666;">
              ID: ${armario.id} | Coordenadas: ${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}
            </p>
          </div>
        `;
        if (infoWindowRef.current) {
          applyMapInfoWindowResponsiveOptions(infoWindowRef.current);
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(map, marker);
          google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
            normalizeMapInfoWindowLayout();
          });
        }
      });

      bindMapOverlayContextMenu(marker, (mouseX, mouseY) => {
        onArmarioContextMenu(armario, mouseX, mouseY);
      });

      marker.addListener('dragstart', () => {
        const pos = marker.getPosition();
        if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setOptions({ draggable: false });
        }
      });

      marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
          if (onArmarioDrag) {
            const newPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
            const pts = Array.isArray(armario.pontos_id) ? armario.pontos_id : [];
            onArmarioDrag(armario.id, pts, newPos);
          }
        }
      });

      marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
        hideDragCircle();
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setOptions({ draggable: true });
        }
        if (event.latLng) {
          const newPosition = { lat: event.latLng.lat(), lng: event.latLng.lng() };
          onArmarioMove(armario.id, newPosition);
        }
      });

      armarioMarkersRef.current.set(armario.id, marker);
    });
  }, [armarios, armarioIdBeingMoved, onArmarioContextMenu, onArmarioMove, onArmarioDrag, showDragCircle, updateDragCircle, hideDragCircle]);

  // Atualizar marcadores de Postes
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    posteMarkersRef.current.forEach(marker => marker.setMap(null));
    posteMarkersRef.current.clear();

    postes.forEach(poste => {
      const coordinates = validateCoordinates(poste.latitude, poste.longitude);
      if (!coordinates) return;

      const isMovingPoste = posteIdBeingMoved === poste.id;
      let posteIconUrl = getPosteIconDataUrl(poste.icon ?? 'sem_trafo', poste.color ?? '#666666');
      if (isMovingPoste) posteIconUrl = wrapSvgWithBounceAnimation(posteIconUrl);
      const markerIcon = {
        url: posteIconUrl,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, isMovingPoste ? 32 : 16),
      };

      const marker = new window.google.maps.Marker({
        position: coordinates,
        map,
        title: poste.identificacao,
        draggable: posteIdBeingMoved === poste.id,
        icon: markerIcon,
      });

      marker.addListener('click', () => {
        if (isDrawingCableRef.current || isEditingCableRef.current) {
          (onAddCablePointFromItemRef.current || onAddCablePointFromItem)(coordinates.lat, coordinates.lng, { tipo: 'poste', id: poste.id });
          return;
        }
        if (infoWindowRef.current) {
          const content = `
            <div style="padding: 4px 4px 4px 0; min-width: 0; margin: 0;">
              <h3 style="margin: 0 0 6px 0; color: #1976d2;">${poste.identificacao}</h3>
              <p style="margin: 0; font-size: 12px; color: #666;">
                ID: ${poste.id} | ${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}
              </p>
            </div>
          `;
          applyMapInfoWindowResponsiveOptions(infoWindowRef.current);
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(map, marker);
          google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
            normalizeMapInfoWindowLayout();
          });
        }
      });

      bindMapOverlayContextMenu(marker, (mouseX, mouseY) => {
        onPosteContextMenu(poste, mouseX, mouseY);
      }, {
        beforeOpen: () => {
          map.setOptions({ draggable: false, gestureHandling: 'none' });
        },
      });

      marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setOptions({ draggable: true });
        }
        if (event.latLng) {
          onPosteMove(poste.id, {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          });
        }
      });

      posteMarkersRef.current.set(poste.id, marker);
    });
  }, [postes, posteIdBeingMoved, onPosteContextMenu, onPosteMove]);

  // Atualizar polylines dos cabos
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Remover polylines antigas
    polylinesRef.current.forEach(polyline => polyline.setMap(null));
    polylinesRef.current.clear();

    // Remover marcadores antigos dos pontos dos cabos
    cableMarkersRef.current.forEach(markers => {
      markers.forEach(marker => marker.setMap(null));
    });
    cableMarkersRef.current.clear();

    // Adicionar cabos finalizados
    cables.forEach(cable => {
      if (cable.points.length > 1) {
        const isSelected = selectedCableId === cable.id && !cable.editable;

        if (isSelected) {
          const outlinePolyline = new window.google.maps.Polyline(
            buildCableSelectionOutlineOptions({
              points: cable.points,
              tipo: cable.tipo,
              zIndex: 99,
            }),
          );
          outlinePolyline.setMap(map);
          polylinesRef.current.set(`${cable.id}-selection`, outlinePolyline);
        }

        // Criar polyline
        const polyline = new window.google.maps.Polyline(
          buildCablePolylineOptions({
            points: cable.points,
            color: cable.color,
            tipo: cable.tipo,
            strokeWeight: cable.type === 'fiber' ? 3 : 2,
            clickable: true,
            zIndex: isSelected ? 101 : 100,
          }),
        );

        polyline.setMap(map);
        polylinesRef.current.set(cable.id, polyline);

        // Listener para clique esquerdo no cabo
        polyline.addListener('click', (event: google.maps.PolyMouseEvent) => {
          if (event.latLng && !isDrawingCable && !isAddingCaixa) {
            // Prevenir propagação para o mapa
            if (event.domEvent) {
              event.domEvent.stopPropagation();
            }

            if (cable.editable) {
              // Se já está editável, inserir ponto
              // Calcular em qual segmento foi clicado
              const clickPosition = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
              };

              // Encontrar o segmento mais próximo
              let closestSegmentIndex = 0;
              let minDistance = Infinity;

              for (let i = 0; i < cable.points.length - 1; i++) {
                const point1 = cable.points[i];
                const point2 = cable.points[i + 1];

                // Calcular distância do clique ao segmento
                const distance = distanceToLineSegment(clickPosition, point1, point2);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestSegmentIndex = i;
                }
              }
              onCablePointInsert(cable.id, closestSegmentIndex, clickPosition);
            } else {
              onCableSelect(cable);

              if (infoWindowRef.current && event.latLng) {
                applyMapInfoWindowResponsiveOptions(infoWindowRef.current, 'wide');
                infoWindowRef.current.setContent(buildCableInfoWindowContent(cable));
                infoWindowRef.current.setPosition(event.latLng);
                infoWindowRef.current.open(map);
                google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                  normalizeMapInfoWindowLayout(getMapInfoWindowMaxWidth('wide'));
                  if (infoWindowRef.current) {
                    bindCableInfoWindowActions(cable, infoWindowRef.current, cableInfoActionsRef.current);
                  }
                });
              }
            }
          }
        });

        bindMapOverlayContextMenu(polyline, (mouseX, mouseY) => {
          onCableContextMenu(cable, mouseX, mouseY);
        }, {
          canOpen: (event) => !!(event.latLng && !isDrawingCable && !isAddingCaixa),
        });

        // Criar marcadores para os pontos (apenas para cabos editáveis)
        if (cable.editable) {
          const markers: google.maps.Marker[] = [];

          cable.points.forEach((point, index) => {
            const isFirstPoint = index === 0;
            const isLastPoint = index === cable.points.length - 1;
            const isExtremity = isFirstPoint || isLastPoint;
            const linkedCaixa = getLinkedCaixaForPoint(point, caixas);
            const vinculoTitle = linkedCaixa
              ? linkedCaixa.tipo === 'reserva'
                ? `Reserva: ${formatReservaMapLabel(linkedCaixa)}`
                : `${linkedCaixa.tipo.toUpperCase()}: ${linkedCaixa.name}`
              : '';

            const marker = new window.google.maps.Marker({
              position: point,
              map,
              draggable: true,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: isExtremity ? 8 : 6, // Extremidades maiores
                fillColor: isExtremity ? '#FF5722' : cable.color, // Extremidades laranja
                fillOpacity: 0.9,
                strokeColor: '#fff',
                strokeWeight: isExtremity ? 3 : 2, // Extremidades com borda mais grossa
              },
              title: (point.id != null ? `ID: ${point.id} | ` : '')
                + (vinculoTitle ? `${vinculoTitle} | ` : '')
                + (isExtremity
                ? `${isFirstPoint ? 'Início' : 'Fim'} do cabo ${cable.id} - Arraste para mover ou clique duplo para estender`
                : `Ponto ${index + 1} do cabo ${cable.id} - Arraste para mover | Clique + Delete para remover`),
              zIndex: isExtremity ? 1001 : 1000, // Extremidades com z-index maior
            });

            // Listener para selecionar ponto (clique) - habilita deleção via Delete
            marker.addListener('click', () => {
              // Destacar visualmente o ponto selecionado
              markers.forEach((m, i) => {
                const isExt = i === 0 || i === cable.points.length - 1;
                m.setIcon({
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: isExt ? 8 : 6,
                  fillColor: isExt ? '#FF5722' : cable.color,
                  fillOpacity: 0.9,
                  strokeColor: '#fff',
                  strokeWeight: isExt ? 3 : 2,
                });
              });
              // Destacar o ponto selecionado
              marker.setIcon({
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: '#F44336',
                fillOpacity: 1,
                strokeColor: '#FFEB3B',
                strokeWeight: 3,
              });

              // Listener temporário de teclado para Delete
              const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  document.removeEventListener('keydown', handleKeyDown);
                  onCablePointDelete(cable.id, index);
                } else if (e.key === 'Escape') {
                  // Desselecionar
                  document.removeEventListener('keydown', handleKeyDown);
                  const isExt = index === 0 || index === cable.points.length - 1;
                  marker.setIcon({
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: isExt ? 8 : 6,
                    fillColor: isExt ? '#FF5722' : cable.color,
                    fillOpacity: 0.9,
                    strokeColor: '#fff',
                    strokeWeight: isExt ? 3 : 2,
                  });
                }
              };
              document.addEventListener('keydown', handleKeyDown);
            });

            // Listeners para arrastar pontos
            marker.addListener('dragstart', () => {
              const pos = marker.getPosition();
              if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setOptions({ draggable: false });
              }
            });

            marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
              if (event.latLng) updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
              // Atualizar polyline em tempo real para o cabo acompanhar o ponto
              if (event.latLng) {
                const newLatLng = event.latLng;
                const polyline = polylinesRef.current.get(cable.id);
                if (polyline) {
                  const path = polyline.getPath();
                  path.setAt(index, newLatLng);
                }
                // Se ponto tem caixa_armario_id, mover todos os pontos vinculados à mesma caixa/armário
                const pontoOriginal = cable.points[index];
                const caixaArmarioId = pontoOriginal && typeof (pontoOriginal as Coordinates).caixa_armario_id === 'number' ? (pontoOriginal as Coordinates).caixa_armario_id : null;
                const caixaArmarioTipo = pontoOriginal && ((pontoOriginal as Coordinates).caixa_armario_tipo === 'caixa' || (pontoOriginal as Coordinates).caixa_armario_tipo === 'armario') ? (pontoOriginal as Coordinates).caixa_armario_tipo : null;
                if (caixaArmarioId != null && caixaArmarioTipo) {
                  const entidade = caixaArmarioTipo === 'caixa'
                    ? caixas.find(c => c.id === caixaArmarioId)
                    : armarios.find(a => a.id === caixaArmarioId);
                  const ptsIds = entidade ? parsePontosId((entidade as any).pontos_id) : [];
                  ptsIds.forEach((pontoId: number) => {
                    if (pontoId === (pontoOriginal as Coordinates).id) return; // já atualizado
                    for (const c of cables) {
                      const idx = c.points.findIndex(p => p.id === pontoId);
                      if (idx >= 0) {
                        const pl = polylinesRef.current.get(c.id);
                        if (pl) pl.getPath().setAt(idx, newLatLng);
                        const markers = cableMarkersRef.current.get(c.id);
                        if (markers && markers[idx]) markers[idx].setPosition(newLatLng);
                        break;
                      }
                    }
                  });
                }
              }
            });

            marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
              hideDragCircle();
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setOptions({ draggable: true });
              }

              if (event.latLng) {
                const newPosition = {
                  lat: event.latLng.lat(),
                  lng: event.latLng.lng()
                };
                onCablePointUpdate(cable.id, index, newPosition);

                // Verificar proximidade (5 metros) com caixas/armários e solicitar vínculo
                const pontoOriginal = cable.points[index];
                const pontoId = pontoOriginal?.id;
                const PROXIMIDADE_KM = 0.005; // 5 metros

                let proximoMaisPerto: { tipo: 'caixa' | 'armario'; id: number; distancia: number } | null = null;

                caixas.forEach(caixa => {
                  const coord = validateCoordinates(caixa.latitude, caixa.longitude);
                  if (coord) {
                    const dist = calculateDistance(newPosition, coord);
                    if (dist <= PROXIMIDADE_KM && (!proximoMaisPerto || dist < proximoMaisPerto.distancia)) {
                      proximoMaisPerto = { tipo: 'caixa', id: caixa.id, distancia: dist };
                    }
                  }
                });

                armarios.forEach(arm => {
                  const coord = validateCoordinates(arm.latitude, arm.longitude);
                  if (coord) {
                    const dist = calculateDistance(newPosition, coord);
                    if (dist <= PROXIMIDADE_KM && (!proximoMaisPerto || dist < proximoMaisPerto.distancia)) {
                      proximoMaisPerto = { tipo: 'armario', id: arm.id, distancia: dist };
                    }
                  }
                });

                if (proximoMaisPerto && typeof pontoId === 'number' && onCablePointNearCaixaArmario) {
                  const caboIdNum = parseInt(cable.id.replace('cabo-', ''), 10);
                  if (!isNaN(caboIdNum)) {
                    const prox: { tipo: 'caixa' | 'armario'; id: number } = proximoMaisPerto;
                    const params: { cableId: string; pontoId: number; caboId: number; caixaId?: number; armarioId?: number } = {
                      cableId: cable.id,
                      pontoId,
                      caboId: caboIdNum
                    };
                    if (prox.tipo === 'caixa') params.caixaId = prox.id; else params.armarioId = prox.id;
                    onCablePointNearCaixaArmario(params);
                  }
                }
              }
            });

            // Listener para clique duplo nas extremidades (estender cabo)
            if (isExtremity) {
              marker.addListener('dblclick', (event: google.maps.MapMouseEvent) => {
                if (event.latLng) {
                  // Ativar modo de extensão do cabo
                  const extensionMode = isFirstPoint ? 'start' : 'end';
                  // Mudar cursor do mapa para crosshair
                  if (mapInstanceRef.current) {
                    mapInstanceRef.current.setOptions({ draggableCursor: 'crosshair' });
                  }

                  // Criar listener temporário para capturar próximo clique
                  const extensionListener = mapInstanceRef.current?.addListener('click', (clickEvent: google.maps.MapMouseEvent) => {
                    if (clickEvent.latLng) {
                      const newPoint = {
                        lat: clickEvent.latLng.lat(),
                        lng: clickEvent.latLng.lng()
                      };

                      if (isFirstPoint) {
                        // Adicionar no início do cabo
                        onCablePointInsert(cable.id, -1, newPoint); // -1 indica inserir no início
                      } else {
                        // Adicionar no final do cabo
                        onCablePointInsert(cable.id, cable.points.length - 1, newPoint);
                      }

                      // Remover listener temporário
                      if (extensionListener) {
                        extensionListener.remove();
                      }

                      // Restaurar cursor normal
                      if (mapInstanceRef.current) {
                        mapInstanceRef.current.setOptions({ draggableCursor: 'grab' });
                      }
                    }
                  });
                }
              });
            }
            markers.push(marker);
          });
          cableMarkersRef.current.set(cable.id, markers);
        }
      }
    });

    // Adicionar cabo em desenho (temporário)
    if (currentCable && currentCable.points.length > 0) {
      const polyline = new window.google.maps.Polyline({
        path: currentCable.points,
        geodesic: true,
        strokeColor: currentCable.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        editable: false, // Desabilitar edição nativa do Google Maps
        draggable: false, // Desabilitar drag nativo do Google Maps
        clickable: true, // Permitir cliques na linha
        zIndex: 101, // Z-index um pouco maior que cabos finalizados
      });

      polyline.setMap(map);
      polylinesRef.current.set('current', polyline);

      // Listener para inserir pontos clicando na linha do cabo em desenho
      polyline.addListener('click', (event: google.maps.PolyMouseEvent) => {
        if (event.latLng && !isAddingCaixa && currentCable) {
          // Calcular em qual segmento foi clicado
          const clickPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          };

          // Encontrar o segmento mais próximo
          let closestSegmentIndex = 0;
          let minDistance = Infinity;

          for (let i = 0; i < currentCable.points.length - 1; i++) {
            const point1 = currentCable.points[i];
            const point2 = currentCable.points[i + 1];

            // Calcular distância do clique ao segmento
            const distance = distanceToLineSegment(clickPosition, point1, point2);

            if (distance < minDistance) {
              minDistance = distance;
              closestSegmentIndex = i;
            }
          }
          onCurrentCablePointInsert(closestSegmentIndex, clickPosition);
        }
      });

      // Marcadores para pontos do cabo em desenho
      const markers: google.maps.Marker[] = [];
      currentCable.points.forEach((point, index) => {
        const isFirstPoint = index === 0;
        const isLastPoint = index === currentCable.points.length - 1;
        const isExtremity = isFirstPoint || isLastPoint;

        const marker = new window.google.maps.Marker({
          position: point,
          map,
          draggable: true, // Cabo em desenho também é editável
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isExtremity ? 7 : 5, // Extremidades maiores
            fillColor: isExtremity ? '#FF5722' : '#2196F3', // Extremidades laranja
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: isExtremity ? 3 : 2, // Extremidades com borda mais grossa
          },
          title: isExtremity
            ? `${isFirstPoint ? 'Início' : 'Fim'} do cabo em desenho - Arraste para mover ou clique duplo para estender`
            : `Ponto ${index + 1} (cabo em desenho) - Arraste para mover`,
          zIndex: isExtremity ? 1002 : 1001, // Extremidades com z-index maior
        });

        // Listeners para arrastar pontos do cabo em desenho
        marker.addListener('dragstart', () => {
          const pos = marker.getPosition();
          if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setOptions({ draggable: false });
          }
        });

        marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        });

        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          // Reabilitar drag do mapa
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setOptions({ draggable: false });
          }

          hideDragCircle();
          if (event.latLng) {
            const newPosition = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            };
            // Atualizar o cabo atual via callback
            const updatedCable = {
              ...currentCable,
              points: currentCable.points.map((point, i) =>
                i === index ? newPosition : point
              )
            };
            onCurrentCableUpdate(updatedCable);
          }
        });

        // Listener para clique duplo nas extremidades do cabo em desenho (estender cabo)
        if (isExtremity) {
          marker.addListener('dblclick', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              // Ativar modo de extensão do cabo em desenho
              const extensionMode = isFirstPoint ? 'start' : 'end';
              // Mudar cursor do mapa para crosshair
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setOptions({ draggableCursor: 'crosshair' });
              }

              // Criar listener temporário para capturar próximo clique
              const extensionListener = mapInstanceRef.current?.addListener('click', (clickEvent: google.maps.MapMouseEvent) => {
                if (clickEvent.latLng) {
                  const newPoint = {
                    lat: clickEvent.latLng.lat(),
                    lng: clickEvent.latLng.lng()
                  };

                  if (isFirstPoint) {
                    // Adicionar no início do cabo em desenho
                    onCurrentCablePointInsert(-1, newPoint); // -1 indica inserir no início
                  } else {
                    // Adicionar no final do cabo em desenho
                    onCurrentCablePointInsert(currentCable.points.length - 1, newPoint);
                  }

                  // Remover listener temporário
                  if (extensionListener) {
                    extensionListener.remove();
                  }

                  // Restaurar cursor normal
                  if (mapInstanceRef.current) {
                    mapInstanceRef.current.setOptions({ draggableCursor: 'grab' });
                  }
                }
              });
            }
          });
        }

        markers.push(marker);
      });

      cableMarkersRef.current.set('current', markers);
    }

    // Adicionar régua de medição
    if (rulerPoints && rulerPoints.length > 0) {
      const rulerPolyline = new window.google.maps.Polyline({
        path: rulerPoints,
        geodesic: true,
        strokeColor: '#FF5722', // Cor laranja-vermelha para régua
        strokeOpacity: 1.0,
        strokeWeight: 3,
        clickable: false,
        zIndex: 200, // Z-index alto para ficar acima dos cabos
      });

      rulerPolyline.setMap(map);
      polylinesRef.current.set('ruler', rulerPolyline);

      // Marcadores para pontos da régua
      const rulerMarkers: google.maps.Marker[] = [];
      rulerPoints.forEach((point, index) => {
        const marker = new window.google.maps.Marker({
          position: point,
          map,
          draggable: false,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#FF5722',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          title: `Ponto ${index + 1} da régua`,
          zIndex: 2000, // Z-index muito alto
        });

        rulerMarkers.push(marker);
      });

      cableMarkersRef.current.set('ruler', rulerMarkers);
    }

    // Áreas (polígonos salvos)
    polygonsRef.current.forEach(p => p.setMap(null));
    polygonsRef.current.clear();
    if (areas && areas.length > 0) {
      areas.forEach(area => {
        let coords = area.coordenadas;
        if (typeof coords === 'string') {
          try {
            coords = JSON.parse(coords);
          } catch {
            coords = [];
          }
        }
        const path = Array.isArray(coords)
          ? coords
            .map((p: any) => {
              const lat = typeof p?.lat === 'number' ? p.lat : (p?.latitude != null ? Number(p.latitude) : null);
              const lng = typeof p?.lng === 'number' ? p.lng : (p?.longitude != null ? Number(p.longitude) : null);
              return lat != null && lng != null && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
            })
            .filter((p: any): p is { lat: number; lng: number } => p != null)
          : [];
        if (path.length >= 3) {
          const isEditing = editingAreaId === area.id;
          const poly = new window.google.maps.Polygon({
            paths: path,
            strokeColor: area.cor || '#2196F3',
            strokeOpacity: 0.9,
            strokeWeight: isEditing ? 3 : 2,
            fillColor: area.cor || '#2196F3',
            fillOpacity: isEditing ? 0.3 : 0.25,
            clickable: true,
            editable: false,
            map
          });
          bindMapOverlayContextMenu(poly, (mouseX, mouseY) => {
            onAreaContextMenu?.(area, mouseX, mouseY);
          }, {
            canOpen: () => !!onAreaContextMenu,
          });
          poly.setMap(map);
          polygonsRef.current.set(`area-${area.id}`, poly);

          if (isEditing && onAreaPathChange) {
            // Clique no polígono: adicionar novo vértice na aresta mais próxima
            poly.addListener('click', (e: google.maps.PolyMouseEvent) => {
              if (!e.latLng) return;
              const clickPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
              const polyPath = poly.getPath();
              const n = polyPath.getLength();
              if (n < 2) return;
              let minDist = Infinity;
              let insertIndex = 1;
              for (let i = 0; i < n; i++) {
                const a = polyPath.getAt(i);
                const b = polyPath.getAt((i + 1) % n);
                const lineStart = { lat: a.lat(), lng: a.lng() };
                const lineEnd = { lat: b.lat(), lng: b.lng() };
                const dist = distanceToLineSegment(clickPoint, lineStart, lineEnd);
                if (dist < minDist) {
                  minDist = dist;
                  insertIndex = i + 1;
                }
              }
              polyPath.insertAt(insertIndex, e.latLng);
              const newCoords: Array<{ lat: number; lng: number }> = [];
              for (let i = 0; i < polyPath.getLength(); i++) {
                const p = polyPath.getAt(i);
                newCoords.push({ lat: p.lat(), lng: p.lng() });
              }
              onAreaPathChange(area.id, newCoords);
            });
            areaVertexMarkersRef.current.get(area.id)?.forEach(m => m.setMap(null));
            areaVertexMarkersRef.current.delete(area.id);
            const vertexMarkers: google.maps.Marker[] = [];
            path.forEach((pt, index) => {
              const isSelected = selectedAreaVertexIndex === index;
              const marker = new window.google.maps.Marker({
                position: pt,
                map,
                draggable: true,
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: isSelected ? 10 : 8,
                  fillColor: isSelected ? '#FF5722' : (area.cor || '#2196F3'),
                  fillOpacity: 0.95,
                  strokeColor: '#fff',
                  strokeWeight: isSelected ? 3 : 2,
                },
                zIndex: 3000,
              });
              marker.addListener('click', () => {
                if (onAreaVertexSelect) onAreaVertexSelect(index);
              });
              marker.addListener('dragstart', () => {
                const pos = marker.getPosition();
                if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
                map.setOptions({ draggable: false });
              });
              marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
                if (event.latLng) {
                  updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
                  const polyPath = poly.getPath();
                  polyPath.setAt(index, event.latLng);
                  // Não chamar onAreaPathChange durante o drag - causa re-render e destroi os marcadores
                }
              });
              marker.addListener('dragend', () => {
                hideDragCircle();
                map.setOptions({ draggable: true });
                const polyPath = poly.getPath();
                const newCoords: Array<{ lat: number; lng: number }> = [];
                for (let i = 0; i < polyPath.getLength(); i++) {
                  const p = polyPath.getAt(i);
                  newCoords.push({ lat: p.lat(), lng: p.lng() });
                }
                onAreaPathChange(area.id, newCoords);
              });
              vertexMarkers.push(marker);
            });
            areaVertexMarkersRef.current.set(area.id, vertexMarkers);
          } else {
            areaVertexMarkersRef.current.get(area.id)?.forEach(m => m.setMap(null));
            areaVertexMarkersRef.current.delete(area.id);
          }
        }
      });
    }
    // Polígono em desenho (currentAreaPoints) - com marcadores arrastáveis quando isAddingArea
    currentAreaVertexMarkersRef.current.forEach(m => m.setMap(null));
    currentAreaVertexMarkersRef.current = [];
    if (currentAreaPoints && currentAreaPoints.length >= 1) {
      const path = currentAreaPoints.length >= 3
        ? [...currentAreaPoints, currentAreaPoints[0]]
        : currentAreaPoints.length >= 2
          ? currentAreaPoints
          : currentAreaPoints;
      const poly = currentAreaPoints.length >= 2 ? new window.google.maps.Polygon({
        paths: path,
        strokeColor: '#2196F3',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#2196F3',
        fillOpacity: 0.15,
        clickable: false,
        map
      }) : null;
      if (poly) {
        poly.setMap(map);
        polygonsRef.current.set('current-area', poly);
      } else {
        polygonsRef.current.get('current-area')?.setMap(null);
        polygonsRef.current.delete('current-area');
      }

      // Marcadores arrastáveis em cada vértice quando está adicionando área
      if (currentAreaPoints.length >= 1 && onCurrentAreaPointUpdate) {
        const vertexMarkers: google.maps.Marker[] = [];
        currentAreaPoints.forEach((pt, index) => {
          const marker = new window.google.maps.Marker({
            position: pt,
            map,
            draggable: true,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#2196F3',
              fillOpacity: 0.95,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
            zIndex: 3000,
          });
          marker.addListener('dragstart', () => {
            const pos = marker.getPosition();
            if (pos) showDragCircle({ lat: pos.lat(), lng: pos.lng() });
            map.setOptions({ draggable: false });
          });
          marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              updateDragCircle({ lat: event.latLng.lat(), lng: event.latLng.lng() });
              const polygon = poly;
              if (polygon) {
                const polyPath = polygon.getPath();
                polyPath.setAt(index, event.latLng);
                if (path.length >= 3 && index === 0) polyPath.setAt(path.length - 1, event.latLng);
              }
            }
          });
          marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
            hideDragCircle();
            map.setOptions({ draggable: true });
            if (event.latLng) {
              onCurrentAreaPointUpdate(index, { lat: event.latLng.lat(), lng: event.latLng.lng() });
            }
          });
          vertexMarkers.push(marker);
        });
        currentAreaVertexMarkersRef.current = vertexMarkers;
      }
    }
  }, [cables, currentCable, rulerPoints, areas, currentAreaPoints, selectedCableId, isEditingCable, editingCable, onCablePointUpdate, caixas, armarios, onCablePointNearCaixaArmario, showDragCircle, updateDragCircle, hideDragCircle, onAreaContextMenu, editingAreaId, onAreaPathChange, onAreaVertexSelect, selectedAreaVertexIndex, onCurrentAreaPointUpdate]);

  // Rastrear localização do usuário em tempo real
  useEffect(() => {
    if (!trackUserLocation) {
      clearUserLocationOverlay();
      return;
    }

    if (!navigator.geolocation) {
      onUserLocationError?.('Geolocalização não suportada pelo navegador');
      return;
    }

    let isFirstUpdate = true;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const accuracy = position.coords.accuracy || 50;

        updateUserLocationOverlay(userLocation, accuracy);

        if (isFirstUpdate && mapInstanceRef.current) {
          mapInstanceRef.current.panTo(userLocation);
          mapInstanceRef.current.setZoom(16);
          isFirstUpdate = false;
        }
      },
      (error) => {
        console.error('Erro ao obter localização:', error);
        onUserLocationError?.('Erro ao obter sua localização');
        clearUserLocationOverlay();
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      }
    );

    userLocationWatchIdRef.current = watchId;

    return () => {
      navigator.geolocation.clearWatch(watchId);
      userLocationWatchIdRef.current = null;
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
        userLocationMarkerRef.current = null;
      }
      if (userLocationAccuracyRef.current) {
        userLocationAccuracyRef.current.setMap(null);
        userLocationAccuracyRef.current = null;
      }
    };
  }, [trackUserLocation, clearUserLocationOverlay, updateUserLocationOverlay, onUserLocationError]);

  // Atualizar centro do mapa
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter(center);
    }
  }, [center]);

  // Atualizar zoom do mapa
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, [zoom]);

  return (
    <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />
  );
};

export interface MapNetworkViabilidadeState {
  lat?: string;
  lng?: string;
  caixaIds?: number[];
  openModal?: boolean;
}

interface MapNetworkTabProps {
  initialViabilidade?: MapNetworkViabilidadeState;
}

const MapNetworkTab: React.FC<MapNetworkTabProps> = ({ initialViabilidade }) => {
  const theme = useTheme();
  const isCompactMapToolbar = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  // Estados principais
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [armarios, setArmarios] = useState<FtthArmario[]>([]);
  const [cables, setCables] = useState<Cable[]>([]);
  const [postes, setPostes] = useState<FtthPosteForMap[]>([]);
  const [areas, setAreas] = useState<FtthAreaMapaForMap[]>([]);
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [currentCable, setCurrentCable] = useState<Cable | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados de UI
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCaixaForm, setShowCaixaForm] = useState(false);
  const [showArmarioForm, setShowArmarioForm] = useState(false);
  const [showPosteForm, setShowPosteForm] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<Coordinates | null>(null);
  const [showCabosForm, setShowCabosForm] = useState(false);
  const [selectedCablePoints, setSelectedCablePoints] = useState<Coordinates[]>([]);
  const [selectedCableId, setSelectedCableId] = useState<string | null>(null);
  const [showCaixaDiagram, setShowCaixaDiagram] = useState(false);
  const [selectedCaixaForDiagram, setSelectedCaixaForDiagram] = useState<FtthCaixa | null>(null);
  const [showArmarioDiagram, setShowArmarioDiagram] = useState(false);
  const [selectedArmarioForDiagram, setSelectedArmarioForDiagram] = useState<FtthArmario | null>(null);
  const [caixaContextMenu, setCaixaContextMenu] = useState<CaixaContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    caixa: null
  });
  const [caixaIdEmMovimento, setCaixaIdEmMovimento] = useState<number | null>(null);
  const [armarioContextMenu, setArmarioContextMenu] = useState<ArmarioContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    armario: null
  });
  const [armarioIdEmMovimento, setArmarioIdEmMovimento] = useState<number | null>(null);
  const [cableContextMenu, setCableContextMenu] = useState<CableContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    cable: null
  });
  const [posteContextMenu, setPosteContextMenu] = useState<PosteContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    poste: null
  });
  const [areaContextMenu, setAreaContextMenu] = useState<AreaContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    area: null
  });
  const [editingCaixa, setEditingCaixa] = useState<FtthCaixa | null>(null);
  const [editingArmario, setEditingArmario] = useState<FtthArmario | null>(null);
  const [editingPoste, setEditingPoste] = useState<FtthPoste | null>(null);
  const [copyFromPoste, setCopyFromPoste] = useState<FtthPoste | null>(null);
  const [posteIdEmMovimento, setPosteIdEmMovimento] = useState<number | null>(null);
  const [editingCable, setEditingCable] = useState<Cable | null>(null);
  const [isEditingCable, setIsEditingCable] = useState(false);
  const [editingCaboOriginal, setEditingCaboOriginal] = useState<Cabo | null>(null);
  const [editingCaboForForm, setEditingCaboForForm] = useState<Cabo | null>(null);
  const [blockNativeContextMenu, setBlockNativeContextMenu] = useState(false);
  const [blockMapDrag, setBlockMapDrag] = useState(false);

  // Estados de modos
  const [isAddingCaixa, setIsAddingCaixa] = useState(false);
  const [addingCaixaTipo, setAddingCaixaTipo] = useState<'cto' | 'ceo' | 'reserva'>('cto');
  const addingCaixaTipoRef = useRef<'cto' | 'ceo' | 'reserva'>('cto');
  const [isAddingArmario, setIsAddingArmario] = useState(false);
  const [isAddingPoste, setIsAddingPoste] = useState(false);
  const [isDrawingCable, setIsDrawingCable] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [rulerPoints, setRulerPoints] = useState<Coordinates[]>([]);
  const [viabilidadeModalOpen, setViabilidadeModalOpen] = useState(false);
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [pickedLocation, setPickedLocation] = useState<{ lat: string; lng: string } | null>(null);
  const [viabilidadeFilterActive, setViabilidadeFilterActive] = useState(false);
  const visibleCaixaIdsBeforeViabilidadeRef = useRef<Set<number> | null>(null);
  const initialViabilidadeAppliedRef = useRef(false);
  const [isAddingArea, setIsAddingArea] = useState(false);
  const [areaPoints, setAreaPoints] = useState<Coordinates[]>([]);
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [editingArea, setEditingArea] = useState<FtthAreaMapaForMapLocal | null>(null);
  const [editingAreaPoints, setEditingAreaPoints] = useState(false);

  // Toast que renderiza dentro do container do mapa (visível em fullscreen)
  const mapToast = useMemo(() => ({
    success: (msg: string, opts?: object) => toast.success(msg, { ...opts, containerId: 'map-fullscreen' }),
    error: (msg: string, opts?: object) => toast.error(msg, { ...opts, containerId: 'map-fullscreen' }),
    info: (msg: string, opts?: object) => toast.info(msg, { ...opts, containerId: 'map-fullscreen' }),
    warning: (msg: string, opts?: object) => toast.warning(msg, { ...opts, containerId: 'map-fullscreen' }),
  }), []);
  const [editingAreaCoords, setEditingAreaCoords] = useState<Array<{ lat: number; lng: number }> | null>(null);
  const [selectedAreaVertexIndex, setSelectedAreaVertexIndex] = useState<number | null>(null);

  // Estados do mapa
  const [mapCenter, setMapCenter] = useState<Coordinates>({ lat: -14.235004, lng: -51.92528 });
  const [mapZoom, setMapZoom] = useState(5);
  const [trackUserLocation, setTrackUserLocation] = useState(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [autoFitApplied, setAutoFitApplied] = useState(false); // Flag para controlar auto-fit
  const [mapType, setMapType] = useState<string>('satellite'); // Tipo do mapa

  // API Key do Google Maps
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>('');

  // TreeView: visibilidade no mapa (somente os marcados são exibidos)
  const [visibleCaixaIds, setVisibleCaixaIds] = useState<Set<number>>(new Set());
  const [visibleArmarioIds, setVisibleArmarioIds] = useState<Set<number>>(new Set());
  const [visibleCableIds, setVisibleCableIds] = useState<Set<string>>(new Set());
  const [visiblePosteIds, setVisiblePosteIds] = useState<Set<number>>(new Set());
  const [visibleAreaIds, setVisibleAreaIds] = useState<Set<number>>(new Set());

  // Custom TreeView States
  const [expandedEscritorios, setExpandedEscritorios] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [toolsMenuAnchorEl, setToolsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const toolsMenuOpen = Boolean(toolsMenuAnchorEl);
  const [mapActionsMenuAnchorEl, setMapActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const mapActionsMenuOpen = Boolean(mapActionsMenuAnchorEl);
  const [showOnlyAguardandoInstalacao, setShowOnlyAguardandoInstalacao] = useState(false);
  const [hideAguardandoInstalacao, setHideAguardandoInstalacao] = useState(false);
  const [showOnlyDrop, setShowOnlyDrop] = useState(false);

  // Visibilidade inicia desmarcada; usuário marca no treeview o que quer exibir no mapa
  const hasMapComponentsSelected =
    visibleCaixaIds.size > 0 ||
    visibleArmarioIds.size > 0 ||
    visibleCableIds.size > 0 ||
    visiblePosteIds.size > 0 ||
    visibleAreaIds.size > 0;

  useEffect(() => {
    if (!hasMapComponentsSelected) {
      setShowOnlyAguardandoInstalacao(false);
      setHideAguardandoInstalacao(false);
      setShowOnlyDrop(false);
    }
  }, [hasMapComponentsSelected]);

  // Caixas, cabos e postes filtrados para exibir no mapa (somente os marcados; cabo em edição sempre visível)
  const visibleCaixas = useMemo(() => caixas.filter(c =>
    visibleCaixaIds.has(c.id) &&
    (!showOnlyAguardandoInstalacao || c.status === 'aguardando_instalacao') &&
    (!hideAguardandoInstalacao || c.status !== 'aguardando_instalacao')
  ), [caixas, visibleCaixaIds, showOnlyAguardandoInstalacao, hideAguardandoInstalacao]);

  const visibleArmarios = useMemo(() => armarios.filter(a =>
    visibleArmarioIds.has(a.id) &&
    (!showOnlyAguardandoInstalacao || a.status === 'aguardando_instalacao') &&
    (!hideAguardandoInstalacao || a.status !== 'aguardando_instalacao')
  ), [armarios, visibleArmarioIds, showOnlyAguardandoInstalacao, hideAguardandoInstalacao]);

  const visibleCables = useMemo(() => cables.filter(c =>
    (visibleCableIds.has(c.id) || editingCable?.id === c.id) &&
    !showOnlyAguardandoInstalacao &&
    (!showOnlyDrop || c.tipo === 'drop' || editingCable?.id === c.id)
  ), [cables, visibleCableIds, editingCable?.id, showOnlyAguardandoInstalacao, showOnlyDrop]);

  const visiblePostes = useMemo(() => postes.filter(p =>
    visiblePosteIds.has(p.id) && !showOnlyAguardandoInstalacao
  ), [postes, visiblePosteIds, showOnlyAguardandoInstalacao]);

  const visibleAreas = useMemo(() => areas.filter(a =>
    visibleAreaIds.has(a.id) && !showOnlyAguardandoInstalacao
  ), [areas, visibleAreaIds, showOnlyAguardandoInstalacao]);

  // Áreas para o mapa: quando em edição de pontos, usa editingAreaCoords para a área sendo editada
  const areasForMap = useMemo(() => {
    if (!editingArea || !editingAreaCoords || editingAreaCoords.length < 3) return visibleAreas;
    return visibleAreas.map(a =>
      a.id === editingArea.id ? { ...a, coordenadas: editingAreaCoords } : a
    );
  }, [visibleAreas, editingArea, editingAreaCoords]);

  const toggleCaixaVisibility = (id: number) => {
    setVisibleCaixaIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleCableVisibility = (id: string) => {
    setVisibleCableIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleArmarioVisibility = (id: number) => {
    setVisibleArmarioIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePosteVisibility = (id: number) => {
    setVisiblePosteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAreaVisibility = (id: number) => {
    setVisibleAreaIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const panToMapNode = (lat?: number | string | null, lng?: number | string | null) => {
    if (lat && lng && mapInstance) {
      mapInstance.panTo({ lat: Number(lat), lng: Number(lng) });
      mapInstance.setZoom(20);
    }
  };

  const markAllCeo = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'ceo' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.add(c.id));
    return next;
  });
  const unmarkAllCeo = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'ceo' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.delete(c.id));
    return next;
  });
  const markAllCto = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'cto' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.add(c.id));
    return next;
  });
  const unmarkAllCto = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'cto' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.delete(c.id));
    return next;
  });
  const markAllReserva = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'reserva' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.add(c.id));
    return next;
  });
  const unmarkAllReserva = (escritorioId?: number) => setVisibleCaixaIds(prev => {
    const next = new Set(prev);
    caixas.filter(c => c.tipo === 'reserva' && (!escritorioId || c.escritorio_id === escritorioId)).forEach(c => next.delete(c.id));
    return next;
  });
  const markAllArmarios = (escritorioId?: number) => setVisibleArmarioIds(prev => {
    const next = new Set(prev);
    armarios.filter(a => !escritorioId || a.escritorio_id === escritorioId).forEach(a => next.add(a.id));
    return next;
  });
  const unmarkAllArmarios = (escritorioId?: number) => setVisibleArmarioIds(prev => {
    const next = new Set(prev);
    armarios.filter(a => !escritorioId || a.escritorio_id === escritorioId).forEach(a => next.delete(a.id));
    return next;
  });
  const markAllCabos = (escritorioId?: number) => setVisibleCableIds(prev => {
    const next = new Set(prev);
    filterCablesByEscritorio(cables, escritorioId).forEach(c => next.add(c.id));
    return next;
  });
  const unmarkAllCabos = (escritorioId?: number) => setVisibleCableIds(prev => {
    const next = new Set(prev);
    filterCablesByEscritorio(cables, escritorioId).forEach(c => next.delete(c.id));
    return next;
  });
  const markAllPostes = (escritorioId?: number) => setVisiblePosteIds(prev => {
    const next = new Set(prev);
    postes.filter(p => !escritorioId || (p as any).escritorio_id === escritorioId || !(p as any).escritorio_id).forEach(p => next.add(p.id));
    return next;
  });
  const unmarkAllPostes = (escritorioId?: number) => setVisiblePosteIds(prev => {
    const next = new Set(prev);
    postes.filter(p => !escritorioId || (p as any).escritorio_id === escritorioId || !(p as any).escritorio_id).forEach(p => next.delete(p.id));
    return next;
  });
  const markAllAreas = (escritorioId?: number) => setVisibleAreaIds(prev => {
    const next = new Set(prev);
    areas.filter(a => !escritorioId || a.escritorio_id === escritorioId).forEach(a => next.add(a.id));
    return next;
  });
  const unmarkAllAreas = (escritorioId?: number) => setVisibleAreaIds(prev => {
    const next = new Set(prev);
    areas.filter(a => !escritorioId || a.escritorio_id === escritorioId).forEach(a => next.delete(a.id));
    return next;
  });

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const toggleEscritorio = (id: number) => {
    setExpandedEscritorios(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Debug: Monitorar mudanças no editingCaboForForm
  useEffect(() => {

  }, [editingCaboForForm, showCabosForm]);

  // Carregar API Key do Google Maps
  useEffect(() => {
    const loadGoogleMapsKey = async () => {
      try {
        const config = await configurationService.getConfigurationByKey('google_maps_api_key');
        if (config && config.valor) {
          setGoogleMapsApiKey(config.valor);
        } else {
          setError('Chave do Google Maps não configurada');
        }
      } catch (err) {
        console.error('Erro ao carregar chave do Google Maps:', err);
        setError('Erro ao carregar configuração do Google Maps');
      }
    };

    loadGoogleMapsKey();
  }, []);

  // Carregar Caixas
  const loadCaixas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ftthCaixaService.getCaixas({ limit: 1000 });

      // Backend pode retornar { data: { caixas, pagination } } ou { caixas, pagination }
      const list = (response as any)?.data?.caixas ?? (response as any)?.caixas;
      if (list && Array.isArray(list)) {
        setCaixas(list);
      } else if (Array.isArray(response)) {
        setCaixas(response);
      } else {
        console.warn('Formato de resposta inesperado:', response);
        setCaixas([]);
      }
    } catch (err: any) {
      console.error('Erro ao carregar Caixas:', err);
      setError('Erro ao carregar Caixas: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar Armários
  const loadArmarios = useCallback(async () => {
    try {
      const response = await ftthArmarioService.getArmarios({ limit: 1000 });
      const list = (response as any)?.data?.armarios ?? (response as any)?.armarios;
      if (list && Array.isArray(list)) {
        setArmarios(list);
      } else if (Array.isArray(response)) {
        setArmarios(response);
      } else {
        setArmarios([]);
      }
    } catch (err: any) {
      console.error('Erro ao carregar Armários:', err);
      setArmarios([]);
    }
  }, []);

  // Carregar Cabos
  // cacheBust: quando true, força nova requisição sem cache (ex: após mover caixa/armário)
  const loadCabos = useCallback(async (cacheBust = false) => {
    try {
      const cabosData = await caboService.getCabosForMap(cacheBust);

      // Converter cabos do backend (Cabo) para formato do mapa (Cable)
      const cablesForMap: Cable[] = cabosData.map(cabo => mapCaboToCable(cabo));

      setCables(prev => {
        const editingCableId = prev.find(c => c.editable)?.id ?? null;
        if (!editingCableId) return cablesForMap;
        return cablesForMap.map(c => ({
          ...c,
          editable: c.id === editingCableId,
        }));
      });

      setEditingCable(prev => {
        if (!prev) return prev;
        const refreshed = cablesForMap.find(c => c.id === prev.id);
        if (!refreshed) return prev;
        return { ...refreshed, editable: true };
      });

    } catch (err: any) {
      console.error('❌ Erro ao carregar cabos:', err);
      setCables([]); // Definir array vazio em caso de erro
    }
  }, []);

  // Carregar Postes (service já normaliza resposta para array)
  const loadPostes = useCallback(async () => {
    try {
      const list = await ftthPosteService.getPostesForMap();
      setPostes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Erro ao carregar postes:', err);
      setPostes([]);
    }
  }, []);

  // Carregar Áreas do Mapa
  const loadAreas = useCallback(async () => {
    try {
      const list = await ftthAreaMapaService.getAreasForMap();
      const arr = Array.isArray(list) ? list : [];
      setAreas(arr);
    } catch (err) {
      console.error('Erro ao carregar áreas:', err);
      setAreas([]);
    }
  }, []);

  // Carregar Escritorios
  const loadEscritorios = useCallback(async () => {
    try {
      const resp = await escritorioService.getForSelect({ ativo: 'true' });
      // API response might be nested or direct array
      const list = (resp as any)?.data ?? resp;
      if (Array.isArray(list)) {
        setEscritorios(list);
      }
    } catch (err) {
      console.error('Erro ao carregar escritórios', err);
    }
  }, []);

  // Carregar dados iniciais quando API key estiver disponível
  const loadMapData = useCallback(async () => {
    if (!googleMapsApiKey) return;

    setLoading(true);

    try {
      // Carregar Caixas, armários, cabos, postes, áreas e escritórios em paralelo para melhor performance
      await Promise.all([
        loadCaixas(),
        loadArmarios(),
        loadCabos(),
        loadPostes(),
        loadAreas(),
        loadEscritorios()
      ]);

    } catch (err) {
      console.error('❌ Erro ao carregar dados do mapa:', err);
    } finally {
      setLoading(false);
    }
  }, [googleMapsApiKey, loadCaixas, loadArmarios, loadCabos, loadPostes, loadAreas, loadEscritorios]);

  useEffect(() => {
    loadMapData();
  }, [loadMapData]);

  // Sistema de bloqueio seletivo do menu de contexto nativo
  useEffect(() => {
    const selectiveContextMenuHandler = (e: Event) => {
      if (blockNativeContextMenu) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    // Adicionar listener global quando bloqueio está ativo
    if (blockNativeContextMenu) {
      document.addEventListener('contextmenu', selectiveContextMenuHandler, true);
    }

    return () => {
      document.removeEventListener('contextmenu', selectiveContextMenuHandler, true);
    };
  }, [blockNativeContextMenu]);

  // Sistema de bloqueio de movimento do mapa durante menu de contexto (Caixa, Armário, etc.)
  // NÃO aplicar quando editingAreaPoints: o polígono precisa receber mousedown/mousemove para arrastar vértices
  useEffect(() => {
    const blockMapMovement = (e: Event) => {
      if (blockMapDrag && !editingAreaPoints) {
        // Bloquear eventos que podem causar movimento do mapa
        if (e.type === 'mousedown' || e.type === 'mousemove' || e.type === 'drag' || e.type === 'dragstart') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }
    };

    if (blockMapDrag && !editingAreaPoints) {
      // Adicionar listeners para eventos que podem mover o mapa (apenas quando menu de contexto está aberto)
      const eventTypes = ['mousedown', 'mousemove', 'drag', 'dragstart', 'touchstart', 'touchmove'];
      eventTypes.forEach(eventType => {
        document.addEventListener(eventType, blockMapMovement, true);
      });
    }

    return () => {
      const eventTypes = ['mousedown', 'mousemove', 'drag', 'dragstart', 'touchstart', 'touchmove'];
      eventTypes.forEach(eventType => {
        document.removeEventListener(eventType, blockMapMovement, true);
      });
    };
  }, [blockMapDrag, editingAreaPoints]);

  // CSS para SweetAlert em modo fullscreen
  useEffect(() => {
    if (isFullscreen) {
      // Injetar CSS para SweetAlert em fullscreen
      const style = document.createElement('style');
      style.id = 'swal2-fullscreen-styles';
      style.textContent = `
        .swal2-fullscreen-container {
          z-index: 99999 !important;
        }
        .swal2-fullscreen-popup {
          z-index: 99999 !important;
        }
        .swal2-backdrop-show {
          z-index: 99998 !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      // Remover CSS quando sair do fullscreen
      const existingStyle = document.getElementById('swal2-fullscreen-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    }

    return () => {
      // Cleanup na desmontagem do componente
      const existingStyle = document.getElementById('swal2-fullscreen-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isFullscreen]);

  const handleContextMenu = (e: React.MouseEvent) => {
    // Só bloquear se o sistema seletivo não estiver ativo
    if (!blockNativeContextMenu) {
      e.preventDefault();
    }
  };


  // Handlers para o mapa (removido useCallback para evitar problemas de closure)
  const handleMapClick = (lat: number, lng: number) => {

    // Clique fora da área em edição: salvar alterações automaticamente
    if (editingAreaPoints) {
      handleSaveEditAreaPoints();
      return;
    }

    if (isAddingCaixa) {
      setSelectedCoordinates({ lat, lng });
      setShowCaixaForm(true);
      setIsAddingCaixa(false);
    } else if (isAddingArmario) {
      setSelectedCoordinates({ lat, lng });
      setShowArmarioForm(true);
      setIsAddingArmario(false);
    } else if (isDrawingCable) {
      setCurrentCable(prev => {
        if (!prev) {
          // Iniciar novo cabo
          const newCable = {
            id: `cable-${Date.now()}`,
            points: [{ lat, lng }],
            type: 'drawing' as const,
            color: '#2196F3', // Azul para cabo em desenho
            editable: true
          };
          return newCable;
        } else {
          // Adicionar ponto ao cabo atual
          const updatedCable = {
            ...prev,
            points: [...prev.points, { lat, lng }]
          };
          return updatedCable;
        }
      });
    } else if (isMeasuring) {
      setRulerPoints(prev => {
        const newPoints = [...prev, { lat, lng }];
        if (newPoints.length > 1) {
          const totalDistance = calculateTotalDistance(newPoints);
        }
        return newPoints;
      });
    } else if (isPickingLocation) {
      setPickedLocation({
        lat: lat.toFixed(7),
        lng: lng.toFixed(7),
      });
      setIsPickingLocation(false);
      setViabilidadeModalOpen(true);
    } else if (isAddingPoste) {
      setSelectedCoordinates({ lat, lng });
      setShowPosteForm(true);
      setIsAddingPoste(false);
    } else if (isAddingArea) {
      setAreaPoints(prev => [...prev, { lat, lng }]);
    } else {
      setSelectedCableId(null);
    }
  };

  const handleCaixaClick = useCallback((_caixa: FtthCaixa) => {
    // Clique na CTO/CEO não abre mais o diagrama (comportamento removido)
  }, []);

  // Durante o drag da caixa: arrastar os pontos de cabos vinculados junto
  const handleCaixaDrag = useCallback((caixaId: number, pontosLinked: number[], newPosition: Coordinates) => {
    if (pontosLinked.length === 0) return;
    const idsSet = new Set(pontosLinked);
    setCables(prev => prev.map(c => ({
      ...c,
      points: c.points.map(pt => idsSet.has(pt.id as number) ? { ...pt, lat: newPosition.lat, lng: newPosition.lng } : pt)
    })));
  }, []);

  // Durante o drag do armário: arrastar os pontos de cabos vinculados junto
  const handleArmarioDrag = useCallback((armarioId: number, pontosLinked: number[], newPosition: Coordinates) => {
    if (pontosLinked.length === 0) return;
    const idsSet = new Set(pontosLinked);
    setCables(prev => prev.map(c => ({
      ...c,
      points: c.points.map(pt => idsSet.has(pt.id as number) ? { ...pt, lat: newPosition.lat, lng: newPosition.lng } : pt)
    })));
  }, []);

  // Handler para mover Caixa
  const handleCaixaMove = useCallback(async (caixaId: number, newPosition: Coordinates) => {
    try {
      const caixa = caixas.find(c => c.id === caixaId);
      if (!caixa) {
        setError("Caixa não encontrada para mover.");
        return;
      }

      const result = await Swal.fire({
        title: 'Confirmar Mudança de Posição',
        html: `
          <div style="text-align: left;">
            Tem certeza que deseja mover a caixa <strong>${caixa.name}</strong> para as novas coordenadas?<br/><br/>
            <strong>Coordenadas atuais:</strong><br/>
            Latitude: ${caixa.latitude ?? '(não definida)'}<br/>
            Longitude: ${caixa.longitude ?? '(não definida)'}<br/><br/>
            <strong>Nova localização:</strong><br/>
            Latitude: ${newPosition.lat.toFixed(6)}<br/>
            Longitude: ${newPosition.lng.toFixed(6)}
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, mover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#1976d2',
        cancelButtonColor: '#aaa',
        reverseButtons: true,
        focusCancel: true,
        target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
      });

      if (result.isConfirmed) {
        const updateData = {
          latitude: newPosition.lat,
          longitude: newPosition.lng
        };
        await ftthCaixaService.update(caixaId, updateData);
        setCaixas(prev => prev.map(c =>
          c.id === caixaId ? { ...c, latitude: newPosition.lat, longitude: newPosition.lng } : c
        ));
        await loadCabos(true); // Recarregar cabos (sem cache) para exibir pontos vinculados na nova posição
        mapToast.success('Caixa movida com sucesso');

        // Após confirmar mudança de posição: verificar proximidade (3m) com pontos de cabos
        const PROXIMIDADE_KM = 0.003;
        let pontoMaisProximo: { pontoId: number; caboId: number; distancia: number } | null = null;
        cables.forEach(cable => {
          const caboIdNum = parseInt(cable.id.replace('cabo-', ''), 10);
          if (isNaN(caboIdNum)) return;
          cable.points.forEach((point) => {
            if (typeof point.id !== 'number') return;
            const dist = calculateDistance(newPosition, point);
            if (dist <= PROXIMIDADE_KM && (!pontoMaisProximo || dist < pontoMaisProximo.distancia)) {
              pontoMaisProximo = { pontoId: point.id, caboId: caboIdNum, distancia: dist };
            }
          });
        });
        if (pontoMaisProximo) {
          const p = pontoMaisProximo as { pontoId: number; caboId: number };
          const linkResult = await Swal.fire({
            icon: 'question',
            title: 'Vincular à caixa',
            html: 'Deseja vincular esta caixa a este cabo?<br/><br/><small>Ponto ID: ' + p.pontoId + ' | Caixa ID: ' + caixaId + '</small>',
            showCancelButton: true,
            confirmButtonText: 'Sim, vincular',
            cancelButtonText: 'Não',
            target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
          });
          if (linkResult.isConfirmed) {
            try {
              await caboService.vincularPonto({ ponto_id: p.pontoId, cabo_id: p.caboId, caixa_id: caixaId });
              mapToast.success('Caixa vinculada ao cabo com sucesso');
              loadCaixas();
              await loadCabos(true);
            } catch (err: any) {
              mapToast.error(err.message || 'Erro ao vincular');
            }
          }
        }
      }
    } catch (error: any) {
      setError(`Erro ao atualizar posição da Caixa: ${error.message || 'Erro desconhecido'}`);
      loadCaixas();
    } finally {
      setCaixaIdEmMovimento(null);
    }
  }, [caixas, cables, loadCaixas, loadCabos]);

  // Handler para mover Armário
  const handleArmarioMove = useCallback(async (armarioId: number, newPosition: Coordinates) => {
    try {
      const armario = armarios.find(a => a.id === armarioId);
      if (!armario) return;

      const result = await Swal.fire({
        title: 'Confirmar Mudança de Posição',
        html: `
          <div style="text-align: left;">
            Tem certeza que deseja mover o armário <strong>${armario.name}</strong> para as novas coordenadas?<br/><br/>
            <strong>Nova localização:</strong><br/>
            Latitude: ${newPosition.lat.toFixed(6)}<br/>
            Longitude: ${newPosition.lng.toFixed(6)}
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, mover',
        cancelButtonText: 'Cancelar',
        target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
      });

      if (result.isConfirmed) {
        await ftthArmarioService.update(armarioId, { latitude: newPosition.lat, longitude: newPosition.lng });
        setArmarios(prev => prev.map(a => a.id === armarioId ? { ...a, latitude: newPosition.lat, longitude: newPosition.lng } : a));
        await loadCabos(true); // Recarregar cabos (sem cache) para exibir pontos vinculados na nova posição
        mapToast.success('Armário movido com sucesso');

        // Após confirmar mudança de posição: verificar proximidade (3m) com pontos de cabos
        const PROXIMIDADE_KM = 0.003;
        let pontoMaisProximo: { pontoId: number; caboId: number; distancia: number } | null = null;
        for (const cable of cables) {
          const caboIdNum = parseInt(cable.id.replace('cabo-', ''), 10);
          if (isNaN(caboIdNum)) continue;
          for (const point of cable.points) {
            if (typeof point.id !== 'number') continue;
            const dist = calculateDistance(newPosition, point);
            if (dist <= PROXIMIDADE_KM && (!pontoMaisProximo || dist < pontoMaisProximo.distancia)) {
              pontoMaisProximo = { pontoId: point.id, caboId: caboIdNum, distancia: dist };
            }
          }
        }
        if (pontoMaisProximo) {
          const p = pontoMaisProximo as { pontoId: number; caboId: number };
          const linkResult = await Swal.fire({
            icon: 'question',
            title: 'Vincular ao armário',
            html: 'Deseja vincular este armário a este cabo?<br/><br/><small>Ponto ID: ' + p.pontoId + ' | Armário ID: ' + armarioId + '</small>',
            showCancelButton: true,
            confirmButtonText: 'Sim, vincular',
            cancelButtonText: 'Não',
            target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
          });
          if (linkResult.isConfirmed) {
            try {
              await caboService.vincularPonto({ ponto_id: p.pontoId, cabo_id: p.caboId, armario_id: armarioId });
              mapToast.success('Armário vinculado ao cabo com sucesso');
              loadArmarios();
              await loadCabos(true);
            } catch (err: any) {
              mapToast.error(err.message || 'Erro ao vincular');
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao mover armário:', error);
      mapToast.error(error.message || 'Erro ao mover armário');
      loadArmarios();
    } finally {
      setArmarioIdEmMovimento(null);
    }
  }, [armarios, cables, loadArmarios, loadCabos]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  // Handler para atualizar pontos dos cabos (move pontos vinculados à caixa/armário junto)
  const handleCablePointUpdate = useCallback(async (cableId: string, pointIndex: number, newPosition: Coordinates) => {

    const cable = cables.find(c => c.id === cableId);
    const pontoOriginal = cable?.points[pointIndex];
    const pontoId = pontoOriginal?.id;
    const caixaArmarioId = pontoOriginal && typeof (pontoOriginal as Coordinates).caixa_armario_id === 'number' ? (pontoOriginal as Coordinates).caixa_armario_id : null;
    const caixaArmarioTipo = pontoOriginal && ((pontoOriginal as Coordinates).caixa_armario_tipo === 'caixa' || (pontoOriginal as Coordinates).caixa_armario_tipo === 'armario') ? (pontoOriginal as Coordinates).caixa_armario_tipo : null;

    // Lista de pontos a atualizar (ponto arrastado + vinculados à mesma caixa/armário)
    let pontosToUpdate: { caboId: string; caboIdNum: number; pontoId: number; pointIndex: number }[] = [];
    if (pontoId) {
      pontosToUpdate.push({
        caboId: cableId,
        caboIdNum: parseInt(cableId.replace('cabo-', ''), 10),
        pontoId,
        pointIndex
      });
      if (caixaArmarioId != null && caixaArmarioTipo) {
        const entidade = caixaArmarioTipo === 'caixa'
          ? caixas.find(c => c.id === caixaArmarioId)
          : armarios.find(a => a.id === caixaArmarioId);
        const ptsIds = entidade ? parsePontosId((entidade as any).pontos_id) : [];
        ptsIds.forEach((pid: number) => {
          if (pid === pontoId) return;
          for (const c of cables) {
            const idx = c.points.findIndex(p => p.id === pid);
            if (idx >= 0) {
              pontosToUpdate.push({
                caboId: c.id,
                caboIdNum: parseInt(c.id.replace('cabo-', ''), 10),
                pontoId: pid,
                pointIndex: idx
              });
              break;
            }
          }
        });
      }
    }

    // Atualizar state local imediatamente (feedback visual) para todos os pontos afetados
    setCables(prev => prev.map(c => {
      const updatesForThisCable = pontosToUpdate.filter(p => p.caboId === c.id);
      if (updatesForThisCable.length === 0) return c;
      const newPoints = [...c.points];
      updatesForThisCable.forEach(({ pointIndex: idx, pontoId: pid }) => {
        const orig = newPoints[idx];
        if (orig) newPoints[idx] = { ...newPosition, id: pid, proximo_ponto_id: orig.proximo_ponto_id, caixa_armario_id: (orig as Coordinates).caixa_armario_id, caixa_armario_tipo: (orig as Coordinates).caixa_armario_tipo };
      });
      const updatedCable = { ...c, points: newPoints };
      if (isEditingCable && editingCable?.id === c.id) setEditingCable(updatedCable);
      return updatedCable;
    }));

    // Salvar no BD: se vinculado a caixa/armário, atualizar a caixa/armário (backend atualiza todos os pontos)
    // Caso contrário, atualizar apenas o ponto individual
    try {
      if (caixaArmarioId != null && caixaArmarioTipo) {
        if (caixaArmarioTipo === 'caixa') {
          await ftthCaixaService.update(caixaArmarioId, { latitude: newPosition.lat, longitude: newPosition.lng });
          setCaixas(prev => prev.map(c => c.id === caixaArmarioId ? { ...c, latitude: newPosition.lat, longitude: newPosition.lng } : c));
        } else {
          await ftthArmarioService.update(caixaArmarioId, { latitude: newPosition.lat, longitude: newPosition.lng });
          setArmarios(prev => prev.map(a => a.id === caixaArmarioId ? { ...a, latitude: newPosition.lat, longitude: newPosition.lng } : a));
        }
      } else {
        for (const { caboIdNum, pontoId: pid } of pontosToUpdate) {
          await caboService.updatePonto(caboIdNum, pid, { lat: newPosition.lat, lng: newPosition.lng });
        }
      }
    } catch (error: any) {
      mapToast.error('Erro ao salvar posição do ponto');
    }
  }, [cables, caixas, armarios, isEditingCable, editingCable]);

  // Handler quando ponto do cabo é arrastado perto de caixa/armário - perguntar vínculo
  const handleCablePointNearCaixaArmario = useCallback(async (params: { cableId: string; pontoId: number; caboId: number; caixaId?: number; armarioId?: number }) => {
    const { cableId, pontoId, caboId, caixaId, armarioId } = params;
    const tipoLabel = caixaId ? 'caixa' : 'armário';
    const linkResult = await Swal.fire({
      icon: 'question',
      title: 'Vincular ao cabo',
      html: 'Deseja vincular esta ' + tipoLabel + ' a este cabo?<br/><br/><small>Ponto ID: ' + pontoId + ' | ' + (caixaId ? 'Caixa ID: ' + caixaId : 'Armário ID: ' + armarioId) + '</small>',
      showCancelButton: true,
      confirmButtonText: 'Sim, vincular',
      cancelButtonText: 'Não',
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
    });
    if (linkResult.isConfirmed) {
      try {
        await caboService.vincularPonto({ ponto_id: pontoId, cabo_id: caboId, ...(caixaId ? { caixa_id: caixaId } : { armario_id: armarioId }) });
        mapToast.success((caixaId ? 'Caixa' : 'Armário') + ' vinculado ao cabo com sucesso');

        const pontosAtualizados = await caboService.getPontos(caboId);
        const mappedPoints = mapCaboPontosToCoordinates(pontosAtualizados);

        setCables(prev => prev.map(c => {
          if (c.id !== cableId) return c;
          return { ...c, points: mappedPoints, editable: c.editable };
        }));

        if (isEditingCable && editingCable?.id === cableId) {
          setEditingCable(prev => (prev ? { ...prev, points: mappedPoints } : prev));
        }

        loadCaixas();
        loadArmarios();
      } catch (err: any) {
        mapToast.error(err.message || 'Erro ao vincular');
      }
    }
  }, [loadCaixas, loadArmarios, isEditingCable, editingCable]);

  // Handler para atualizar o cabo atual
  const handleCurrentCableUpdate = useCallback((updatedCable: Cable) => {
    setCurrentCable(updatedCable);
  }, []);

  // Handler para inserir ponto em cabo finalizado
  const handleCablePointInsert = useCallback(async (cableId: string, afterIndex: number, newPosition: Coordinates) => {

    const caboIdNum = parseInt(cableId.replace('cabo-', ''));
    const cable = cables.find(c => c.id === cableId);

    if (!cable) {
      return;
    }

    try {
      // Obter o ID do ponto anterior (depois_de)
      const pontoAnterior = cable.points[afterIndex];
      const depoisDeId = pontoAnterior?.id;


      // Chamar API para inserir o ponto (apenas 2 registros tocados no BD)
      const novoPonto = await caboService.addPonto(caboIdNum, newPosition.lat, newPosition.lng, depoisDeId || undefined);

      if (newPosition.caixa_armario_id && newPosition.caixa_armario_tipo && novoPonto?.id) {
        try {
          await caboService.vincularPonto({
            ponto_id: novoPonto.id,
            cabo_id: caboIdNum,
            ...(newPosition.caixa_armario_tipo === 'caixa' ? { caixa_id: newPosition.caixa_armario_id } : { armario_id: newPosition.caixa_armario_id })
          });
        } catch (e) {
          console.warn('Erro ao auto-vincular ponto inserido:', e);
        }
      }

      // Recarregar todos os pontos do cabo para obter IDs corretos
      const pontosAtualizados = await caboService.getPontos(caboIdNum);

      const updatedCable: Cable = {
        ...cable,
        points: mapCaboPontosToCoordinates(pontosAtualizados),
      };

      setCables(prev => prev.map(c => c.id === cableId ? updatedCable : c));

      if (isEditingCable && editingCable?.id === cableId) {
        setEditingCable(updatedCable);
      }

    } catch (error: any) {
      mapToast.error('Erro ao inserir ponto no cabo');
    }
  }, [cables, isEditingCable, editingCable]);

  // Handler para deletar ponto de cabo (chamado ao clicar no ponto e apertar Delete)
  const handleCablePointDelete = useCallback(async (cableId: string, pointIndex: number) => {
    const cable = cables.find(c => c.id === cableId);
    if (!cable) return;

    // Impedir deleção se o cabo tem apenas 2 pontos (mínimo)
    if (cable.points.length <= 2) {
      mapToast.warning('Não é possível remover: um cabo precisa ter no mínimo 2 pontos');
      return;
    }

    const pontoId = cable.points[pointIndex]?.id;
    if (!pontoId) {
      mapToast.error('Ponto sem ID - não é possível remover');
      return;
    }

    // Confirmação via Swal
    const result = await Swal.fire({
      title: 'Remover ponto?',
      text: `Deseja remover o ponto ${pointIndex + 1} deste cabo?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
    });

    if (!result.isConfirmed) return;

    const caboIdNum = parseInt(cableId.replace('cabo-', ''));

    try {
      await caboService.deletePonto(caboIdNum, pontoId);
      // Recarregar pontos do cabo
      const pontosAtualizados = await caboService.getPontos(caboIdNum);
      const updatedCable: Cable = {
        ...cable,
        points: mapCaboPontosToCoordinates(pontosAtualizados),
      };

      setCables(prev => prev.map(c => c.id === cableId ? updatedCable : c));

      if (isEditingCable && editingCable?.id === cableId) {
        setEditingCable(updatedCable);
      }

      mapToast.success('Ponto removido com sucesso');
    } catch (error: any) {
      mapToast.error('Erro ao remover ponto');
    }
  }, [cables, isEditingCable, editingCable]);

  // Handler para inserir ponto no cabo atual
  const handleCurrentCablePointInsert = useCallback((afterIndex: number, newPosition: Coordinates) => {
    setCurrentCable(prev => {
      if (!prev) return prev;
      const newPoints = [...prev.points];

      if (afterIndex === -1) {
        // Inserir no início do cabo atual
        newPoints.unshift(newPosition);
      } else {
        // Inserir após o índice especificado
        newPoints.splice(afterIndex + 1, 0, newPosition);
      }

      return { ...prev, points: newPoints };
    });
  }, []);

  const onCurrentCablePointInsert = useCallback((afterIndex: number, newPosition: Coordinates) => {
    if (editingCable) {
      handleCablePointInsert(editingCable.id, afterIndex, newPosition);
    } else {
      handleCurrentCablePointInsert(afterIndex, newPosition);
    }
  }, [editingCable, handleCablePointInsert, handleCurrentCablePointInsert]);

  const handleAddCablePointFromItem = useCallback((
    lat: number,
    lng: number,
    item: { tipo: 'caixa' | 'armario' | 'poste'; id: number }
  ) => {
    const caixaArmarioId = item.tipo !== 'poste' ? item.id : null;
    const caixaArmarioTipo = item.tipo !== 'poste' ? item.tipo : null;
    const newPoint: Coordinates = {
      lat,
      lng,
      caixa_armario_id: caixaArmarioId,
      caixa_armario_tipo: caixaArmarioTipo as any,
    };

    if (isDrawingCable) {
      setCurrentCable(prev => {
        if (!prev) {
          return {
            id: `cable-${Date.now()}`,
            points: [newPoint],
            type: 'drawing',
            color: '#2196F3',
            editable: true,
          };
        }
        return { ...prev, points: [...prev.points, newPoint] };
      });
    } else if (isEditingCable && editingCable) {
      onCurrentCablePointInsert(editingCable.points.length - 1, newPoint);
    }
  }, [isDrawingCable, isEditingCable, editingCable, onCurrentCablePointInsert]);

  // Handler para marcar que o auto-fit foi aplicado
  const handleAutoFitApplied = useCallback(() => {
    setAutoFitApplied(true);
  }, []);

  // Handlers dos modos (apenas para os botões de adicionar)
  const handleAddCaixaClick = (tipo: 'cto' | 'ceo' | 'reserva') => {
    const wasTipo = addingCaixaTipoRef.current;
    addingCaixaTipoRef.current = tipo;
    setAddingCaixaTipo(tipo);
    setIsDrawingCable(false);
    setIsMeasuring(false);
    setIsAddingArmario(false);
    setIsAddingPoste(false);
    setIsAddingArea(false);
    setCurrentCable(null);
    setRulerPoints([]);
    setAreaPoints([]);
    setIsAddingCaixa(prev => (wasTipo === tipo && prev) ? false : true);
  };
  useEffect(() => { addingCaixaTipoRef.current = addingCaixaTipo; }, [addingCaixaTipo]);

  const handleAddArmarioClick = () => {
    setIsAddingArmario(!isAddingArmario);
    setIsAddingCaixa(false);
    setIsDrawingCable(false);
    setIsMeasuring(false);
    setIsAddingPoste(false);
    setIsAddingArea(false);
    setCurrentCable(null);
    setRulerPoints([]);
    setAreaPoints([]);
  };

  const handleToggleAddPoste = () => {
    setIsAddingPoste(!isAddingPoste);
    setIsAddingCaixa(false);
    setIsAddingArmario(false);
    setIsDrawingCable(false);
    setIsMeasuring(false);
    setIsAddingArea(false);
    setCurrentCable(null);
    setRulerPoints([]);
    setAreaPoints([]);
  };

  const handleToggleAddArea = () => {
    const next = !isAddingArea;
    setIsAddingArea(next);
    setIsAddingCaixa(false);
    setIsAddingArmario(false);
    setIsAddingPoste(false);
    setIsDrawingCable(false);
    setIsMeasuring(false);
    setCurrentCable(null);
    setRulerPoints([]);
    if (!next) setAreaPoints([]);
  };

  const handleFinishArea = () => {
    if (areaPoints.length >= 3) {
      setShowAreaForm(true);
    } else {
      mapToast.warning('Adicione pelo menos 3 pontos para formar o polígono');
    }
  };

  const handleCancelArea = () => {
    setAreaPoints([]);
    setIsAddingArea(false);
    setShowAreaForm(false);
  };

  const handleCurrentAreaPointUpdate = useCallback((index: number, newPosition: Coordinates) => {
    setAreaPoints(prev => prev.map((p, i) => (i === index ? newPosition : p)));
  }, []);

  const handleAreaFormSuccess = (area?: { id: number }) => {
    setShowAreaForm(false);
    setAreaPoints([]);
    setIsAddingArea(false);
    setEditingArea(null);
    loadAreas();
    if (area?.id) {
      setVisibleAreaIds(prev => new Set(prev).add(area.id));
    }
    mapToast.success('Área salva com sucesso');
  };

  const handleAreaFormCancel = () => {
    setShowAreaForm(false);
    setAreaPoints([]);
    setEditingArea(null);
  };

  const handleAreaContextMenu = useCallback((area: FtthAreaMapaForMapLocal, x: number, y: number) => {
    setBlockNativeContextMenu(true);
    setBlockMapDrag(true);
    setAreaContextMenu({ visible: true, x, y, area });
  }, []);

  const handleCloseAreaContextMenu = useCallback(() => {
    setAreaContextMenu(prev => ({ ...prev, visible: false }));
    setBlockNativeContextMenu(false);
    setBlockMapDrag(false);
  }, []);

  const handleEditArea = useCallback((area: FtthAreaMapaForMapLocal) => {
    setEditingArea(area);
    setAreaPoints(area.coordenadas || []);
    setShowAreaForm(true);
    handleCloseAreaContextMenu();
  }, [handleCloseAreaContextMenu]);

  const handleEditAreaPoints = useCallback((area: FtthAreaMapaForMapLocal) => {
    handleCloseAreaContextMenu();
    setEditingArea(area);
    setEditingAreaCoords(area.coordenadas || []);
    setEditingAreaPoints(true);
    setSelectedAreaVertexIndex(null);
    setBlockMapDrag(true); // Bloquear arraste do mapa para permitir arrastar vértices do polígono
  }, [handleCloseAreaContextMenu]);

  const handleCancelEditAreaPoints = useCallback(() => {
    setEditingArea(null);
    setEditingAreaPoints(false);
    setEditingAreaCoords(null);
    setSelectedAreaVertexIndex(null);
    setBlockMapDrag(false);
    mapToast.info('Edição de pontos cancelada');
  }, []);

  const handleAreaVertexSelect = useCallback((index: number) => {
    setSelectedAreaVertexIndex(prev => (prev === index ? null : index)); // toggle: clique no mesmo deseleciona
  }, []);

  // Tecla Delete/Backspace: remover vértice selecionado do polígono (mínimo 3 pontos)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!editingAreaPoints || !editingArea || !editingAreaCoords || selectedAreaVertexIndex === null) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (editingAreaCoords.length <= 3) {
        mapToast.warning('O polígono deve ter pelo menos 3 pontos');
        return;
      }
      e.preventDefault();
      const newCoords = editingAreaCoords.filter((_, i) => i !== selectedAreaVertexIndex);
      setEditingAreaCoords(newCoords);
      setSelectedAreaVertexIndex(null);
      mapToast.info('Ponto removido');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingAreaPoints, editingArea, editingAreaCoords, selectedAreaVertexIndex]);

  const handleAreaPathChange = useCallback((areaId: number, coords: Array<{ lat: number; lng: number }>) => {
    setEditingAreaCoords(coords);
  }, []);

  const handleSaveEditAreaPoints = useCallback(async () => {
    if (!editingArea || !editingAreaCoords || editingAreaCoords.length < 3) {
      mapToast.error('Polígono deve ter pelo menos 3 pontos');
      return;
    }
    try {
      await ftthAreaMapaService.update(editingArea.id, { coordenadas: editingAreaCoords });
      mapToast.success('Pontos da área atualizados');
      setEditingArea(null);
      setEditingAreaPoints(false);
      setEditingAreaCoords(null);
      setSelectedAreaVertexIndex(null);
      setBlockMapDrag(false);
      loadAreas();
    } catch (err: any) {
      mapToast.error(err?.response?.data?.message || err?.message || 'Erro ao atualizar pontos');
    }
  }, [editingArea, editingAreaCoords, loadAreas]);

  const handleAreaInfo = useCallback((area: FtthAreaMapaForMapLocal) => {
    handleCloseAreaContextMenu();
    let coords = area.coordenadas;
    if (typeof coords === 'string') {
      try {
        coords = JSON.parse(coords);
      } catch {
        coords = [];
      }
    }
    const arr = Array.isArray(coords) ? coords : [];
    const validCoords = arr
      .map((p: any) => {
        const lat = typeof p?.lat === 'number' ? p.lat : (p?.latitude != null ? Number(p.latitude) : null);
        const lng = typeof p?.lng === 'number' ? p.lng : (p?.longitude != null ? Number(p.longitude) : null);
        return lat != null && lng != null && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
      })
      .filter((p: any): p is { lat: number; lng: number } => p != null);
    const areaM2 = calculatePolygonAreaM2(validCoords);
    const informacoes = area.informacoes ?? '';
    void showMapInfoSwal({
      html: buildAreaInfoSwalHtml(area, areaM2, informacoes),
      confirmColor: area.cor || '#2196F3',
      isFullscreen,
    });
  }, [handleCloseAreaContextMenu, isFullscreen]);

  const handleDeleteArea = useCallback(async (area: FtthAreaMapaForMapLocal) => {
    const result = await Swal.fire({
      title: 'Excluir área?',
      text: `A área "${area.nome}" será excluída permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
      customClass: isFullscreen ? {
        container: 'swal2-fullscreen-container',
        popup: 'swal2-fullscreen-popup'
      } : undefined,
      allowOutsideClick: true,
      allowEscapeKey: true
    });
    if (result.isConfirmed) {
      try {
        await ftthAreaMapaService.delete(area.id);
        mapToast.success('Área excluída com sucesso');
        loadAreas();
      } catch (err: any) {
        mapToast.error(err?.response?.data?.message || err?.message || 'Erro ao excluir área');
      }
    }
    handleCloseAreaContextMenu();
  }, [handleCloseAreaContextMenu, loadAreas, isFullscreen]);

  const handleToggleDrawCable = () => {
    if (isDrawingCable) {
      // Se estava desenhando, apenas desativa o modo sem finalizar
      setIsDrawingCable(false);
    } else {
      // Se não estava desenhando, ativa o modo
      setIsDrawingCable(true);
      setIsAddingCaixa(false);
      setIsAddingArmario(false);
      setIsAddingPoste(false);
      setIsMeasuring(false);
      setIsAddingArea(false);
      setAreaPoints([]);
      setCurrentCable(null); // Limpar cabo anterior
      setRulerPoints([]);
    }
  };

  const handleToggleMeasuring = () => {
    if (isMeasuring) {
      // Se estava medindo, desativa o modo e limpa pontos
      setIsMeasuring(false);
      setRulerPoints([]);
    } else {
      // Se não estava medindo, ativa o modo
      setIsMeasuring(true);
      setIsAddingCaixa(false);
      setIsAddingArmario(false);
      setIsAddingPoste(false);
      setIsDrawingCable(false);
      setIsAddingArea(false);
      setAreaPoints([]);
      setCurrentCable(null);
      setRulerPoints([]);
    }
  };

  const handleClearRuler = () => {
    setIsMeasuring(false);
    setRulerPoints([]);
  };

  const handleMostrarViabilidadeNoMapa = useCallback((resultados: ViabilidadeResultado[]) => {
    visibleCaixaIdsBeforeViabilidadeRef.current = new Set(visibleCaixaIds);
    setVisibleCaixaIds(new Set(resultados.map((r) => r.id)));
    setViabilidadeFilterActive(true);

    const lat = pickedLocation?.lat;
    const lng = pickedLocation?.lng;
    if (lat && lng) {
      setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
      setMapZoom(17);
      setAutoFitApplied(true);
    }

    setViabilidadeModalOpen(false);
    setPickedLocation(null);
  }, [visibleCaixaIds, pickedLocation]);

  const handleClearViabilidadeFilter = useCallback(() => {
    if (visibleCaixaIdsBeforeViabilidadeRef.current) {
      setVisibleCaixaIds(visibleCaixaIdsBeforeViabilidadeRef.current);
      visibleCaixaIdsBeforeViabilidadeRef.current = null;
    }
    setViabilidadeFilterActive(false);
  }, []);

  useEffect(() => {
    if (!initialViabilidade || initialViabilidadeAppliedRef.current || loading) return;

    initialViabilidadeAppliedRef.current = true;

    if (initialViabilidade.lat && initialViabilidade.lng) {
      setPickedLocation({ lat: initialViabilidade.lat, lng: initialViabilidade.lng });
      setMapCenter({
        lat: parseFloat(initialViabilidade.lat),
        lng: parseFloat(initialViabilidade.lng),
      });
      setMapZoom(17);
      setAutoFitApplied(true);
    }

    if (initialViabilidade.caixaIds?.length) {
      visibleCaixaIdsBeforeViabilidadeRef.current = new Set(visibleCaixaIds);
      setVisibleCaixaIds(new Set(initialViabilidade.caixaIds));
      setViabilidadeFilterActive(true);
    }

    setViabilidadeModalOpen(true);
  }, [initialViabilidade, loading, visibleCaixaIds]);

  const handleFinishCable = () => {
    if (currentCable && currentCable.points.length > 1) {
      setSelectedCablePoints(currentCable.points);
      setShowCabosForm(true);
      setIsDrawingCable(false);
    } else {
    }
  };

  const handleCancelCable = () => {
    setCurrentCable(null);
    setIsDrawingCable(false);
  };

  // Handlers do formulário Caixa
  const handleCaixaFormSuccess = (updatedCaixa: FtthCaixa) => {
    if (editingCaixa) {
      // Modo edição - atualizar caixa existente
      setCaixas(prev => prev.map(caixa =>
        caixa.id === updatedCaixa.id ? updatedCaixa : caixa
      ));
    } else {
      // Modo criação - adicionar nova caixa
      setCaixas(prev => [...prev, updatedCaixa]);
      setVisibleCaixaIds(prev => new Set(prev).add(updatedCaixa.id));
    }

    // Limpar estados
    setShowCaixaForm(false);
    setSelectedCoordinates(null);
    setEditingCaixa(null);
  };

  const handleCaixaFormCancel = () => {
    setShowCaixaForm(false);
    setSelectedCoordinates(null);
    setEditingCaixa(null);
  };

  const handleArmarioFormSuccess = (armario: FtthArmario) => {
    if (editingArmario) {
      setArmarios(prev => prev.map(a => (a.id === armario.id ? armario : a)));
    } else {
      setArmarios(prev => [...prev, armario]);
      setVisibleArmarioIds(prev => new Set(prev).add(armario.id));
    }
    setShowArmarioForm(false);
    setSelectedCoordinates(null);
    setEditingArmario(null);
  };

  const handleArmarioFormCancel = () => {
    setShowArmarioForm(false);
    setSelectedCoordinates(null);
    setEditingArmario(null);
  };

  // Handlers do formulário Poste
  const handlePosteFormSuccess = (poste?: any) => {
    setShowPosteForm(false);
    setSelectedCoordinates(null);
    setEditingPoste(null);
    setCopyFromPoste(null);

    if (poste && !editingPoste && poste.id) {
      setVisiblePosteIds(prev => new Set(prev).add(poste.id));
    }

    mapToast.success(editingPoste ? 'Poste atualizado com sucesso' : 'Poste cadastrado com sucesso');
    loadPostes();
  };

  const handlePosteFormCancel = () => {
    setShowPosteForm(false);
    setSelectedCoordinates(null);
    setEditingPoste(null);
    setCopyFromPoste(null);
  };

  // Handlers do menu de contexto de Postes
  const handlePosteContextMenu = useCallback((poste: FtthPosteForMap, x: number, y: number) => {
    setBlockNativeContextMenu(true);
    setBlockMapDrag(true);
    setPosteContextMenu({ visible: true, x, y, poste });
  }, []);

  const handleClosePosteContextMenu = useCallback(() => {
    setPosteContextMenu(prev => ({ ...prev, visible: false }));
    setBlockNativeContextMenu(false);
    setBlockMapDrag(false);
  }, []);

  const handleEditPoste = useCallback(async (poste: FtthPosteForMap) => {
    try {
      const full = await ftthPosteService.getPoste(poste.id);
      setEditingPoste(full);
      setShowPosteForm(true);
    } catch (err: any) {
      mapToast.error(err.response?.data?.message || err.message || 'Erro ao carregar poste');
    }
    handleClosePosteContextMenu();
  }, [handleClosePosteContextMenu]);

  const handleDeletePoste = useCallback(async (poste: FtthPosteForMap) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: `Tem certeza que deseja excluir o poste "${poste.identificacao}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
    });
    if (result.isConfirmed) {
      try {
        await ftthPosteService.delete(poste.id);
        mapToast.success('Poste excluído com sucesso');
        loadPostes();
      } catch (err: any) {
        mapToast.error(err.response?.data?.message || err.message || 'Erro ao excluir poste');
      }
    }
    handleClosePosteContextMenu();
  }, [handleClosePosteContextMenu]);

  const handleMovePoste = useCallback((poste: FtthPosteForMap) => {
    setPosteIdEmMovimento(poste.id);
    mapToast.info('Arraste o marcador do poste para a nova posição. Ao soltar, o modo mover será desativado.');
    handleClosePosteContextMenu();
  }, [handleClosePosteContextMenu]);

  // Deslocamento ao copiar poste (~5 m) para não sobrepor o original
  const COPY_POSTE_OFFSET_DEG = 0.00007;

  const handleCopyPoste = useCallback(async (poste: FtthPosteForMap) => {
    try {
      const full = await ftthPosteService.getPoste(poste.id);
      setCopyFromPoste(full);
      setEditingPoste(null);
      setSelectedCoordinates(
        full.latitude != null && full.longitude != null
          ? {
            lat: full.latitude + COPY_POSTE_OFFSET_DEG,
            lng: full.longitude + COPY_POSTE_OFFSET_DEG,
          }
          : null
      );
      setShowPosteForm(true);
      mapToast.info('Preencha a identificação da cópia e salve. A posição pode ser alterada no mapa.');
    } catch (err: any) {
      mapToast.error(err.response?.data?.message || err.message || 'Erro ao carregar poste');
    }
    handleClosePosteContextMenu();
  }, [handleClosePosteContextMenu]);

  const handlePosteMove = useCallback(async (posteId: number, newPosition: Coordinates) => {
    try {
      await ftthPosteService.update(posteId, {
        latitude: newPosition.lat,
        longitude: newPosition.lng,
      });
      mapToast.success('Poste movido com sucesso');
      loadPostes();
    } catch (err: any) {
      mapToast.error(err.response?.data?.message || err.message || 'Erro ao mover poste');
    } finally {
      setPosteIdEmMovimento(null);
    }
  }, []);

  // Handlers do formulário Cabo
  const handleCabosFormSuccess = (newCabo: Cabo) => {

    // Verificar se é uma edição ou novo cabo
    const isEditing = editingCaboForForm !== null;

    if (isEditing) {
      // Atualizar cabo existente
      setCables(prev => prev.map(cable =>
        cable.id === `cabo-${newCabo.id}`
          ? mapCaboToCable(newCabo, false)
          : { ...cable, editable: false }
      ));

      // Finalizar modo de edição
      setIsEditingCable(false);
      setEditingCable(null);
      setEditingCaboOriginal(null);
      setEditingCaboForForm(null);

    } else {
      // Criar Cable compatível com o sistema de mapa
      const cableForMap = mapCaboToCable(newCabo, false);

      setCables(prev => [...prev, cableForMap]);
      setVisibleCableIds(prev => new Set(prev).add(cableForMap.id));
    }

    // Limpar estados comuns
    setShowCabosForm(false);
    setSelectedCablePoints([]);
    setCurrentCable(null);

  };

  const handleCabosFormCancel = () => {

    // Se estava em modo de edição, finalizar e desabilitar edição de todos os cabos
    if (isEditingCable) {
      setCables(prev => prev.map(cable => ({ ...cable, editable: false })));
      setIsEditingCable(false);
      setEditingCable(null);
      setEditingCaboOriginal(null);
    }

    // Limpar estados do formulário
    setShowCabosForm(false);
    setSelectedCablePoints([]);
    setCurrentCable(null);
    setEditingCaboForForm(null);

  };

  // Handlers do modal de diagrama da Caixa
  const handleCloseCaixaDiagram = () => {
    setShowCaixaDiagram(false);
    setSelectedCaixaForDiagram(null);
  };

  const handleCloseArmarioDiagram = () => {
    setShowArmarioDiagram(false);
    setSelectedArmarioForDiagram(null);
  };

  // Ref do container para Fullscreen API
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const mapAreaRef = useRef<HTMLDivElement>(null);

  // Sincronizar estado com Fullscreen API (ex.: usuário sai com ESC)
  useEffect(() => {
    const onFullscreenChange = () => {
      const full = !!(document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? (document as any).msFullscreenElement);
      setIsFullscreen(full);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    document.addEventListener('MSFullscreenChange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      document.removeEventListener('MSFullscreenChange', onFullscreenChange);
    };
  }, []);

  // Handlers da UI
  const handleToggleFullscreen = async () => {
    const el = fullscreenContainerRef.current;
    const doc = document as any;
    if (isFullscreen) {
      if (doc.exitFullscreen) doc.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      else if (doc.msExitFullscreen) doc.msExitFullscreen();
      setIsFullscreen(false);
    } else if (el) {
      const elAny = el as any;
      try {
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (elAny.webkitRequestFullscreen) await elAny.webkitRequestFullscreen();
        else if (elAny.msRequestFullscreen) await elAny.msRequestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.warn('Fullscreen não suportado ou negado:', err);
        setIsFullscreen(true);
      }
    }
  };

  const handleRefreshData = () => {
    loadMapData();
  };

  const handleCenterOnUser = () => {
    setTrackUserLocation((prev) => !prev);
  };

  const handleUserLocationError = useCallback((message: string) => {
    setTrackUserLocation(false);
    setError(message);
  }, []);

  // Handler para alternar tipo de mapa
  const handleToggleMapType = () => {
    const mapTypes = ['hybrid', 'satellite', 'roadmap'];
    const currentIndex = mapTypes.indexOf(mapType);
    const nextIndex = (currentIndex + 1) % mapTypes.length;
    const nextMapType = mapTypes[nextIndex];

    setMapType(nextMapType);

    if (mapInstance) {
      mapInstance.setMapTypeId(nextMapType as any);
    }
  };

  // Handlers para menu de contexto de Caixas
  const handleCaixaContextMenu = useCallback((caixa: FtthCaixa, x: number, y: number) => {

    // Ativar bloqueio seletivo do menu nativo
    setBlockNativeContextMenu(true);

    // Bloquear movimento do mapa
    setBlockMapDrag(true);

    setCaixaContextMenu({
      visible: true,
      x,
      y,
      caixa
    });

  }, []);

  const handleCloseCaixaContextMenu = useCallback(() => {
    setCaixaContextMenu(prev => ({ ...prev, visible: false }));

    // Desativar bloqueio seletivo do menu nativo
    setBlockNativeContextMenu(false);

    // Reabilitar movimento do mapa
    setBlockMapDrag(false);

  }, []);

  const handleArmarioContextMenu = useCallback((armario: FtthArmario, x: number, y: number) => {
    setBlockNativeContextMenu(true);
    setBlockMapDrag(true);
    setArmarioContextMenu({ visible: true, x, y, armario });
  }, []);

  const handleCloseArmarioContextMenu = useCallback(() => {
    setArmarioContextMenu(prev => ({ ...prev, visible: false }));
    setBlockNativeContextMenu(false);
    setBlockMapDrag(false);
  }, []);

  const handleEditArmario = useCallback((armario: FtthArmario) => {
    setEditingArmario(armario);
    const coordinates = validateCoordinates(armario.latitude, armario.longitude);
    if (coordinates) setSelectedCoordinates(coordinates);
    setShowArmarioForm(true);
    handleCloseArmarioContextMenu();
  }, [handleCloseArmarioContextMenu]);

  const handleMoveArmario = useCallback((armario: FtthArmario) => {
    setArmarioIdEmMovimento(armario.id);
    mapToast.info('Arraste o marcador do armário para a nova posição. Ao soltar, o modo mover será desativado.');
    handleCloseArmarioContextMenu();
  }, [handleCloseArmarioContextMenu]);

  const handleDiagramArmario = useCallback((armario: FtthArmario) => {
    setSelectedArmarioForDiagram(armario);
    setShowArmarioDiagram(true);
    handleCloseArmarioContextMenu();
  }, [handleCloseArmarioContextMenu]);

  const handleDeleteArmario = useCallback(async (armario: FtthArmario) => {
    const result = await Swal.fire({
      title: 'Confirmar Exclusão',
      text: `Tem certeza que deseja deletar o armário "${armario.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar',
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement
    });
    if (result.isConfirmed) {
      try {
        await ftthArmarioService.delete(armario.id);
        setArmarios(prev => prev.filter(a => a.id !== armario.id));
        mapToast.success('Armário excluído com sucesso');
      } catch (err: any) {
        mapToast.error(err.message || 'Erro ao excluir armário');
        loadArmarios();
      }
    }
    handleCloseArmarioContextMenu();
  }, [handleCloseArmarioContextMenu, loadArmarios]);

  const handleMoveCaixa = useCallback((caixa: FtthCaixa) => {
    setCaixaIdEmMovimento(caixa.id);
    mapToast.info('Arraste o marcador da caixa para a nova posição. Ao soltar, o modo mover será desativado.');
    handleCloseCaixaContextMenu();
  }, [handleCloseCaixaContextMenu, mapToast]);

  const handleDiagramCaixa = useCallback((caixa: FtthCaixa) => {
    setSelectedCaixaForDiagram(caixa);
    setShowCaixaDiagram(true);
    handleCloseCaixaContextMenu();
  }, [handleCloseCaixaContextMenu]);

  const handleViewCaixa = useCallback((caixaId: number) => {
    navigate(`/ftth/caixas/${caixaId}`);
    handleCloseCaixaContextMenu();
  }, [navigate, handleCloseCaixaContextMenu]);

  const handleCancelCaixaMove = useCallback(() => {
    setCaixaIdEmMovimento(null);
    mapToast.info('Movimento cancelado');
    handleCloseCaixaContextMenu();
  }, [handleCloseCaixaContextMenu, mapToast]);

  const handleEditCaixa = useCallback((caixa: FtthCaixa) => {
    // Configurar dados para edição
    setEditingCaixa(caixa);
    const coordinates = validateCoordinates(caixa.latitude, caixa.longitude);
    if (coordinates) {
      setSelectedCoordinates(coordinates);
    }
    setShowCaixaForm(true);
    // Fechar menu de contexto
    handleCloseCaixaContextMenu();
  }, [handleCloseCaixaContextMenu]);

  const handleDeleteCaixa = useCallback(async (caixa: FtthCaixa) => {
    const result = await Swal.fire({
      title: 'Confirmar Exclusão',
      text: `Tem certeza que deseja deletar a Caixa "${caixa.name}"?`,
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <div style="background-color: #fff3cd; padding: 10px; border-radius: 4px; margin-bottom: 15px;">
            <strong>⚠️ ATENÇÃO:</strong> Esta ação não pode ser desfeita!
          </div>
          
          <strong>📡 Caixa:</strong> ${caixa.name}<br/>
          <strong>🆔 ID:</strong> ${caixa.id}<br/>
          <strong>📍 Endereço:</strong> ${caixa.address || 'Não informado'}<br/>
          <strong>📊 Status:</strong> 
          <span style="color: ${CAIXA_COLORS[caixa.status]}; font-weight: bold;">
            ${caixa.status.replace('_', ' ').toUpperCase()}
          </span><br/>
          <strong>🔌 Portas em uso:</strong> ${caixa.portas_em_uso ?? 0} / ${caixa.capacidade ?? 0}
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
      customClass: isFullscreen ? {
        container: 'swal2-fullscreen-container',
        popup: 'swal2-fullscreen-popup'
      } : undefined,
      allowOutsideClick: true,
      allowEscapeKey: true
    });

    if (result.isConfirmed) {
      try {
        // Deletar no backend
        await ftthCaixaService.delete(caixa.id);

        // Remover do estado local
        setCaixas(prev => prev.filter(c => c.id !== caixa.id));
        await Swal.fire({
          title: 'Deletada!',
          text: `A Caixa "${caixa.name}" foi removida com sucesso.`,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false,
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: isFullscreen ? {
            container: 'swal2-fullscreen-container',
            popup: 'swal2-fullscreen-popup'
          } : undefined
        });

      } catch (error: any) {
        await Swal.fire({
          title: 'Erro!',
          text: `Erro ao deletar Caixa: ${error.message || 'Erro desconhecido'}`,
          icon: 'error',
          confirmButtonText: 'OK',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: isFullscreen ? {
            container: 'swal2-fullscreen-container',
            popup: 'swal2-fullscreen-popup'
          } : undefined
        });
      }
    } else {
    }

    // Fechar menu de contexto
    handleCloseCaixaContextMenu();
  }, [handleCloseCaixaContextMenu, isFullscreen]);

  // Handlers para menu de contexto de Cabos
  const handleCableContextMenu = useCallback((cable: Cable, x: number, y: number) => {
    // Ativar bloqueio seletivo do menu nativo
    setBlockNativeContextMenu(true);

    // Bloquear movimento do mapa
    setBlockMapDrag(true);

    setCableContextMenu({
      visible: true,
      x,
      y,
      cable
    });

  }, []);

  const handleCloseCableContextMenu = useCallback(() => {
    setCableContextMenu(prev => ({ ...prev, visible: false }));

    // Desativar bloqueio seletivo do menu nativo
    setBlockNativeContextMenu(false);

    // Reabilitar movimento do mapa
    setBlockMapDrag(false);

  }, []);

  // Função para ativar modo de edição de pontos (menu de contexto)
  const handleActivateCableEditMode = useCallback(async (cable: Cable) => {
    setSelectedCableId(null);

    try {
      // Extrair ID numérico do cabo
      const caboId = parseInt(cable.id.replace('cabo-', ''));

      if (isNaN(caboId)) {
        return;
      }

      // Buscar dados originais do cabo no backend
      const caboOriginal = await caboService.getCaboById(caboId);

      // Configurar estados para permitir salvamento automático
      setIsEditingCable(true);
      setEditingCable(cable);
      setEditingCaboOriginal(caboOriginal);

      // Desabilitar edição de todos os outros cabos e ativar o atual
      setCables(prev => prev.map(c => ({
        ...c,
        editable: c.id === cable.id ? true : false
      })));

    } catch (error: any) {
      mapToast.error(`Erro ao ativar edição: ${error.message || 'Erro desconhecido'}`);

      // Fallback: apenas ativar visualmente sem salvamento automático
      setCables(prev => prev.map(c => ({
        ...c,
        editable: c.id === cable.id ? true : false
      })));
    }
  }, []);

  const handleCableSelect = useCallback((cable: Cable) => {
    setSelectedCableId(cable.id);
  }, []);

  const handleEditCablePoints = useCallback((cable: Cable) => {
    handleCloseCableContextMenu();
    handleActivateCableEditMode(cable);
  }, [handleCloseCableContextMenu, handleActivateCableEditMode]);

  // Função para abrir formulário de edição (menu de contexto)
  const handleEditCable = useCallback(async (cable: Cable) => {
    try {
      // Extrair ID numérico do cabo
      const caboId = parseInt(cable.id.replace('cabo-', ''));

      if (isNaN(caboId)) {
        return;
      }

      // Buscar dados originais do cabo no backend
      const caboOriginal = await caboService.getCaboById(caboId);

      // Configurar pontos atuais do mapa (que podem ter sido editados)
      setSelectedCablePoints(cable.points);

      // Configurar cabo para edição no formulário
      setEditingCaboForForm(caboOriginal);

      // Debug: Verificar se editingCaboForForm está sendo configurado
      // Abrir CabosForm diretamente em modo de edição
      setShowCabosForm(true);

      // Fechar menu de contexto
      handleCloseCableContextMenu();

    } catch (error: any) {
      await Swal.fire({
        title: 'Erro!',
        text: `Erro ao carregar dados do cabo: ${error.message || 'Erro desconhecido'}`,
        icon: 'error',
        confirmButtonText: 'OK',
        target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
        customClass: isFullscreen ? {
          container: 'swal2-fullscreen-container',
          popup: 'swal2-fullscreen-popup'
        } : undefined
      });

      // Fechar menu de contexto mesmo com erro
      handleCloseCableContextMenu();
    }
  }, [handleCloseCableContextMenu, isFullscreen]);

  const handleDeleteCable = useCallback(async (cable: Cable) => {
    const totalDistance = calculateTotalDistance(cable.points);
    const formattedDistance = formatDistance(totalDistance);

    const result = await Swal.fire({
      title: 'Confirmar Exclusão',
      text: `Tem certeza que deseja deletar este cabo?`,
      html: `
        <div style="text-align: left; margin-top: 15px;">
          <div style="background-color: #fff3cd; padding: 10px; border-radius: 4px; margin-bottom: 15px;">
            <strong>⚠️ ATENÇÃO:</strong> Esta ação não pode ser desfeita!
          </div>
          
          <strong>🔌 Cabo:</strong> ${cable.id.replace('cabo-', '')}<br/>
          <strong>📏 Distância:</strong> ${formattedDistance}<br/>
          <strong>📍 Pontos:</strong> ${cable.points.length}<br/>
          <strong>🎨 Cor:</strong> 
          <span style="color: ${cable.color}; font-weight: bold;">
            ${cable.color.toUpperCase()}
          </span><br/>
          <strong>🔧 Tipo:</strong> ${cable.type === 'fiber' ? 'Fibra Óptica' : 'Em Desenho'}
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
      customClass: isFullscreen ? {
        container: 'swal2-fullscreen-container',
        popup: 'swal2-fullscreen-popup'
      } : undefined,
      allowOutsideClick: true,
      allowEscapeKey: true
    });

    if (result.isConfirmed) {
      try {
        // Extrair ID numérico do cabo
        const caboId = parseInt(cable.id.replace('cabo-', ''));

        if (!isNaN(caboId)) {
          // Deletar no backend
          await caboService.deleteCabo(caboId);
        }

        // Remover do estado local
        setCables(prev => prev.filter(c => c.id !== cable.id));

        await Swal.fire({
          title: 'Deletado!',
          text: `O cabo foi removido com sucesso.`,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false,
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: isFullscreen ? {
            container: 'swal2-fullscreen-container',
            popup: 'swal2-fullscreen-popup'
          } : undefined
        });

      } catch (error: any) {
        await Swal.fire({
          title: 'Erro!',
          text: `Erro ao deletar cabo: ${error.message || 'Erro desconhecido'}`,
          icon: 'error',
          confirmButtonText: 'OK',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: isFullscreen ? {
            container: 'swal2-fullscreen-container',
            popup: 'swal2-fullscreen-popup'
          } : undefined
        });
      }
    } else {
    }

    // Fechar menu de contexto
    handleCloseCableContextMenu();
  }, [handleCloseCableContextMenu, isFullscreen]);

  const handleFinishCableEdit = useCallback(async () => {
    if (!editingCable || !editingCaboOriginal) {
      return;
    }

    try {
      // Pontos já foram salvos individualmente (updatePonto/addPonto)
      // Aqui só salvamos metadados se necessário
      await caboService.updateCabo(editingCaboOriginal.id, {
        titulo: editingCaboOriginal.titulo,
        qtde_fo: editingCaboOriginal.qtde_fo,
        color: editingCaboOriginal.color
      });

      mapToast.success(`Cabo ${editingCaboOriginal.id} salvo com sucesso!`);

      setCables(prev => prev.map(cable => ({ ...cable, editable: false })));
    } catch (error: any) {
      mapToast.error(`Erro ao salvar cabo: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsEditingCable(false);
      setEditingCable(null);
      setEditingCaboOriginal(null);
      setEditingCaboForForm(null);
    }
  }, [editingCable, editingCaboOriginal]);

  const handleCancelCableEdit = useCallback(() => {
    // Sair do modo de edição
    setIsEditingCable(false);
    setEditingCable(null);
    setEditingCaboOriginal(null);
    setEditingCaboForForm(null);

    // Desabilitar edição de todos os cabos
    setCables(prev => prev.map(c => ({ ...c, editable: false })));
  }, []);

  // Estatísticas em tempo real (usadas no modal de estatísticas)
  const stats = {
    ctoAtivas: caixas.filter(c => c.tipo === 'cto' && c.status === 'ativo').length,
    ctoInativas: caixas.filter(c => c.tipo === 'cto' && c.status === 'inativo').length,
    ceo: caixas.filter(c => c.tipo === 'ceo').length,
    cabos: cables.length,
    armarios: armarios.length,
    postes: postes.length,
    areas: areas.length
  };

  if (!googleMapsApiKey && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Carregando configurações...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={fullscreenContainerRef}
      sx={{
        height: isFullscreen ? '100vh' : 'calc(100vh - 100px)',
        width: '100%',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        display: 'flex',
        bgcolor: isFullscreen ? 'background.default' : 'transparent',
      }}
    >
      {/* ToastContainer dentro do mapa - visível em fullscreen */}
      <ToastContainer
        containerId="map-fullscreen"
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 100000 }}
      />
      {/* Área do Mapa */}
      <Box ref={mapAreaRef} sx={{ flex: 1, position: 'relative' }}>
        {/* Menu Flutuante de Ferramentas */}
        <Tooltip title="Ferramentas do mapa (arraste para mover)" disableTouchListener>
          <DraggableMapFab
            containerRef={mapAreaRef}
            storageKey="ftth-map-tools-fab-position"
            defaultPosition={{ top: 16, left: 16 }}
            color="primary"
            size="medium"
            onClick={(e) => setToolsMenuAnchorEl(e.currentTarget)}
            sx={{ boxShadow: 2 }}
          >
            <BuildIcon />
          </DraggableMapFab>
        </Tooltip>
        <Menu
          anchorEl={toolsMenuAnchorEl}
          open={toolsMenuOpen}
          onClose={() => setToolsMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          container={isFullscreen ? fullscreenContainerRef.current : undefined}
          disablePortal={!!isFullscreen}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 220,
              maxHeight: '70vh',
              boxShadow: 3,
              p: 1
            }
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            <Tooltip title="Adicionar CTO">
              <Box
                onClick={() => { handleAddCaixaClick('cto'); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingCaixa && addingCaixaTipo === 'cto' ? 'primary.main' : 'transparent',
                  color: isAddingCaixa && addingCaixaTipo === 'cto' ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingCaixa && addingCaixaTipo === 'cto' ? 'primary.dark' : 'primary.light', color: isAddingCaixa && addingCaixaTipo === 'cto' ? 'white' : 'primary.dark' }
                }}
              >
                <Box component="img" src={getCtoIconDataUrl(isAddingCaixa && addingCaixaTipo === 'cto' ? '#fff' : theme.palette.primary.main)} alt="CTO" sx={{ width: 18, height: 22 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>CTO</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Adicionar CEO">
              <Box
                onClick={() => { handleAddCaixaClick('ceo'); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingCaixa && addingCaixaTipo === 'ceo' ? 'primary.main' : 'transparent',
                  color: isAddingCaixa && addingCaixaTipo === 'ceo' ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingCaixa && addingCaixaTipo === 'ceo' ? 'primary.dark' : 'primary.light', color: isAddingCaixa && addingCaixaTipo === 'ceo' ? 'white' : 'primary.dark' }
                }}
              >
                <Box component="img" src={getCeoIconDataUrl(isAddingCaixa && addingCaixaTipo === 'ceo' ? '#fff' : theme.palette.primary.main)} alt="CEO" sx={{ width: 12, height: 24 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>CEO</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Adicionar Reserva">
              <Box
                onClick={() => { handleAddCaixaClick('reserva'); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingCaixa && addingCaixaTipo === 'reserva' ? 'primary.main' : 'transparent',
                  color: isAddingCaixa && addingCaixaTipo === 'reserva' ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingCaixa && addingCaixaTipo === 'reserva' ? 'primary.dark' : 'primary.light', color: isAddingCaixa && addingCaixaTipo === 'reserva' ? 'white' : 'primary.dark' }
                }}
              >
                <ReservaIcon sx={{ fontSize: 20 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>Reserva</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Adicionar Armário">
              <Box
                onClick={() => { handleAddArmarioClick(); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingArmario ? 'primary.main' : 'transparent',
                  color: isAddingArmario ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingArmario ? 'primary.dark' : 'primary.light', color: isAddingArmario ? 'white' : 'primary.dark' }
                }}
              >
                <ArmarioIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>ARMÁRIO</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Desenhar Cabo">
              <Box
                onClick={() => { handleToggleDrawCable(); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isDrawingCable ? 'primary.main' : 'transparent',
                  color: isDrawingCable ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isDrawingCable ? 'primary.dark' : 'primary.light', color: isDrawingCable ? 'white' : 'primary.dark' }
                }}
              >
                <CableIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>CABO</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Adicionar Poste">
              <Box
                onClick={() => { handleToggleAddPoste(); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingPoste ? 'primary.main' : 'transparent',
                  color: isAddingPoste ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingPoste ? 'primary.dark' : 'primary.light', color: isAddingPoste ? 'white' : 'primary.dark' }
                }}
              >
                <Box component="img" src={getPosteIconDataUrl('sem_trafo', isAddingPoste ? '#fff' : theme.palette.primary.main)} alt="POSTE" sx={{ width: 18, height: 22 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>POSTE</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Régua de Medição">
              <Box
                onClick={() => { handleToggleMeasuring(); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isMeasuring ? 'primary.main' : 'transparent',
                  color: isMeasuring ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isMeasuring ? 'primary.dark' : 'primary.light', color: isMeasuring ? 'white' : 'primary.dark' }
                }}
              >
                <ReguaIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>RÉGUA</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Marcar Área (polígono)">
              <Box
                onClick={() => { handleToggleAddArea(); setToolsMenuAnchorEl(null); }}
                sx={{
                  width: 48,
                  height: 48,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: isAddingArea ? 'primary.main' : 'transparent',
                  color: isAddingArea ? 'white' : 'primary.main',
                  '&:hover': { backgroundColor: isAddingArea ? 'primary.dark' : 'primary.light', color: isAddingArea ? 'white' : 'primary.dark' }
                }}
              >
                <LandscapeIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontSize: 8 }}>ÁREA</Typography>
              </Box>
            </Tooltip>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Button
            variant="contained"
            fullWidth
            startIcon={<SearchIcon />}
            onClick={() => {
              setViabilidadeModalOpen(true);
              setToolsMenuAnchorEl(null);
            }}
            sx={{ mb: 1, py: 1, borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
          >
            Consultar Viabilidade
          </Button>
          <Typography variant="overline" sx={{ px: 0.5, mb: 0.1, display: 'block', color: 'text.secondary', fontWeight: 'bold', lineHeight: 1.5 }}>
            Componentes do mapa
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showOnlyAguardandoInstalacao}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowOnlyAguardandoInstalacao(checked);
                  if (checked) setHideAguardandoInstalacao(false);
                }}
                disabled={!hasMapComponentsSelected}
                sx={{ color: CAIXA_COLORS.aguardando_instalacao, '&.Mui-checked': { color: CAIXA_COLORS.aguardando_instalacao } }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: '0.75rem', userSelect: 'none', color: hasMapComponentsSelected ? 'text.primary' : 'text.disabled' }}>
                Exibir somente aguardando instalação
              </Typography>
            }
            sx={{ px: 0.01, mb: 0.01, ml: 0, display: 'flex' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hideAguardandoInstalacao}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHideAguardandoInstalacao(checked);
                  if (checked) setShowOnlyAguardandoInstalacao(false);
                }}
                disabled={!hasMapComponentsSelected}
                sx={{ color: CAIXA_COLORS.aguardando_instalacao, '&.Mui-checked': { color: CAIXA_COLORS.aguardando_instalacao } }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: '0.75rem', userSelect: 'none', color: hasMapComponentsSelected ? 'text.primary' : 'text.disabled' }}>
                Ocultar aguardando instalação
              </Typography>
            }
            sx={{ px: 0.01, mb: 0.01, ml: 0, display: 'flex' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showOnlyDrop}
                onChange={(e) => setShowOnlyDrop(e.target.checked)}
                disabled={!hasMapComponentsSelected}
                sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: '0.75rem', userSelect: 'none', color: hasMapComponentsSelected ? 'text.primary' : 'text.disabled' }}>
                Exibir somente drop
              </Typography>
            }
            sx={{ px: 0.01, mb: 0.01, ml: 0, display: 'flex' }}
          />
          <Box sx={{ maxHeight: 320, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' } }}>
            {escritorios.map((esc, rootIndex) => {
              const isLastRoot = rootIndex === escritorios.length - 1;
              const isExpanded = expandedEscritorios.has(esc.id);
              const _armarios = armarios.filter(a => a.escritorio_id === esc.id);
              const _cabos = filterCablesByEscritorio(cables, esc.id);
              const _ctos = caixas.filter(c => c.tipo === 'cto' && c.escritorio_id === esc.id);
              const _ceos = caixas.filter(c => c.tipo === 'ceo' && c.escritorio_id === esc.id);
              const _reservas = caixas.filter(c => c.tipo === 'reserva' && c.escritorio_id === esc.id);
              const _postes = postes.filter(p => (p as any).escritorio_id === esc.id || !(p as any).escritorio_id);
              const _areas = areas.filter(a => a.escritorio_id === esc.id);
              const allArmariosChecked = _armarios.length > 0 && _armarios.every(a => visibleArmarioIds.has(a.id));
              const someArmariosChecked = _armarios.some(a => visibleArmarioIds.has(a.id));
              const allCabosChecked = _cabos.length > 0 && _cabos.every(c => visibleCableIds.has(c.id));
              const someCabosChecked = _cabos.some(c => visibleCableIds.has(c.id));
              const allCtoChecked = _ctos.length > 0 && _ctos.every(c => visibleCaixaIds.has(c.id));
              const someCtoChecked = _ctos.some(c => visibleCaixaIds.has(c.id));
              const allCeoChecked = _ceos.length > 0 && _ceos.every(c => visibleCaixaIds.has(c.id));
              const someCeoChecked = _ceos.some(c => visibleCaixaIds.has(c.id));
              const allReservasChecked = _reservas.length > 0 && _reservas.every(c => visibleCaixaIds.has(c.id));
              const someReservasChecked = _reservas.some(c => visibleCaixaIds.has(c.id));
              const allPostesChecked = _postes.length > 0 && _postes.every(p => visiblePosteIds.has(p.id));
              const somePostesChecked = _postes.some(p => visiblePosteIds.has(p.id));
              const allAreasChecked = _areas.length > 0 && _areas.every(a => visibleAreaIds.has(a.id));
              const someAreasChecked = _areas.some(a => visibleAreaIds.has(a.id));
              const allEscritorioChecked =
                (_armarios.length === 0 || allArmariosChecked) &&
                (_cabos.length === 0 || allCabosChecked) &&
                (_ctos.length === 0 || allCtoChecked) &&
                (_ceos.length === 0 || allCeoChecked) &&
                (_reservas.length === 0 || allReservasChecked) &&
                (_postes.length === 0 || allPostesChecked) &&
                (_areas.length === 0 || allAreasChecked);
              const someEscritorioChecked = someArmariosChecked || someCabosChecked || someCtoChecked || someCeoChecked || someReservasChecked || somePostesChecked || someAreasChecked;
              const toggleAllEscritorioItems = (e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                if (allEscritorioChecked) {
                  unmarkAllArmarios(esc.id);
                  unmarkAllCabos(esc.id);
                  unmarkAllCto(esc.id);
                  unmarkAllCeo(esc.id);
                  unmarkAllReserva(esc.id);
                  unmarkAllPostes(esc.id);
                  unmarkAllAreas(esc.id);
                } else {
                  markAllArmarios(esc.id);
                  markAllCabos(esc.id);
                  markAllCto(esc.id);
                  markAllCeo(esc.id);
                  markAllReserva(esc.id);
                  markAllPostes(esc.id);
                  markAllAreas(esc.id);
                }
              };
              const TreeIcon = () => <AccountTreeIcon sx={{ fontSize: 16, color: '#2e7d32', ml: 0.5, mr: 0.5 }} />;
              return (
                <Box key={esc.id} sx={{ position: 'relative' }}>
                  {!isLastRoot && <Box sx={{ position: 'absolute', top: 12, bottom: 0, left: 6, borderLeft: '1px dotted #888', zIndex: 1 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, position: 'relative', zIndex: 2 }}>
                    <Box onClick={(e) => { e.stopPropagation(); toggleEscritorio(esc.id); }} sx={{ width: 12, height: 12, border: '1px solid #555', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.5, bgcolor: '#fff', cursor: 'pointer' }}>
                      <Box sx={{ width: 0, height: 0, ...(isExpanded ? { borderTop: '6px solid #555', borderLeft: '6px solid transparent', borderRight: '6px solid transparent' } : { borderLeft: '6px solid #555', borderTop: '4px solid transparent', borderBottom: '4px solid transparent' }) }} />
                    </Box>
                    <Checkbox size="small" sx={{ p: 0, ml: 0.5, mr: 0.5 }} checked={allEscritorioChecked} indeterminate={someEscritorioChecked && !allEscritorioChecked} onChange={toggleAllEscritorioItems} />
                    <TreeIcon />
                    <Typography variant="body1" sx={{ ml: 0.5, cursor: 'pointer', fontSize: '0.875rem' }} onClick={() => toggleEscritorio(esc.id)}>{esc.identificacao}</Typography>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ ml: 1.5, position: 'relative' }}>
                      {['cabos', 'ctos', 'ceos', 'reservas', 'armarios', 'postes', 'areas'].map((grupo, gi) => {
                        const _items = grupo === 'cabos' ? _cabos : grupo === 'ctos' ? _ctos : grupo === 'ceos' ? _ceos : grupo === 'reservas' ? _reservas : grupo === 'armarios' ? _armarios : grupo === 'postes' ? _postes : _areas;
                        const allChecked = grupo === 'cabos' ? allCabosChecked : grupo === 'ctos' ? allCtoChecked : grupo === 'ceos' ? allCeoChecked : grupo === 'reservas' ? allReservasChecked : grupo === 'armarios' ? allArmariosChecked : grupo === 'postes' ? allPostesChecked : allAreasChecked;
                        const someChecked = grupo === 'cabos' ? someCabosChecked : grupo === 'ctos' ? someCtoChecked : grupo === 'ceos' ? someCeoChecked : grupo === 'reservas' ? someReservasChecked : grupo === 'armarios' ? someArmariosChecked : grupo === 'postes' ? somePostesChecked : someAreasChecked;
                        const label = grupo === 'cabos' ? 'Cabos' : grupo === 'ctos' ? 'Caixas CTO' : grupo === 'ceos' ? 'Caixas CEO' : grupo === 'reservas' ? 'Reservas' : grupo === 'armarios' ? 'Armários' : grupo === 'postes' ? 'Postes' : 'Áreas';
                        const toggleFn = () => { if (allChecked) { if (grupo === 'cabos') unmarkAllCabos(esc.id); else if (grupo === 'ctos') unmarkAllCto(esc.id); else if (grupo === 'ceos') unmarkAllCeo(esc.id); else if (grupo === 'reservas') unmarkAllReserva(esc.id); else if (grupo === 'armarios') unmarkAllArmarios(esc.id); else if (grupo === 'postes') unmarkAllPostes(esc.id); else unmarkAllAreas(esc.id); } else { if (grupo === 'cabos') markAllCabos(esc.id); else if (grupo === 'ctos') markAllCto(esc.id); else if (grupo === 'ceos') markAllCeo(esc.id); else if (grupo === 'reservas') markAllReserva(esc.id); else if (grupo === 'armarios') markAllArmarios(esc.id); else if (grupo === 'postes') markAllPostes(esc.id); else markAllAreas(esc.id); } };
                        const hasId = (grupo === 'cabos' ? (c: any) => visibleCableIds.has(c.id) : grupo === 'ctos' || grupo === 'ceos' || grupo === 'reservas' ? (c: any) => visibleCaixaIds.has(c.id) : grupo === 'armarios' ? (a: any) => visibleArmarioIds.has(a.id) : grupo === 'postes' ? (p: any) => visiblePosteIds.has(p.id) : (a: any) => visibleAreaIds.has(a.id));
                        const toggleItem = (id: string | number) => {
                          if (grupo === 'cabos') toggleCableVisibility(String(id));
                          else if (grupo === 'ctos' || grupo === 'ceos' || grupo === 'reservas') toggleCaixaVisibility(Number(id));
                          else if (grupo === 'armarios') toggleArmarioVisibility(Number(id));
                          else if (grupo === 'postes') togglePosteVisibility(Number(id));
                          else toggleAreaVisibility(Number(id));
                        };
                        const panTo = grupo === 'cabos' ? (c: any) => { const pt = c.points?.[0]; if (pt) panToMapNode(pt.lat, pt.lng); } : grupo === 'ctos' || grupo === 'ceos' || grupo === 'reservas' ? (c: any) => panToMapNode(c.latitude, c.longitude) : grupo === 'armarios' ? (a: any) => panToMapNode(a.latitude, a.longitude) : grupo === 'postes' ? (p: any) => panToMapNode(p.latitude, p.longitude) : (a: any) => { const fp = a.coordenadas?.[0]; if (fp) panToMapNode(fp.lat, fp.lng); };
                        const getId = (x: any) => grupo === 'cabos' ? x.id : x.id;
                        const getLabel = (x: any) => grupo === 'cabos' ? getCableDisplayLabel(x) : grupo === 'ctos' || grupo === 'ceos' || grupo === 'reservas' ? x.name : grupo === 'armarios' ? x.name : grupo === 'postes' ? x.identificacao : x.nome;
                        return (
                          <Box key={grupo} sx={{ position: 'relative', pt: 0.5 }}>
                            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: -6, borderLeft: '1px dotted #888', zIndex: 1 }} />
                            <Box sx={{ position: 'absolute', top: 14, left: -6, width: 12, borderTop: '1px dotted #888', zIndex: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 2 }}>
                              <Box sx={{ width: 8, height: 8, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '6px solid #888', cursor: 'pointer' }} onClick={() => toggleGroup(`${grupo}_${esc.id}`)} />
                              <Checkbox size="small" sx={{ p: 0, pl: 0.5 }} checked={allChecked} indeterminate={someChecked && !allChecked} onChange={(e: any) => { e.stopPropagation(); toggleFn(); }} />
                              <TreeIcon />
                              <Typography variant="body2" sx={{ cursor: 'pointer', ml: 0.5, fontSize: '0.8rem' }} onClick={() => toggleGroup(`${grupo}_${esc.id}`)}>{label} ({_items.length})</Typography>
                            </Box>
                            <Collapse in={expandedGroups.has(`${grupo}_${esc.id}`)} timeout="auto" unmountOnExit>
                              <Box sx={{ ml: 2, pt: 0.5 }}>
                                {_items.map((item: any, idx: number) => {
                                  const isLast = idx === _items.length - 1;
                                  const id = getId(item);
                                  return (
                                    <Box key={id} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                                      <Box sx={{ position: 'absolute', top: 0, bottom: isLast ? '50%' : 0, left: -20, borderLeft: '1px dotted #888', zIndex: 1 }} />
                                      <Box sx={{ position: 'absolute', top: 10, left: -20, width: 22, borderTop: '1px dotted #888', zIndex: 1 }} />
                                      <Checkbox size="small" sx={{ p: 0, ml: 1, mr: 0.5 }} checked={hasId(item)} onChange={() => toggleItem(id)} />
                                      <TreeIcon />
                                      <Typography variant="caption" sx={{ ml: 0.5, cursor: 'pointer', fontSize: '0.75rem', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }} onClick={() => panTo(item)}>{getLabel(item)}</Typography>
                                    </Box>
                                  );
                                })}
                                {_items.length === 0 && <Typography variant="caption" sx={{ ml: 3, color: 'text.disabled' }}>Vazio</Typography>}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {escritorios.length === 0 && <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', display: 'block' }}>Nenhum escritório carregado</Typography>}
          </Box>
        </Menu>

        {/* Botões Finalizar/Cancelar/Limpar - topo centro */}
        {(currentCable && currentCable.points.length > 0) || (areaPoints.length > 0 && !editingAreaPoints) || (isEditingCable && editingCable) || (editingAreaPoints && editingArea) || rulerPoints.length > 0 ? (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {currentCable && currentCable.points.length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<CheckIcon />}
                  onClick={handleFinishCable}
                  disabled={!currentCable || currentCable.points.length < 2}
                >
                  Finalizar Cabo ({currentCable?.points.length || 0} pontos)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelCable}
                >
                  Cancelar Cabo
                </Button>
              </>
            )}
            {areaPoints.length > 0 && !editingAreaPoints && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<CheckIcon />}
                  onClick={handleFinishArea}
                  disabled={areaPoints.length < 3}
                >
                  Finalizar Área ({areaPoints.length} pontos)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelArea}
                >
                  Cancelar Área
                </Button>
              </>
            )}
            {isEditingCable && editingCable && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<SaveIcon />}
                  onClick={handleFinishCableEdit}
                >
                  Finalizar Edição ({editingCable.points.length} pontos)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelCableEdit}
                >
                  Cancelar Edição
                </Button>
              </>
            )}
            {editingAreaPoints && editingArea && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveEditAreaPoints}
                  disabled={!editingAreaCoords || editingAreaCoords.length < 3}
                >
                  Salvar ({editingAreaCoords?.length ?? 0} pontos)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEditAreaPoints}
                >
                  Cancelar Edição
                </Button>
              </>
            )}
            {rulerPoints.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                onClick={handleClearRuler}
              >
                Limpar Régua ({rulerPoints.length} pontos)
                {rulerPoints.length > 1 && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.9 }}>
                    📏 {formatDistance(calculateTotalDistance(rulerPoints))}
                  </Typography>
                )}
              </Button>
            )}
          </Paper>
        ) : null}

        {/* Toolbar Superior */}
        {isCompactMapToolbar ? (
          <>
            <Tooltip title="Ações do mapa (arraste para mover)" disableTouchListener>
              <DraggableMapFab
                containerRef={mapAreaRef}
                storageKey="ftth-map-actions-fab-position"
                defaultPosition={{ top: 16, right: 16 }}
                size="medium"
                onClick={(e) => setMapActionsMenuAnchorEl(e.currentTarget)}
                sx={{
                  boxShadow: 2,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <MoreVertIcon />
              </DraggableMapFab>
            </Tooltip>
            <Menu
              anchorEl={mapActionsMenuAnchorEl}
              open={mapActionsMenuOpen}
              onClose={() => setMapActionsMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              container={isFullscreen ? fullscreenContainerRef.current : undefined}
              disablePortal={!!isFullscreen}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 220,
                  boxShadow: 3,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleCenterOnUser();
                  setMapActionsMenuAnchorEl(null);
                }}
                selected={trackUserLocation}
              >
                <ListItemIcon>
                  <MyLocationIcon fontSize="small" color={trackUserLocation ? 'primary' : 'inherit'} />
                </ListItemIcon>
                {trackUserLocation ? 'Desativar localização' : 'Minha localização'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setStatsModalOpen(true);
                  setMapActionsMenuAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                Estatísticas
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleRefreshData();
                  setMapActionsMenuAnchorEl(null);
                }}
                disabled={loading}
              >
                <ListItemIcon>
                  <RefreshIcon fontSize="small" />
                </ListItemIcon>
                Atualizar dados
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleToggleMapType();
                  setMapActionsMenuAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  {mapType === 'satellite' || mapType === 'hybrid' ? (
                    <SatelliteIcon fontSize="small" />
                  ) : (
                    <MapIcon fontSize="small" />
                  )}
                </ListItemIcon>
                Modo: {mapType === 'hybrid' ? 'Satélite' : mapType === 'satellite' ? 'Satélite puro' : mapType === 'roadmap' ? 'Mapa' : 'Terreno'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleToggleFullscreen();
                  setMapActionsMenuAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </ListItemIcon>
                {isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 9,
              display: 'flex',
              gap: 1,
              p: 1
            }}
          >
            <Tooltip title={trackUserLocation ? 'Desativar minha localização' : 'Minha Localização'}>
              <IconButton
                onClick={handleCenterOnUser}
                size="small"
                color={trackUserLocation ? 'primary' : 'default'}
              >
                <MyLocationIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Estatísticas">
              <IconButton onClick={() => setStatsModalOpen(true)} size="small">
                <AssessmentIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Atualizar Dados">
              <span>
                <IconButton onClick={handleRefreshData} size="small" disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={`Modo: ${mapType === 'hybrid' ? 'Satélite' : mapType === 'satellite' ? 'Satélite Puro' : mapType === 'roadmap' ? 'Mapa' : 'Terreno'}`}>
              <IconButton onClick={handleToggleMapType} size="small">
                {mapType === 'satellite' || mapType === 'hybrid' ? <SatelliteIcon /> : <MapIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}>
              <IconButton onClick={handleToggleFullscreen} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Paper>
        )}

        {isPickingLocation && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 15,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Clique no mapa para escolher a localização
            </Typography>
          </Paper>
        )}

        {viabilidadeFilterActive && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 15,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700} color="primary.main">
              Filtro de viabilidade ativo
            </Typography>
            <Button size="small" variant="outlined" onClick={handleClearViabilidadeFilter} sx={{ textTransform: 'none', py: 0.2 }}>
              Limpar
            </Button>
          </Paper>
        )}

        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Google Maps */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
          }}
          onContextMenu={handleContextMenu}
        >
          <Wrapper apiKey={googleMapsApiKey}>
            <GoogleMapComponent
              center={mapCenter}
              zoom={mapZoom}
              caixas={visibleCaixas}
              armarios={visibleArmarios}
              postes={visiblePostes}
              cables={visibleCables}
              currentCable={currentCable}
              rulerPoints={rulerPoints}
              areas={areasForMap}
              currentAreaPoints={areaPoints}
              isAddingCaixa={isAddingCaixa}
              isAddingArmario={isAddingArmario}
              isAddingPoste={isAddingPoste}
              isDrawingCable={isDrawingCable}
              isMeasuring={isMeasuring}
              isPickingLocation={isPickingLocation}
              isAddingArea={isAddingArea}
              autoFitApplied={autoFitApplied}
              onMapClick={handleMapClick}
              onAddCablePointFromItem={handleAddCablePointFromItem}
              onCaixaClick={handleCaixaClick}
              onEditCaixa={handleEditCaixa}
              onMoveCaixa={handleMoveCaixa}
              onDeleteCaixa={handleDeleteCaixa}
              onDiagramCaixa={handleDiagramCaixa}
              onViewCaixa={handleViewCaixa}
              onCancelCaixaMove={handleCancelCaixaMove}
              onCaixaMove={handleCaixaMove}
              onCaixaDrag={handleCaixaDrag}
              caixaIdBeingMoved={caixaIdEmMovimento}
              onArmarioMove={handleArmarioMove}
              onArmarioDrag={handleArmarioDrag}
              armarioIdBeingMoved={armarioIdEmMovimento}
              onMapLoad={handleMapLoad}
              onCablePointUpdate={handleCablePointUpdate}
              onCurrentCableUpdate={handleCurrentCableUpdate}
              onCablePointInsert={handleCablePointInsert}
              onCurrentCablePointInsert={handleCurrentCablePointInsert}
              onCablePointDelete={handleCablePointDelete}
              onAutoFitApplied={handleAutoFitApplied}
              onCaixaContextMenu={handleCaixaContextMenu}
              onArmarioContextMenu={handleArmarioContextMenu}
              onPosteContextMenu={handlePosteContextMenu}
              onPosteMove={handlePosteMove}
              posteIdBeingMoved={posteIdEmMovimento}
              onAreaContextMenu={handleAreaContextMenu}
              editingAreaId={editingArea?.id ?? null}
              onAreaPathChange={handleAreaPathChange}
              onAreaVertexSelect={handleAreaVertexSelect}
              selectedAreaVertexIndex={selectedAreaVertexIndex}
              onCurrentAreaPointUpdate={handleCurrentAreaPointUpdate}
              onCableContextMenu={handleCableContextMenu}
              selectedCableId={selectedCableId}
              onCableSelect={handleCableSelect}
              onEditCable={handleEditCable}
              onEditCablePoints={handleEditCablePoints}
              onDeleteCable={handleDeleteCable}
              onCancelCableEdit={handleCancelCableEdit}
              isEditingCable={isEditingCable}
              editingCable={editingCable}
              editingCaboOriginal={editingCaboOriginal}
              blockMapDrag={blockMapDrag}
              editingAreaPoints={editingAreaPoints}
              apiKey={googleMapsApiKey}
              onCablePointNearCaixaArmario={handleCablePointNearCaixaArmario}
              mapToast={mapToast}
              trackUserLocation={trackUserLocation}
              onUserLocationError={handleUserLocationError}
            />
          </Wrapper>
        </Box>
      </Box>

      {/* Dialog do Formulário Caixa - container no fullscreen para aparecer sobre o mapa */}
      <Dialog
        open={showCaixaForm}
        onClose={handleCaixaFormCancel}
        maxWidth="md"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{
          zIndex: isFullscreen ? 10000 : 1300,
          '& .MuiDialog-paper': {
            zIndex: isFullscreen ? 10001 : 'auto',
          }
        }}
      >
        <DialogContent>
          {((editingCaixa?.tipo) ?? addingCaixaTipo) === 'ceo' ? (
            <FtthCaixaCeoForm
              caixa={editingCaixa || undefined}
              coordinates={selectedCoordinates || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCaixaFormCancel}
              isFullscreen={isFullscreen}
            />
          ) : ((editingCaixa?.tipo) ?? addingCaixaTipo) === 'reserva' ? (
            <FtthCaixaReservaForm
              caixa={editingCaixa || undefined}
              coordinates={selectedCoordinates || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCaixaFormCancel}
              isFullscreen={isFullscreen}
            />
          ) : (
            <CaixaForm
              caixa={editingCaixa || undefined}
              coordinates={selectedCoordinates || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCaixaFormCancel}
              isFullscreen={isFullscreen}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog do Formulário Armário */}
      <Dialog
        open={showArmarioForm}
        onClose={handleArmarioFormCancel}
        maxWidth="md"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10000 : 1300 }}
      >
        <DialogContent>
          <FtthArmarioForm
            armario={editingArmario || undefined}
            coordinates={selectedCoordinates || undefined}
            onSuccess={handleArmarioFormSuccess}
            onCancel={handleArmarioFormCancel}
            isFullscreen={isFullscreen}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog do Formulário Cabo - container no fullscreen para aparecer sobre o mapa */}
      <Dialog
        open={showCabosForm}
        onClose={handleCabosFormCancel}
        maxWidth="md"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{
          zIndex: isFullscreen ? 10000 : 1300,
          '& .MuiDialog-paper': {
            zIndex: isFullscreen ? 10001 : 'auto',
          }
        }}
      >
        <DialogContent>
          {(() => {
            return null;
          })()}
          <CabosForm
            cabo={editingCaboForForm || undefined}
            pontos={selectedCablePoints}
            caixas={caixas}
            armarios={armarios}
            menuZIndex={isFullscreen ? 10002 : undefined}
            onSuccess={handleCabosFormSuccess}
            onCancel={handleCabosFormCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog do Formulário Área (polígono) */}
      <Dialog
        open={showAreaForm}
        onClose={handleAreaFormCancel}
        maxWidth="sm"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10000 : 1300 }}
      >
        <DialogTitle>{editingArea ? 'Editar Área' : 'Salvar Área'}</DialogTitle>
        <DialogContent>
          <FtthAreaMapaForm
            coordenadas={areaPoints}
            area={editingArea as any}
            onSuccess={handleAreaFormSuccess}
            onCancel={handleAreaFormCancel}
            isFullscreen={isFullscreen}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog do Formulário Poste */}
      <Dialog
        open={showPosteForm}
        onClose={handlePosteFormCancel}
        maxWidth="sm"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{
          zIndex: isFullscreen ? 10000 : 1300,
          '& .MuiDialog-paper': {
            zIndex: isFullscreen ? 10001 : 'auto',
          }
        }}
      >
        <DialogContent>
          <FtthPosteForm
            poste={editingPoste ?? undefined}
            coordinates={selectedCoordinates || undefined}
            copyFrom={copyFromPoste ?? undefined}
            onSuccess={handlePosteFormSuccess}
            onCancel={handlePosteFormCancel}
            isFullscreen={isFullscreen}
          />
        </DialogContent>
      </Dialog>

      {/* Modal do Diagrama da Caixa - container no fullscreen para aparecer sobre o mapa */}
      {selectedCaixaForDiagram && (
        <CaixaDiagramModal
          open={showCaixaDiagram}
          onClose={handleCloseCaixaDiagram}
          caixa={selectedCaixaForDiagram}
          isFullscreen={isFullscreen}
          {...(isFullscreen && fullscreenContainerRef.current ? { container: fullscreenContainerRef.current } : {})}
        />
      )}

      {selectedArmarioForDiagram && (
        <ArmarioDiagramModal
          open={showArmarioDiagram}
          onClose={handleCloseArmarioDiagram}
          armario={selectedArmarioForDiagram}
          isFullscreen={isFullscreen}
          {...(isFullscreen && fullscreenContainerRef.current ? { container: fullscreenContainerRef.current } : {})}
        />
      )}

      {/* Modal de Estatísticas */}
      <Dialog
        open={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        maxWidth="xs"
        fullWidth
        container={isFullscreen ? fullscreenContainerRef.current : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10000 : 1300 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          Estatísticas
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Caixas CTO</Typography>
              <Typography variant="body1" color="success.main">Ativas: {stats.ctoAtivas}</Typography>
              <Typography variant="body1" color="error.main">Inativas: {stats.ctoInativas}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Caixas CEO</Typography>
              <Typography variant="body1">{stats.ceo}</Typography>
            </Box>
            <Divider />
            <Typography variant="body1">Cabos: {stats.cabos}</Typography>
            <Typography variant="body1">Armários: {stats.armarios}</Typography>
            <Typography variant="body1">Postes: {stats.postes}</Typography>
            <Typography variant="body1">Áreas: {stats.areas}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Menu de Contexto para Caixas */}
      <CaixaContextMenuComponent
        menu={caixaContextMenu}
        onClose={handleCloseCaixaContextMenu}
        onEdit={handleEditCaixa}
        onMove={handleMoveCaixa}
        onDelete={handleDeleteCaixa}
        onDiagram={handleDiagramCaixa}
        caixaIdBeingMoved={caixaIdEmMovimento}
        onCancelMove={handleCancelCaixaMove}
      />

      {/* Menu de Contexto para Armários */}
      <ArmarioContextMenuComponent
        menu={armarioContextMenu}
        onClose={handleCloseArmarioContextMenu}
        onEdit={handleEditArmario}
        onMove={handleMoveArmario}
        onDiagram={handleDiagramArmario}
        onDelete={handleDeleteArmario}
        armarioIdBeingMoved={armarioIdEmMovimento}
        onCancelMove={() => { setArmarioIdEmMovimento(null); mapToast.info('Movimento cancelado'); }}
      />

      {/* Menu de Contexto para Postes */}
      <PosteContextMenuComponent
        menu={posteContextMenu}
        onClose={handleClosePosteContextMenu}
        onEdit={handleEditPoste}
        onDelete={handleDeletePoste}
        onMove={handleMovePoste}
        onCopy={handleCopyPoste}
        posteIdBeingMoved={posteIdEmMovimento}
        onCancelMove={() => { setPosteIdEmMovimento(null); mapToast.info('Movimento cancelado'); }}
      />

      {/* Menu de Contexto para Cabos */}
      <CableContextMenuComponent
        menu={cableContextMenu}
        onClose={handleCloseCableContextMenu}
        onEdit={handleEditCable}
        onEditPoints={handleEditCablePoints}
        onDelete={handleDeleteCable}
      />

      {/* Menu de Contexto para Áreas */}
      <AreaContextMenuComponent
        menu={areaContextMenu}
        onClose={handleCloseAreaContextMenu}
        onEdit={handleEditArea}
        onEditPoints={handleEditAreaPoints}
        onDelete={handleDeleteArea}
        onInfo={handleAreaInfo}
        editingAreaPoints={editingAreaPoints}
        editingAreaId={editingArea?.id ?? null}
        onCancelEditPoints={handleCancelEditAreaPoints}
      />

      <ViabilidadeModal
        open={viabilidadeModalOpen}
        onClose={() => {
          setViabilidadeModalOpen(false);
          setPickedLocation(null);
          setIsPickingLocation(false);
        }}
        caixas={caixas}
        onMostrarNoMapa={handleMostrarViabilidadeNoMapa}
        onPickOnMap={() => {
          setViabilidadeModalOpen(false);
          setIsPickingLocation(true);
        }}
        initialLat={pickedLocation?.lat}
        initialLng={pickedLocation?.lng}
        portalContainer={isFullscreen ? fullscreenContainerRef.current : undefined}
      />
    </Box>
  );
};

export default MapNetworkTab;
